import { AfterViewChecked, Component, ElementRef, HostBinding, HostListener, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/shared/services/api.service';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { RecaptchaService } from 'src/app/shared/services/recaptcha.service';
import { environment } from 'src/environments/environment';
import {
    ChatRequest,
    ChatSessionDto,
    CreditsResponse,
    CreditToolUsage,
    EditTool,
    EraseImageRequest,
    FaceRestoreRequest,
    FillImageRequest,
    RestoreImageRequest,
    GenerationModelsResponse,
    ImageChatService,
    ModelControl,
    ModelInfo,
    ConverseResponse,
    VideoJob,
    CustomerDto,
    PermissionTool,
    UserPermissionResponse,
    StreamEvent,
    UpscalerOption
} from 'src/app/shared/services/image-chat.service';

interface JobState {
    status: 'queued' | 'processing' | 'succeeded' | 'failed' | string;
    model?: string;
    operation?: string;
    elapsed?: number;   // seconds since submit
    startedAt?: number; // epoch ms
}

interface UIMessage {
    role: 'user' | 'assistant';
    content: string;
    images: { id: string; url: string }[];
    videos?: { id?: string; url: string }[];
    created_at: string;
    pending?: boolean;
    // The reader stopped this turn. The bubble stays in the transcript, marked,
    // rather than disappearing as though the turn never happened.
    stopped?: boolean;
    step?: string;      // live status label while streaming (e.g. "Generating with Chat Image 1")
    job?: JobState;
    // The prompt a video was rendered from. Kept on the message because the
    // assistant's own text for a video turn is just a confirmation line.
    prompt?: string;
    // Renders the sign-up / sign-in card in place of a generated reply. Set on
    // the assistant turn a signed-out visitor gets instead of a result.
    auth?: boolean;
    // Renders library search results in place of a generated reply. Set on the
    // assistant turn a Stock search produces.
    stock?: StockSearch;
    // Which panel that card is showing. Per-message rather than component state:
    // a visitor can send several prompts and collect several cards, and expanding
    // one must not expand the rest.

    /*
     * This turn is expected to return images, so the placeholder is drawn as the
     * frames they will land in rather than as a row of dots. imageCount is how
     * many were asked for, so the placeholder matches the shape of the answer.
     */
    imagePending?: boolean;
    imageCount?: number;
}

/** One row from the creative (stock) library search. */
interface StockResult {
    id: string;
    filename?: string;
    license?: string;
    collection?: number;
    caption?: string;
    images_caption?: string;
    preview?: string;
    thumbnail?: string;
}

/**
 * A stock search shown as an assistant turn. Held on the message rather than in
 * component state so a session can hold several searches, each keeping its own
 * page count and its own "load more".
 */
interface StockSearch {
    query: string;
    total: number;
    pages: number;
    page: number;
    seconds: number;
    results: StockResult[];
    loading: boolean;
    error?: string;
}

/**
 * One buyable credit pack. Prices are held as plain numbers and formatted for
 * display by inr(), so the dialog and any later checkout agree on the figures.
 */
interface CreditPlan {
    key: string;
    name: string;
    credits: number;      // credits granted per month
    monthly: number;      // ₹ per month
    yearly: number;       // ₹ billed across the year
    badge?: string;       // e.g. 'Best Value'
}

/** Where a rail flyout is pinned, and the height the viewport leaves it. */
interface FlyoutPos {
    top: number;
    left: number;
    maxHeight: number;
}

interface ControlGroup {
    label: string;
    options: { value: any; label: string }[];
}

/**
 * One entry of the Edit menu on a generated image. Most map straight to an
 * EditTool; the rest have their own endpoints (face restore, restore, video
 * jobs) or just prefill the composer (upscale, reference).
 */
type ImageEditAction =
    | 'remove_bg' | 'replace_bg' | 'crop' | 'colorize' | 'denoise'
    | 'face' | 'fill' | 'restore' | 'animate' | 'talk'
    | 'upscale' | 'reference';

/**
 * One stop in the guided dashboard walkthrough. `target` is a CSS selector for
 * the element to spotlight; omit it for a centered, element-less card (welcome
 * / finish). `placement` is the preferred side for the popover relative to the
 * target — the layout logic clamps it back into the viewport if it won't fit.
 */
interface TourStep {
    target?: string;
    icon: string;                 // inline emoji/glyph shown in the popover header
    title: string;
    body: string;                 // the "what" — a plain-language description
    why?: string;                 // why this matters to the user
    how?: string;                 // how to actually use it
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

/**
 * Upscale is four rail items, one per (resolution, engine) pairing, rather than
 * one item that leaves both choices to the router. Bria is
 * `bria/increase-resolution`, ESRGAN is `nightmareai/real-esrgan`; the engine
 * reaches the orchestrator in the prompt text, since converse takes no tool
 * argument of its own.
 */
type UpscaleNavKey =
    | 'upscale-2k-bria' | 'upscale-4k-bria'
    | 'upscale-2k-esrgan' | 'upscale-4k-esrgan';

type NavKey =
    | 'generate' | 'edit' | UpscaleNavKey | 'stock' | 'templates'
    | 'copy' | 'compliance' | 'licence' | 'sessions';

type ChatModeKey =
    | 'generate' | 'edit' | UpscaleNavKey | 'stock' | 'templates'
    | 'campaign-copy' | 'compliance' | 'licence'
    | 'premium' | 'add-text' | 'remove-bg' | '4k'
    | 'compliance-runtime' | 'copy-action';

interface ChatModeConfig {
    header: string;
    title: string;
    inputText: string;
    placeholder: string;
}

const DEFAULT_PLACEHOLDER = 'Describe what you want to create…';

@Component({
  selector: 'molla-ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss']
})
export class AiChatComponent implements OnInit, AfterViewChecked, OnDestroy {
    @ViewChild('messageList') messageList?: ElementRef<HTMLDivElement>;
    @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

    sessions: ChatSessionDto[] = [];

    /** Sidebar session filter — matches titles in the already-loaded list. */
    sessionSearch = '';

    messages: UIMessage[] = [];
    /**
     * The id of the placeholder row "Start something new" puts at the top of the
     * list. Not a real session — the studio only creates one when the first
     * message is sent — so it needs an id no server row can collide with.
     */
    static readonly DRAFT_SESSION_ID = '__draft__';

    /** The placeholder row itself, or null when there is no unsent draft. */
    draftSession: ChatSessionDto | null = null;

    private _activeSessionId: string | null = '';

    /**
     * Behind an accessor so the draft row cannot outlive itself.
     *
     * A dozen call sites assign this from a response (`res.session_id`, the
     * stream's `ev.session_id`, a finished job) and more will follow. Clearing
     * the placeholder in each of them is a rule someone eventually forgets, and
     * forgetting it leaves "Untitled" sitting above the real session it became.
     * Here it is one line that every path goes through.
     */
    get activeSessionId(): string | null {
        return this._activeSessionId;
    }

    set activeSessionId(value: string | null) {
        this._activeSessionId = value;

        if (value !== AiChatComponent.DRAFT_SESSION_ID) {
            this.draftSession = null;
        }
    }

    pendingMessage = '';
    /**
     * Reference images on the composer, sent together as attached_image_ids.
     * Uploads and drops add to it, up to MAX_REFERENCE_IMAGES; the single-image
     * tools (refine, upscale, edit, use as reference) replace it with one.
     */
    attachedImages: { id: string; url: string; size?: number }[] = [];

    /** How many reference images one message may carry. */
    readonly MAX_REFERENCE_IMAGES = 5;

    /**
     * The most the files uploaded to one message may weigh together (5 MB).
     * Counts the files picked from the customer's computer; an image already in
     * the studio (a generated one, "use as reference") has no upload size.
     */
    readonly MAX_REFERENCE_BYTES = 5 * 1024 * 1024;

    isSending = false;

    /**
     * The in-flight turn, held so the composer's stop button can cut it off: the
     * request itself, the bubble it is filling, and the video job id it is polling
     * (a video turn is a poller rather than an open stream).
     */
    private activeRequest: Subscription | null = null;
    private activePending: UIMessage | null = null;
    private activeJobId: string | null = null;
    isUploading = false;
    /** Files still to go up in the running multi-file upload, the current one included. */
    uploadingCount = 0;
    isLoadingHistory = false;
    isLoadingSessions = false;

    activeNav: NavKey = 'sessions';

    /**
     * Whether the Generate rail item is expanded into its model list. Held apart
     * from `activeNav` so the list can stay open while the user reads it, and so
     * collapsing it doesn't navigate away from Generate.
     */
    generateMenuOpen = false;

    /** The same, for the Upscale group's four size-and-engine pairings. */
    upscaleMenuOpen = false;

    /**
     * Where a rail flyout is pinned, in viewport coordinates, plus the height it
     * has room for.
     *
     * Measured from the button and positioned fixed rather than absolutely: the
     * rail's list is a scroll container, and an absolutely positioned panel
     * inside one is clipped by it — a flyout that has to escape the rail cannot
     * be laid out inside it.
     */
    upscaleMenuPos: FlyoutPos | null = null;
    generateMenuPos: FlyoutPos | null = null;

    /**
     * Pin a flyout beside its button.
     *
     * Prefers aligning the panel's top with the button's, which keeps the two
     * visibly connected. When there is not enough room below for a usable panel
     * it lifts it instead, so a button near the foot of the rail does not open a
     * 40px scrolling sliver. Either way `maxHeight` is whatever room is left, so
     * the panel can never run off the bottom of the viewport.
     */
    private flyoutPosition(button: HTMLElement | null): FlyoutPos | null {
        const rect = button?.getBoundingClientRect();
        if (!rect) { return null; }

        const margin = 10;
        const cap = Math.min(420, window.innerHeight - margin * 2);
        const roomBelow = window.innerHeight - rect.top - margin;
        const usable = Math.min(cap, 220);

        const top = roomBelow >= usable
            ? rect.top
            : Math.max(margin, window.innerHeight - cap - margin);

        return {
            top,
            left: rect.right + 6,
            maxHeight: Math.min(cap, window.innerHeight - top - margin)
        };
    }

    /**
     * The Upscale group's children. Four rail items of their own made the section
     * as tall as the rest of the rail put together, so they fold into one parent
     * the way Generate's models do.
     */
    readonly upscaleOptions: {
        nav: UpscaleNavKey;
        size: string;
        engine: string;
        /** Which engine mark the row draws — see the switch in the template. */
        icon: 'bria' | 'esrgan';
    }[] = [
        { nav: 'upscale-2k-bria', size: '2K', engine: 'Bria', icon: 'bria' },
        { nav: 'upscale-2k-esrgan', size: '2K', engine: 'Real-ESRGAN', icon: 'esrgan' },
        { nav: 'upscale-4k-bria', size: '4K', engine: 'Bria', icon: 'bria' },
        { nav: 'upscale-4k-esrgan', size: '4K', engine: 'Real-ESRGAN', icon: 'esrgan' }
    ];

    /**
     * Whether the selected model's controls are shown as chips over the composer.
     * Opened by picking a model in the rail, and toggled by the composer's own
     * settings button.
     */
    showComposerControls = false;

    /** Key of the control whose value popover is open, or '' for none. */
    openControlKey = '';

    /** Whether the session list is expanded. Toggled by the rail's Sessions item. */
    sessionsListOpen = true;

    /**
     * Whether the whole sessions column is folded away, via the arrow in its own
     * header. Kept separate from `sessionsListOpen`: one hides the list inside the
     * sidebar, the other hides the sidebar itself.
     */
    sidebarCollapsed = false;

    /* ---------- Voice input ---------- */

    isRecording = false;

    /**
     * Everything dictated in this take — final chunks plus the interim tail.
     * Held here instead of being typed into the composer as it arrives: the
     * recording bar covers the input while the mic is live, so the text lands in
     * the textarea when the take ends (and is dropped if it is cancelled).
     */
    voiceTranscript = '';

    /**
     * One entry per bar in the recording waveform; the value is only its index.
     * Sized to overfill the widest composer at the 6px pitch the bars sit on —
     * the strip clips its left edge, so the oldest samples scroll out of view
     * rather than the trace stopping short of the bar's width.
     */
    readonly voiceBars: number[] = Array.from({ length: 140 }, (_, i) => i);

    /**
     * True once the analyser is feeding real microphone levels. While false the
     * bars run a CSS animation instead, so the bar still reads as "listening" on
     * a browser that won't give us a second audio stream.
     */
    voiceMeterLive = false;

    @ViewChild('voiceWave') private voiceWave?: ElementRef<HTMLElement>;

    @ViewChild('composerInput') private composerInputEl?: ElementRef<HTMLTextAreaElement>;

    /** The text the composer was last sized against — see ngAfterViewChecked. */
    private composerSizedFor: string | null = null;

    // Web Speech API recognizer + the text already in the box when we started
    // listening (new speech is appended to it).
    private recognition: any = null;
    private recognitionBaseText = '';
    /** Final (settled) speech so far; `voiceTranscript` is this plus the interim tail. */
    private voiceFinalText = '';

    // Level metering for the waveform. Kept off the template — the bars are
    // written straight to the DOM outside Angular, so dictation doesn't run a
    // change-detection pass per frame.
    private micStream: MediaStream | null = null;
    private audioCtx: AudioContext | null = null;
    private analyser: AnalyserNode | null = null;
    private waveFrame = 0;
    private waveSamples: number[] = [];
    private lastSampleAt = 0;

    currentMode: ChatModeKey | null = null;
    private lastPrefill = '';

    downloadModalImage: { id: string; url: string } | null = null;
    downloadAccepted = false;
    isDownloading = false;

    // The licence modal gates both download paths, so it records which one to
    // run once the terms are accepted:
    //   'highres' — openDownloadModal(): buys/records a high-res download via
    //               the backend, then fetches the returned download_link.
    //   'studio'  — downloadAssetFile(): streams the asset URL as-is (also the
    //               only path used for videos).
    private pendingDownload: { kind: 'highres' | 'studio'; assetType: 'image' | 'video' } =
        { kind: 'highres', assetType: 'image' };

    previewImage: { id: string; url: string } | null = null;
    currentUser: any;

    /**
     * Passed to <indiapicture-header>. This route lives outside LayoutComponent,
     * so the header variant layout would have chosen is selected here instead.
     */
    headerType = environment.headerType;

    /* ---- Colour theme ---- */

    /** Remembered per browser, so the workspace opens the way it was left. */
    private static readonly THEME_KEY = 'ipStudioTheme';

    theme: 'light' | 'dark' = 'light';

    /**
     * The stylesheet defines both palettes as custom properties and switches on
     * this class, so the whole component re-themes from one binding.
     */
    @HostBinding('class.theme-dark')
    get isDarkTheme(): boolean {
        return this.theme === 'dark';
    }

    /* ---------- Writing mode ---------- */

    /**
     * Whether the reader is in the middle of working the composer. Set on the
     * first keystroke and cleared when the draft is deleted by hand or the
     * sessions column is asked for — not by sending, which empties the composer
     * without meaning the session is over.
     *
     * Held apart from `pendingMessage` so that text put there by something other
     * than the keyboard — a rail item's prefill, a dictated take — does not clear
     * the screen on its own.
     */
    private composerTouched = false;

    /**
     * Writing mode: the sessions column folds left, so the prompt and the
     * transcript have the screen to themselves.
     *
     * The site header used to go with it. It no longer does — it answers to the
     * transcript's scroll direction instead, which is a reader's gesture rather
     * than a writer's; see onTranscriptScroll(). Typing now only moves the
     * sessions column.
     *
     * Deliberately *not* tied to the composer still holding text. Sending clears
     * it, and treating that as "done writing" put the chrome back the moment a
     * prompt went out — exactly when the reader wants the result to have the
     * screen. It ends when the draft is deleted by hand, or when the sessions
     * column is asked for.
     */
    @HostBinding('class.immersive')
    get isImmersive(): boolean {
        return this.composerTouched;
    }

    /* ---------- Header travel ---------- */

    /**
     * Whether the site header is currently slid up out of the way.
     *
     * A plain field rather than a getter: it is written from a scroll handler, and
     * a getter would have to re-derive the direction on every change detection
     * pass from state the handler already knows.
     *
     * Hovering the top edge peeks at it while hidden — that part is CSS, so
     * peeking costs no change detection.
     */
    @HostBinding('class.header-hidden')
    headerHidden = false;

    /** Where the transcript was on the previous scroll event. */
    private lastTranscriptScrollTop = 0;

    /**
     * Travel needed before the bar moves, in px.
     *
     * A trackpad reports single-pixel deltas and rubber-banding reports deltas in
     * both directions at the ends, so acting on every event would flutter the
     * header. Sub-threshold events deliberately leave `lastTranscriptScrollTop`
     * alone, which lets slow scrolling accumulate to the threshold instead of
     * being ignored outright.
     */
    private static readonly HEADER_SCROLL_THRESHOLD = 14;

    /**
     * How much scrollable room the transcript needs before the header will move
     * at all. Below this there is nothing to read back through, so taking the
     * chrome away would only cost the reader the header.
     */
    private static readonly HEADER_SCROLL_MIN_RANGE = 120;

    /**
     * Scrolls we caused ourselves are ignored until this timestamp.
     *
     * scrollToBottom() runs on every new message and all the way through a
     * streaming reply. Those are downward travel and would otherwise pull the
     * header down mid-answer, over and over. The rule is about what the reader
     * does, so our own scrolling is not part of it.
     */
    private headerScrollSuppressedUntil = 0;

    /**
     * Read off the element, not `pendingMessage`: ngModel and this handler both
     * listen for `input`, and their order is not guaranteed.
     *
     * Emptying the box by hand is the one keystroke that ends writing mode — the
     * draft was abandoned, so the chrome comes back.
     */
    onComposerInput(event: Event): void {
        const value = (event.target as HTMLTextAreaElement)?.value || '';
        this.composerTouched = !!value.trim();
        this.growComposer(event.target as HTMLTextAreaElement);

        // Stock mode previews the library as the words are typed; every other mode
        // has nothing to look up until the turn is sent.
        this.onStockTyping(value);
    }

    /**
     * Grow the composer to fit what is in it, up to the ceiling its stylesheet
     * sets. A textarea will not do this itself: rows="1" fixes the box and the
     * text scrolls inside it.
     *
     * The limit is read from the computed style rather than restated here, so the
     * stylesheet stays the one place the ceiling is set.
     */
    private growComposer(el: HTMLTextAreaElement | null | undefined): void {
        if (!el) { return; }

        const max = parseFloat(getComputedStyle(el).maxHeight);

        // Collapsed first: scrollHeight cannot report a smaller content height
        // while the box is still holding the taller one, so deleting text would
        // never shrink the field back.
        el.style.height = 'auto';

        const fits = isNaN(max) || el.scrollHeight <= max;
        el.style.height = (fits ? el.scrollHeight : max) + 'px';
        // Only scrolls once it has stopped growing.
        el.style.overflowY = fits ? 'hidden' : 'auto';

        this.composerSizedFor = el.value;
    }

    toggleTheme(): void {
        this.theme = this.theme === 'dark' ? 'light' : 'dark';
        try {
            localStorage.setItem(AiChatComponent.THEME_KEY, this.theme);
        } catch {
            // Private browsing and blocked storage: the theme still applies for
            // this visit, it just is not remembered.
        }
    }

    /**
     * The prompt held back when a signed-out visitor pressed send. Restored to the
     * composer once they are in, which is the promise the card makes ("your
     * request is saved, so you won't need to type it again").
     */
    authPrompt = '';

    /**
     * Which tab of the account dialog is open, or null when it is closed. The
     * card in the transcript only offers the choice — the fields live in the
     * dialog, so signing in isn't done through a form wedged into a chat bubble.
     */
    authDialog: 'signin' | 'signup' | null = null;

    authName = '';
    /** Sign In takes one field, which the backend accepts as email or username. */
    authIdentity = '';
    authEmail = '';
    authPhone = '';
    authPassword = '';
    authRemember = false;
    authAcceptTerms = false;

    /*
     * Marketing opt-in on the sign-up tab.
     *
     * Optional, and false by default: a consent that starts ticked is not one.
     * UI only for now — the choice is held here but no submit path reads it, so
     * nothing is sent to the backend yet.
     */
    authPromos = false;
    authBusy = false;
    authError = '';

    /**
     * How the open tab is signing in: a mailed one-time code, or a password.
     *
     * One set of OTP state serves both tabs — only one is ever on screen, and the
     * dialog already keeps a single error and a single busy flag the same way.
     * Which endpoint a send or verify hits is decided from `authDialog`, so the
     * two flows cannot get out of step with the tab that is showing.
     *
     * Follows socialAndOtpAuth rather than defaulting to OTP outright: the
     * password fields render on 'password', so with OTP hidden and the mode left
     * at 'otp' this dialog would open with no fields in it at all.
     */
    authMethod: 'otp' | 'password' = environment.socialAndOtpAuthEnabled ? 'otp' : 'password';

    /**
     * Whether Google and email-OTP sign-in are offered here.
     *
     * Off hides the Google button, the OTP/Password switch and the code form,
     * leaving email + password. Nothing about the flows changes — the handlers
     * and their backends stay — so this flips back without a code change.
     */
    readonly socialAndOtpAuth = environment.socialAndOtpAuthEnabled;

    /** 1 = enter the email, 2 = enter the code that was mailed to it. */
    authOtpStep: 1 | 2 = 1;
    authOtpEmail = '';
    authOtpCode = '';

    /**
     * Set only while a code is being mailed. `authBusy` already gates the dialog;
     * this exists so step 2 can tell "Resending…" from "Verifying…", since the
     * resend link fires a send from a step whose button is a verify.
     */
    authOtpSending = false;

    /** Seconds until "Resend code" becomes clickable again. */
    authResendIn = 0;
    private authResendTimer: any = null;

    /** Remember-me holds the identity only — never the password. */
    private static readonly REMEMBER_KEY = 'ip.aichat.identity';

    /**
     * Where the Google round trip should come back to.
     *
     * Signing in with Google is a full-page departure — Google's consent screen
     * cannot be framed or fetched — and /auth/callback otherwise lands everyone
     * on the homepage. The studio is not somewhere to be dumped out of, so it
     * leaves its own path behind for the callback to return to.
     */
    private static readonly RETURN_KEY = 'ip.auth.returnTo';

    /** The held prompt, parked across the same round trip. */
    private static readonly PROMPT_KEY = 'ip.auth.pendingPrompt';

    /** Whether the "Add more credits" dialog is open. */
    showCreditPlans = false;

    /**
     * Whether the Pricing & Licence dialog is open. The studio answers the
     * header's Pricing control with this instead of routing to /subscription,
     * which would abandon whatever the visitor was part-way through.
     */
    showPricing = false;

    /** Which pack the dialog has selected. Business is the pre-selected default. */
    selectedCreditPlan = 'business';

    /**
     * The packs offered in that dialog. Static because these are commercial
     * plans, not something the studio's model/credit APIs expose.
     */
    readonly creditPlans: CreditPlan[] = [
        { key: 'starter', name: 'Starter', credits: 200, monthly: 5000, yearly: 60000 },
        { key: 'pro', name: 'Pro', credits: 400, monthly: 10000, yearly: 120000 },
        { key: 'business', name: 'Business', credits: 1000, monthly: 23000, yearly: 276000, badge: 'Best Value' }
    ];

    /** Shown next to Add more until the studio returns a real balance. */
    private readonly defaultCreditCount = 35;

    /* ---- Studio API state ---- */

    /** Whole generationModels catalogue — both kinds, filtered per `modelKind`. */
    allModels: ModelInfo[] = [];

    /**
     * Image ⇄ video switch above the model picker. Matches the `kind` field on
     * each model from generationModels; video models drive the text→video job.
     */
    modelKind: 'image' | 'video' = 'image';

    /** Last model picked per kind, so flipping the toggle back restores it. */
    private modelByKind: { image: string; video: string } = { image: '', video: '' };

    // Selected model + dynamic control values keyed by control.key
    // (image: aspect_ratio, output_size, num_images, style_preset, seed,
    //  negative_prompt · video: duration, resolution, aspect_ratio,
    //  camera_fixed, seed).
    selectedMode = '';
    controlValues: { [key: string]: any } = {};

    credits: CreditsResponse['credits'] = null;

    // Credits / "usage by tool" modal (opened from the credits badge).
    showCreditsModal = false;
    creditsTools: CreditToolUsage[] = [];
    creditsCustomerName: string | null = null;
    creditsCustomerId: string | null = null;

    // Studio user id (from IMAGE_CHAT_INDIAPICTURE_USER_KEY via the backend) —
    // sent as user_id on the in-app Studio edit tools (crop/erase).
    studioUserId = '';

    editingImageId: string | null = null;

    /* ---- Discovery catalogues (edit-tools / video-models) ---- */
    editToolsCatalogue: any[] = [];
    videoModelsCatalogue: ModelInfo[] = [];

    /* ---- Recent async jobs panel (video / talking-head / async edits) ---- */
    jobs: (VideoJob & { created_at?: string; kind?: string })[] = [];
    showJobsPanel = false;
    isLoadingJobs = false;

    /* ---- Customer directory (scoping sessions to a customer) ---- */
    customers: CustomerDto[] = [];

    // Text→video: when on, the composer's send button starts a text-to-video job.
    videoFromTextMode = false;
    videoTextModel = 'wan25';

    // Converse: when on, the composer routes through the studio orchestrator
    // (it decides chat vs create/refine and handles the confirm handshake).
    converseMode = false;

    // Crop modal (drag-select a region → normalized x/y/w/h)
    cropModalImage: { id: string; url: string } | null = null;
    cropRect: { x: number; y: number; w: number; h: number } | null = null; // pixels within the stage
    private cropDragging = false;
    private cropStartX = 0;
    private cropStartY = 0;
    private cropStageRect: { left: number; top: number; width: number; height: number } | null = null;

    // Erase modal (brush-paint a mask → white = area to remove)
    eraseModalImage: { id: string; url: string } | null = null;
    eraseBrush = 40;
    hasEraseStrokes = false;
    private eraseDrawing = false;
    private displayCtx: CanvasRenderingContext2D | null = null;
    private maskCanvas: HTMLCanvasElement | null = null;
    private maskCtx: CanvasRenderingContext2D | null = null;
    @ViewChild('eraseImg') eraseImg?: ElementRef<HTMLImageElement>;
    @ViewChild('eraseCanvas') eraseCanvas?: ElementRef<HTMLCanvasElement>;

    // Generative-fill modal (brush-paint a mask + optional prompt)
    fillModalImage: { id: string; url: string } | null = null;
    fillPrompt = '';
    fillBrush = 40;
    fillDims = '';
    hasFillStrokes = false;
    private fillDrawing = false;
    private fillDisplayCtx: CanvasRenderingContext2D | null = null;
    private fillMaskCanvas: HTMLCanvasElement | null = null;
    private fillMaskCtx: CanvasRenderingContext2D | null = null;
    @ViewChild('fillImg') fillImg?: ElementRef<HTMLImageElement>;
    @ViewChild('fillCanvas') fillCanvas?: ElementRef<HTMLCanvasElement>;

    // Replace-background modal (image preview + new-scene prompt)
    replaceBgImage: { id: string; url: string } | null = null;
    replaceBgPrompt = '';

    // Talking head (lip-sync) modal — portrait preview + audio picker.
    talkingHeadImage: { id: string; url: string } | null = null;
    talkAudioFile: File | null = null;
    talkAudioName = '';
    private jobPollers: { [jobId: string]: any } = {};

    /* ---- Guided dashboard walkthrough ---- */
    @ViewChild('tourPopover') tourPopoverEl?: ElementRef<HTMLDivElement>;

    showTour = false;
    tourIndex = 0;

    // Live geometry (viewport px) driven by the target element each step.
    tourSpot = { top: 0, left: 0, width: 0, height: 0, visible: false };
    tourPop = { top: 0, left: 0 };
    tourArrow: 'top' | 'bottom' | 'left' | 'right' | 'none' = 'none';

    readonly tourSteps = null;
    // readonly tourSteps: TourStep[] = [
    //     {
    //         icon: '👋',
    //         title: 'Welcome to Mahatta Image Studio',
    //         body: 'This is your AI creative workspace. Instead of complex design software, you simply <strong>describe what you want in words</strong> and the AI generates it — then you refine, edit, upscale, or even animate the result.',
    //         why: 'No design skills needed. If you can describe a picture, you can create one — in seconds, not hours.',
    //         how: 'This quick tour (about 60 seconds) points out each part of the screen. Click <strong>Next</strong> to continue, or <strong>Skip</strong> to jump straight in.',
    //         placement: 'center'
    //     },
    //     {
    //         target: '.chat-vertical-nav',
    //         icon: '🧭',
    //         title: 'Creative tools (mode switcher)',
    //         body: 'This left rail is where you choose <em>what kind of task</em> you want to do — <strong>Generate</strong> a brand-new image, <strong>Edit</strong> an existing one, <strong>Upscale</strong> to sharp 4K, or open your saved <strong>Sessions</strong>.',
    //         why: 'Each mode retunes the AI and the prompt box for that specific job, so you get better results with less effort.',
    //         how: 'Click a mode <em>before</em> you type — the prompt box and options below update to match your choice.',
    //         placement: 'right'
    //     },
    //     {
    //         target: '.new-session-btn',
    //         icon: '➕',
    //         title: 'Start a fresh session',
    //         body: 'This button opens a brand-new, empty conversation with the AI.',
    //         why: 'The AI remembers everything within one session — every image and every edit. A fresh session keeps unrelated projects from mixing together and confusing the results.',
    //         how: 'Click <strong>+ New Session</strong> whenever you start a different idea, product, or client.',
    //         placement: 'right'
    //     },
    //     {
    //         target: '.sessions-wrap',
    //         icon: '🗂️',
    //         title: 'Your session history',
    //         body: 'Every conversation you start is listed here automatically, with the newest at the top.',
    //         why: 'Your work is saved for you — you never lose an image or have to start from scratch when you come back later.',
    //         how: 'Click any session in the list to reopen it and continue exactly where you left off.',
    //         placement: 'right'
    //     },
    //     {
    //         target: '.chat-topbar__right',
    //         icon: '💳',
    //         title: 'Credits & settings',
    //         body: 'Up here you can see your <strong>credit balance</strong> and open the <strong>settings</strong> gear.',
    //         why: 'Every image, edit, or video uses a few credits. Watching the balance means no surprises about what you have left to spend.',
    //         how: 'Click the credits badge to see a breakdown by tool, or the gear (⚙️) to change which AI model and options you use.',
    //         placement: 'bottom'
    //     },
    //     {
    //         target: '.chat-settings',
    //         icon: '⚙️',
    //         title: 'Choose your AI model',
    //         body: 'This panel is where you pick the <strong>AI model</strong> and fine-tune its controls — things like aspect ratio, quality, seed, and style.',
    //         why: 'Different models are good at different things — some are fast for drafts, others are best for photorealism or crisp text. The controls shape the final look.',
    //         how: 'Pick a model, adjust the options shown, then send your prompt. Only the settings that model supports appear, so you can never choose an invalid value.',
    //         placement: 'left'
    //     },
    //     {
    //         target: '.message-list',
    //         icon: '🖼️',
    //         title: 'Your canvas',
    //         body: 'This is the main area where your chat, generated <strong>images</strong>, and <strong>videos</strong> appear as you work.',
    //         why: 'It doubles as your working gallery — every result stays here in order, so you can compare versions and build on the best one.',
    //         how: 'Hover over any image to <strong>Edit</strong>, <strong>Refine</strong>, <strong>Upscale</strong>, <strong>Animate</strong>, or <strong>Download</strong> it.',
    //         placement: 'center'
    //     },
    //     {
    //         target: '.composer-input',
    //         icon: '✍️',
    //         title: 'Describe your idea',
    //         body: 'This is the prompt box — your main way of talking to the AI. Just describe what you want in plain, everyday language.',
    //         why: 'The AI turns your words into a picture, so the clearer and more specific you are, the closer the result will be to what you imagined.',
    //         how: 'Try something like <em>“a photoreal masala chai cup in soft window light”</em>, then press <strong>Enter</strong>. Add details such as lighting, mood, colour, or camera angle for even better results.',
    //         placement: 'top'
    //     },
    //     {
    //         target: '.composer-actions',
    //         icon: '⚡',
    //         title: 'Quick actions',
    //         body: 'These are one-click shortcuts around the prompt box — attach a reference image, turn text into a video, remove a background, or upscale to 4K.',
    //         why: 'They save you from typing out common instructions and give you creative tools that plain text can’t, like uploading your own photo to work from.',
    //         how: 'Click an action to switch it on, then send your prompt. The available actions change depending on the mode you’re in.',
    //         placement: 'top'
    //     },
    //     {
    //         icon: '🎉',
    //         title: 'You’re all set!',
    //         body: 'That’s the whole studio. The best way to learn is to try it — pick a tool on the left, or simply type a prompt below and hit Enter.',
    //         why: 'You can’t break anything. Experiment freely — every result is saved in your session, and you can always refine or undo by generating again.',
    //         how: 'Need a refresher? Replay this tour anytime using the <strong>?</strong> help button in the top bar. Happy creating!',
    //         placement: 'center'
    //     }
    // ];

    readonly chatModeConfig: Record<ChatModeKey, ChatModeConfig> = {
        'generate': {
            header: 'Generate',
            title: 'Create images from a text prompt',
            inputText: '',
            placeholder: 'Describe what you want to create…'
        },
        'edit': {
            header: 'Edit Image',
            title: 'Upload and describe your changes',
            inputText: 'Edit this image: ',
            placeholder: 'Edit this image: your prompt'
        },
        // The four upscale pairings. Each prefills its own instruction and leaves
        // the caret after it, so the user can still add direction of their own —
        // "…keep the grain", "…don't smooth the skin" — before sending.
        'upscale-2k-bria': {
            header: 'Upscale 2K · Bria',
            title: 'Upscale an image to 2K with Bria',
            inputText: 'Upscale this image to 2K using Bria: ',
            placeholder: 'Upscale this image to 2K using Bria: your prompt'
        },
        'upscale-4k-bria': {
            header: 'Upscale 4K · Bria',
            title: 'Upscale an image to 4K with Bria',
            inputText: 'Upscale this image to 4K using Bria: ',
            placeholder: 'Upscale this image to 4K using Bria: your prompt'
        },
        'upscale-2k-esrgan': {
            header: 'Upscale 2K · Real-ESRGAN',
            title: 'Upscale an image to 2K with Real-ESRGAN',
            inputText: 'Upscale this image to 2K using Real-ESRGAN: ',
            placeholder: 'Upscale this image to 2K using Real-ESRGAN: your prompt'
        },
        'upscale-4k-esrgan': {
            header: 'Upscale 4K · Real-ESRGAN',
            title: 'Upscale an image to 4K with Real-ESRGAN',
            inputText: 'Upscale this image to 4K using Real-ESRGAN: ',
            placeholder: 'Upscale this image to 4K using Real-ESRGAN: your prompt'
        },
        'stock': {
            header: 'Generate',
            title: 'Create images from a text prompt',
            inputText: 'Get Stock Images: ',
            placeholder: 'Get Stock Images: your prompt'
        },
        'templates': {
            header: 'Generate',
            title: 'Create images from a text prompt',
            inputText: 'Describe what you want to create, edit, check or find…  (drop or paste an image to attach)',
            placeholder: 'Get template Images: your prompt'
        },
        'campaign-copy': {
            header: 'Generate',
            title: 'Create images from a text prompt',
            inputText: 'Write campaign copy for ',
            placeholder: 'Write campaign copy for: your prompt'
        },
        'compliance': {
            header: '',
            title: '',
            inputText: '',
            placeholder: ''
        },
        'licence': {
            header: 'Generate',
            title: 'Create images from a text prompt',
            inputText: '',
            placeholder: 'generate image'
        },
        'premium': {
            header: 'Premium',
            title: 'Create Premium from a text prompt',
            inputText: 'Make it more premium — dark tones, gold accents: ',
            placeholder: 'Make it more premium — dark tones, gold accents: Your Prompt'
        },
        'add-text': {
            header: 'Text overlay',
            title: 'Create text overlay from a text prompt',
            inputText: 'Add text overlay: ',
            placeholder: 'Add text overlay: your prompt'
        },
        'remove-bg': {
            header: 'Remove background',
            title: 'Create images with background',
            inputText: 'Remove background: ',
            placeholder: 'Remove background: your prompt'
        },
        '4k': {
            header: '4K',
            title: 'Upscale to 4K',
            inputText: 'Upscale to 4K: ',
            placeholder: 'Upscale to 4K: your prompt'
        },
        'compliance-runtime': {
            header: 'Compliance Check',
            title: 'Run compliance check on this image',
            inputText: 'Run compliance check on this image',
            placeholder: 'Run compliance check on this image'
        },
        'copy-action': {
            header: 'Copy',
            title: 'campaign copy',
            inputText: 'Write campaign copy for this image',
            placeholder: 'Write campaign copy for this image'
        }
    };

    private readonly navToMode: Record<NavKey, ChatModeKey | null> = {
        'generate': 'generate',
        'edit': 'edit',
        'upscale-2k-bria': 'upscale-2k-bria',
        'upscale-4k-bria': 'upscale-4k-bria',
        'upscale-2k-esrgan': 'upscale-2k-esrgan',
        'upscale-4k-esrgan': 'upscale-4k-esrgan',
        'stock': 'stock',
        'templates': 'templates',
        'copy': 'campaign-copy',
        'compliance': 'compliance',
        'licence': 'licence',
        'sessions': null
    };

    private shouldScroll = false;
    private stickToBottom = true;

    constructor(
        private chatService: ImageChatService,
        private toastr: ToastrService,
        private route: ActivatedRoute,
        private apiService: ApiService,
        private zone: NgZone,
        private router: Router,
        private authService: AuthenticationService,
        private recaptcha: RecaptchaService,

    ) {
        this.currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');

        // Read before the first render, so the page does not paint light and then
        // flip to dark a frame later.
        try {
            this.theme = localStorage.getItem(AiChatComponent.THEME_KEY) === 'dark' ? 'dark' : 'light';
        } catch {
            this.theme = 'light';
        }

        // A signed-out visitor has no sessions to list, so the column starts
        // folded rather than showing an empty panel with a dead New Session
        // button. The rail's Sessions item still opens it.
        this.sidebarCollapsed = !this.currentUser?.customers_id;
        // console.log('currentUser', this.currentUser);
    }

    /* ---------- Topbar account menu ---------- */

    /** Full name of the signed-in user, with the email/"Member" as fallbacks. */
    get userName(): string {
        const first = (this.currentUser?.customers_firstname || '').trim();
        const last = (this.currentUser?.customers_lastname || '').trim();
        const full = `${first} ${last}`.trim();
        return full || this.currentUser?.customers_email_address || 'Member';
    }

    get userEmail(): string {
        return this.currentUser?.customers_email_address || '';
    }

    /** One or two letters for the avatar disc. */
    get userInitials(): string {
        const first = (this.currentUser?.customers_firstname || '').trim();
        const last = (this.currentUser?.customers_lastname || '').trim();
        const initials = `${first.charAt(0)}${last.charAt(0)}`.trim();
        return (initials || this.userName.charAt(0) || 'U').toUpperCase();
    }

    logout(): void {
        this.authService.logout();
        this.router.navigate(['/']);
    }

    /* ---------- Signed-out visitors: the in-chat auth card ---------- */

    get isSignedIn(): boolean {
        return !!this.currentUser?.customers_id;
    }

    /**
     * Stands in for a generation when nobody is signed in. The prompt is shown as
     * the visitor's own turn and held in `authPrompt`, so signing in puts it back
     * in the composer rather than losing it.
     */
    private requireAccount(text: string): void {
        this.authPrompt = text;

        if (text) {
            this.messages.push({
                role: 'user',
                content: text,
                images: [],
                created_at: new Date().toISOString()
            });
        }

        // One card per prompt, so the answer sits with the request that provoked
        // it rather than the visitor having to scroll back to a single card.
        this.messages.push({
            role: 'assistant',
            content: '',
            images: [],
            created_at: new Date().toISOString(),
            auth: true
        });

        this.authError = '';
        this.pendingMessage = '';
        this.shouldScroll = true;
        this.stickToBottom = true;
    }

    /* ---------- Pricing dialog ---------- */

    openPricing(): void {
        this.showPricing = true;
    }

    closePricing(): void {
        this.showPricing = false;
    }

    /* ---------- Account dialog ---------- */

    /**
     * Open the dialog on one of its two tabs. Both buttons on the transcript's
     * auth card come through here.
     */
    openAuthDialog(mode: 'signin' | 'signup'): void {
        this.authDialog = mode;
        this.authError = '';
        this.authPassword = '';
        this.resetOtpState();

        // Fetch Google's reCAPTCHA script now so the token is ready by the time
        // they press the button, rather than adding a round trip to the click.
        this.recaptcha.preload();

        // A remembered identity is a convenience on the way in, so it is read
        // when the form opens rather than being held in a field all session.
        if (mode === 'signin' && !this.authIdentity) {
            try {
                const saved = localStorage.getItem(AiChatComponent.REMEMBER_KEY) || '';
                if (saved) {
                    this.authIdentity = saved;
                    this.authRemember = true;

                    // OTP is the tab that opens, so the remembered address is
                    // worth nothing there unless it is put in that field too.
                    // Only an email: the password field also takes a username,
                    // and the OTP endpoints will not.
                    if (!this.authOtpEmail && saved.indexOf('@') > -1) {
                        this.authOtpEmail = saved;
                    }
                }
            } catch { /* private mode — nothing remembered */ }
        }
    }

    /** Switch tabs without closing. Blocked mid-request, like the close button. */
    setAuthDialogTab(mode: 'signin' | 'signup'): void {
        if (this.authBusy) { return; }
        this.authDialog = mode;
        this.authError = '';
        // The two tabs send to different endpoints, so a code mailed by one is no
        // good to the other — the step goes back with the tab.
        this.resetOtpState();
    }

    /**
     * The password is dropped on close; the identity is not, so reopening after a
     * mistyped password doesn't mean typing the email again.
     */
    closeAuthDialog(): void {
        if (this.authBusy) { return; }
        this.authDialog = null;
        this.authError = '';
        this.authPassword = '';
        // The code is as short-lived as the password; the address is not, and is
        // the field the dialog reopens on.
        this.resetOtpState();
    }

    private rememberIdentity(identity: string): void {
        try {
            if (this.authRemember) {
                localStorage.setItem(AiChatComponent.REMEMBER_KEY, identity);
            } else {
                localStorage.removeItem(AiChatComponent.REMEMBER_KEY);
            }
        } catch { /* private mode — nothing to remember with */ }
    }

    /**
     * Forgot password, driven off whatever is in the Sign In identity field. It
     * mails a reset link rather than opening a second dialog over this one.
     */
    requestPasswordReset(): void {
        const email = (this.authIdentity || '').trim();
        if (email.indexOf('@') === -1) {
            this.authError = 'Enter the email address on your account, then choose Forgot password.';
            return;
        }

        this.authBusy = true;
        this.authError = '';

        this.apiService.forgotPassword(email).subscribe(
            (res: any) => {
                this.authBusy = false;
                if (res?.statusCode === 200) {
                    this.toastr.success('Check your email for a link to reset your password.');
                } else {
                    this.authError = 'We could not find an account for that email address.';
                }
            },
            () => {
                this.authBusy = false;
                this.authError = 'Could not send the reset email. Please try again.';
            }
        );
    }

    /* ---------- Google ---------- */

    /**
     * A full page navigation, not an XHR: Google's consent screen refuses to
     * render in a frame or an ajax response, so the browser itself has to go to
     * the backend, which redirects on to Google and later back to /auth/callback
     * with the session. One endpoint serves sign in and sign up — the backend
     * registers the address if it has not seen it before.
     *
     * The studio's path is left behind first so the callback returns here rather
     * than to the homepage, and any prompt the visitor was holding goes with it —
     * both would otherwise be lost on the way out.
     */
    continueWithGoogle(): void {
        if (this.authBusy) { return; }

        try {
            sessionStorage.setItem(AiChatComponent.RETURN_KEY, this.router.url || '/ai');
            if (this.authPrompt) {
                sessionStorage.setItem(AiChatComponent.PROMPT_KEY, this.authPrompt);
            }
        } catch { /* private mode — come back to the homepage instead */ }

        window.location.href = this.apiService.googleRedirectUrl;
    }

    /**
     * Pick up a prompt parked before the Google round trip and hand it back to
     * the composer. Read once and cleared, so a later visit does not resurrect
     * it; not sent automatically, for the same reason onAuthSuccess does not —
     * that would spend credits on something not confirmed since signing in.
     */
    private restoreParkedPrompt(): void {
        try {
            const parked = sessionStorage.getItem(AiChatComponent.PROMPT_KEY);
            if (parked) {
                sessionStorage.removeItem(AiChatComponent.PROMPT_KEY);
                if (!this.pendingMessage) {
                    this.pendingMessage = parked;
                }
            }
        } catch { /* private mode — nothing was parked */ }
    }

    /* ---------- Email OTP ---------- */

    /** Switch between the code and password forms. Blocked mid-request. */
    setAuthMethod(method: 'otp' | 'password'): void {
        if (this.authBusy) { return; }
        this.authMethod = method;
        this.authError = '';
    }

    /**
     * Step 1 — mail a code.
     *
     * The register endpoint refuses an address that already has an account and
     * the login one refuses an address that does not, so the tab decides which is
     * called. Both report the reason in `message`, which is copy written to point
     * at the other tab, so it is shown as-is.
     */
    sendAuthOtp(): void {
        const email = (this.authOtpEmail || '').trim();
        if (email.indexOf('@') === -1) {
            this.authError = 'Enter a valid email address.';
            return;
        }
        if (this.authBusy) { return; }

        const registering = this.authDialog === 'signup';

        this.authBusy = true;
        this.authOtpSending = true;
        this.authError = '';

        const send = registering
            ? this.authService.sendRegisterEmailOtp(email)
            : this.authService.sendLoginEmailOtp(email);

        send.then(result => {
            this.authBusy = false;
            this.authOtpSending = false;

            if (result.success) {
                this.authOtpStep = 2;
                this.authOtpCode = '';
                this.startResendCountdown();
                this.toastr.success(result.message);
            } else {
                this.authError = result.message;
            }
        });
    }

    /**
     * Step 2 — verify it.
     *
     * Both endpoints answer with a full session, and the register one creates the
     * account on the way, so there is no follow-up sign-in call here — unlike the
     * password register flow, which registers and then logs in.
     */
    verifyAuthOtp(): void {
        const code = (this.authOtpCode || '').trim();
        if (!/^[0-9]{6}$/.test(code)) {
            this.authError = 'Enter the 6 digits from your email.';
            return;
        }
        if (this.authBusy) { return; }

        const registering = this.authDialog === 'signup';
        const email = (this.authOtpEmail || '').trim();

        this.authBusy = true;
        this.authError = '';

        const verify = registering
            ? this.authService.verifyRegisterEmailOtp(email, code)
            : this.authService.verifyLoginEmailOtp(email, code);

        verify.then(result => {
            this.authBusy = false;

            if (result.success) {
                this.rememberIdentity(email);
                // Only on the sign-up tab: the opt-in is not offered on sign-in,
                // and this verify method serves both.
                if (registering) { this.saveMarketingOptIn(email); }
                this.onAuthSuccess(registering ? 'Welcome to IndiaPicture.' : 'Signed in.');
            } else {
                this.authError = result.message;
            }
        });
    }

    /** Back to step 1 with the code cleared, for a mistyped address. */
    editAuthOtpEmail(): void {
        if (this.authBusy) { return; }
        this.authOtpStep = 1;
        this.authOtpCode = '';
        this.authError = '';
        this.clearResendCountdown();
    }

    /** Same endpoint as the first send — the countdown is the only difference. */
    resendAuthOtp(): void {
        if (this.authResendIn > 0) { return; }
        this.sendAuthOtp();
    }

    private startResendCountdown(): void {
        this.clearResendCountdown();
        this.authResendIn = 30;

        this.authResendTimer = setInterval(() => {
            // setInterval fires outside Angular's zone in some browsers under
            // zone patching edge cases; run() keeps the countdown painting.
            this.zone.run(() => {
                this.authResendIn--;
                if (this.authResendIn <= 0) {
                    this.clearResendCountdown();
                }
            });
        }, 1000);
    }

    private clearResendCountdown(): void {
        if (this.authResendTimer) {
            clearInterval(this.authResendTimer);
            this.authResendTimer = null;
        }
        this.authResendIn = 0;
    }

    /** Everything the OTP forms hold, back to how the dialog opens. */
    private resetOtpState(): void {
        this.authOtpStep = 1;
        this.authOtpCode = '';
        this.authOtpSending = false;
        this.clearResendCountdown();
    }

    submitSignup(): void {
        const name = (this.authName || '').trim();
        const email = (this.authEmail || '').trim();
        const phone = (this.authPhone || '').trim();

        if (!name) { this.authError = 'Enter your full name.'; return; }
        if (email.indexOf('@') === -1) { this.authError = 'Enter a valid email address.'; return; }
        if (!this.authPassword) { this.authError = 'Choose a password.'; return; }
        if (this.authPassword.length < 6) {
            this.authError = 'Use at least 6 characters for your password.';
            return;
        }
        if (!this.authAcceptTerms) {
            this.authError = 'Please accept the Terms & Privacy Policy to continue.';
            return;
        }

        this.authBusy = true;
        this.authError = '';

        this.authService.register(name, email, phone, this.authPassword).then(created => {
            if (!created) {
                this.authBusy = false;
                // lastAuthMessage first: a rejected captcha is not a duplicate
                // account, and saying so sends them off to a sign-in that will
                // fail the same way.
                this.authError = this.authService.lastAuthMessage
                    || 'That account already exists. Try signing in instead.';
                return;
            }
            // Registering does not sign you in, so follow it straight through with
            // the same credentials rather than sending the visitor back to a form.
            this.authService.login(email, this.authPassword).then(signedIn => {
                this.authBusy = false;
                if (signedIn) {
                    this.saveMarketingOptIn(email);
                    this.onAuthSuccess('Welcome to IndiaPicture.');
                } else {
                    // The account exists now, so the useful next step is the other
                    // tab with the email already in place.
                    this.authDialog = 'signin';
                    this.authIdentity = email;
                    this.authError = this.authService.lastAuthMessage
                        || 'Account created, but signing in failed. Try Sign In.';
                }
            });
        });
    }

    /**
     * Put the address on the newsletter list, when the opt-in was ticked.
     *
     * Fire and forget, on purpose. The account exists by the time this runs, so
     * a mailing-list write that fails must not fail the sign-up, block the
     * dialog, or show the customer an error about something they opted into as
     * an afterthought. It is logged and left.
     *
     * The flag is cleared afterwards so a second sign-up in the same session
     * cannot inherit the first one's answer.
     */
    private saveMarketingOptIn(email: string): void {
        const address = (email || '').trim();

        if (!this.authPromos || !address) { return; }
        this.authPromos = false;

        this.apiService.addSubscribe(address).subscribe(
            () => { },
            (err: any) => console.error('newsletter opt-in could not be saved', err)
        );
    }

    submitSignin(): void {
        const identity = (this.authIdentity || '').trim();
        if (!identity || !this.authPassword) {
            this.authError = 'Enter your email or username and password.';
            return;
        }

        this.authBusy = true;
        this.authError = '';

        this.authService.login(identity, this.authPassword).then(signedIn => {
            this.authBusy = false;
            if (signedIn) {
                this.rememberIdentity(identity);
                this.onAuthSuccess('Signed in.');
            } else {
                this.authError = this.authService.lastAuthMessage
                    || 'Those details did not match an account.';
            }
        });
    }

    /**
     * Clears the card, picks up the newly stored user and hands the held prompt
     * back to the composer. The prompt is not sent automatically — that would
     * spend credits on an action the visitor has not confirmed since signing in.
     */
    private onAuthSuccess(message: string): void {
        this.currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        // The guest prompt and auth card are transient. Start a clean transcript
        // so emptyChatPhase can show the correct first-run/returning greeting.
        this.messages = [];
        this.activeSessionId = null;
        this.resetCredits();

        if (this.authPrompt) {
            this.pendingMessage = this.authPrompt;
            this.authPrompt = '';
        }

        this.authDialog = null;
        this.authName = '';
        this.authEmail = '';
        this.authPhone = '';
        this.authPassword = '';
        this.authAcceptTerms = false;
        this.authPromos = false;
        this.authError = '';
        this.resetOtpState();
        this.authOtpEmail = '';
        // authIdentity is left alone: remember-me may have just stored it, and it
        // is the field the dialog prefills next time.
        if (!this.authRemember) { this.authIdentity = ''; }

        // The panels a signed-out visitor had no use for become relevant now.
        this.sidebarCollapsed = false;
        this.sessionsListOpen = true;

        // Everything the studio shows is per-account, so all of it is refetched
        // against the account that just signed in. The most recent project is
        // then opened on top of that: a signed-in studio that opens on an empty
        // canvas looks like the sign-in did not take, and the work is one click
        // away rather than on screen.
        this.refreshStudioData(() => {
            const latest = this.latestSession;
            if (latest) { this.selectSession(latest); }
        });

        this.toastr.success(message);
    }

    /* ---------- Credits: balance chip and the Add-more dialog ---------- */

    /**
     * The balance shown beside Add more. Falls back to a placeholder rather than
     * a blank or a zero, so the chip reads sensibly before the studio's credits
     * call lands (and for customers whose plan carries no balance at all).
     */
    get creditCount(): number {
        const remaining = this.credits?.remaining;
        return remaining === null || remaining === undefined ? this.defaultCreditCount : remaining;
    }


    /** Where the dialog's footer link goes. */
    goToPlanComparison(): void {
        this.showCreditPlans = false;
        this.router.navigate(['/subscription']);
    }

    /**
     * Indian digit grouping — ₹1,20,000 rather than the ₹120,000 the `number`
     * pipe produces under the app's default locale.
     */
    inr(value: number): string {
        const digits = Math.round(value || 0).toString();
        if (digits.length <= 3) { return '₹' + digits; }
        const last3 = digits.slice(-3);
        const rest = digits.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
        return '₹' + rest + ',' + last3;
    }

    /** Class the global stylesheet uses to hide the Zendesk launcher here. */
    private static readonly HIDE_WIDGET_CLASS = 'hide-support-widget';

    ngOnInit(): void {
        // The studio owns the full viewport and the composer sits where the
        // launcher floats, so the support widget is hidden for this route only.
        document.body.classList.add(AiChatComponent.HIDE_WIDGET_CLASS);

        this.initStockSuggestions();
        this.refreshStudioData();

        this.restoreParkedPrompt();

        const params = this.route.snapshot.queryParamMap;
        const sessionId = params.get('sessionId');
        const prompt = params.get('prompt');

        // A ?sessionId= belongs to an account, so it is only opened once there is
        // one. Signing in re-runs refreshStudioData(), which reopens the most
        // recent project anyway.
        if (sessionId && this.isSignedIn) {
            this.activeSessionId = sessionId;
            this.loadHistory(sessionId);
        }

        if (prompt) {
            this.pendingMessage = prompt;
            if (!sessionId) {
                setTimeout(() => this.send(), 0);
            }
        }

        // Guided walkthrough — runs on every page load, as requested. The short
        // delay lets the layout (sidebar, settings panel, composer) settle so the
        // spotlight measures the real element positions.
        // setTimeout(() => this.startTour(), 700);
    }

    /** Load all studio data that can change when a visitor signs in. */
    /**
     * @param onSessions run once the sessions list has landed — see loadSessions.
     */
    private refreshStudioData(onSessions?: () => void): void {
        /*
         * The three discovery calls are made for everyone, signed in or not.
         *
         * /generationModels, /editTools and /videoModels all sit in the open route
         * group -- no jwt.verify -- and all return the same catalogue whatever
         * account asks: they are the line-up on offer, not an entitlement. So there
         * is no 401 to avoid here, which is what put them behind the guard below.
         *
         * The Generate rail's submenu is the studio's shop window: a visitor has to
         * be able to see what it makes before they have a reason to sign up, and
         * without the first of these the flyout sat on "Loading models..." for the
         * whole visit.
         *
         * Nothing is unlocked by showing them. isModelAllowed() waves every row
         * through while no permission lines are loaded, which is what a guest wants
         * -- a browsable list, not a wall of padlocks -- and sending still runs into
         * the in-chat auth card, which is where signing in belongs.
         */
        this.loadGenerationModels();
        this.loadEditToolsCatalogue();
        this.loadVideoModelsCatalogue();

        /*
         * Nothing further is fetched for a signed-out visitor.
         *
         * Every remaining call is per-account -- sessions, credits, the permission
         * row, the studio user key -- and most now require a token, so for a
         * guest they were a guaranteed round trip to a 401. /ai has no AuthGuard
         * and is deliberately reachable while signed out, so the page has to be
         * quiet until there is an account to fetch for.
         *
         * onAuthSuccess() sets currentUser and then calls this again, so
         * everything lands the moment the visitor signs in through the in-chat
         * card. Nothing is lost by not asking now.
         */
        if (!this.isSignedIn) {
            this.resetCredits();
            this.planCredits = null;
            return;
        }

        this.loadUserPermission();
        this.loadSessions(onSessions);

        // The badge's fallback, fetched whether or not a session is open: the
        // session balance can arrive later or not at all, and this is what stands
        // in for it meanwhile.
        this.loadPlanCredits();

        this.chatService.getStudioUserId().subscribe(
            res => { this.studioUserId = res?.user_id || ''; },
            () => { /* optional — backend also injects it on edit calls */ }
        );

        if (this.activeSessionId) {
            this.loadCredits(this.activeSessionId);
        } else {
            this.resetCredits();
        }
    }

    ngOnDestroy(): void {
        // Leaving the studio hands the widget back to the rest of the site.
        document.body.classList.remove(AiChatComponent.HIDE_WIDGET_CLASS);

        Object.keys(this.jobPollers).forEach(id => this.clearPoller(id));
        if (this.copiedMessageTimer) { clearTimeout(this.copiedMessageTimer); }
        if (this.activeRequest) {
            this.activeRequest.unsubscribe();
            this.activeRequest = null;
        }
        if (this.stockSuggestSub) {
            this.stockSuggestSub.unsubscribe();
            this.stockSuggestSub = null;
        }
        this.clearResendCountdown();
        this.stopRecording();
    }

    ngAfterViewChecked(): void {
        if (this.shouldScroll && this.messageList) {
            this.scrollToBottom(false);
            this.shouldScroll = false;
        }

        // The composer's text can change without a keystroke: a rail item's
        // prefill, a dictated take, a prompt handed back after signing in, or
        // send emptying it. Resizing from here catches all of them, where hooking
        // each call site would eventually miss one. The guard makes it a string
        // compare on the passes where nothing moved.
        const el = this.composerInputEl?.nativeElement;
        if (el && this.composerSizedFor !== el.value) {
            this.growComposer(el);
        }
    }

    /* ---------- Guided dashboard walkthrough ---------- */

    get tourStep(): TourStep {
        return this.tourSteps[this.tourIndex];
    }

    get isFirstTourStep(): boolean {
        return this.tourIndex === 0;
    }

    get isLastTourStep(): boolean {
        return this.tourIndex === this.tourSteps.length - 1;
    }

    /** Open the walkthrough at the first step. Also used by the help button. */
    startTour(): void {
        this.tourIndex = 0;
        this.showTour = true;
        // Wait a frame so *ngIf renders the overlay/popover before measuring.
        setTimeout(() => this.layoutTourStep(), 30);
    }

    endTour(): void {
        this.showTour = false;
    }

    nextTourStep(): void {
        if (this.isLastTourStep) {
            this.endTour();
            return;
        }
        this.tourIndex++;
        setTimeout(() => this.layoutTourStep(), 0);
    }

    prevTourStep(): void {
        if (this.isFirstTourStep) { return; }
        this.tourIndex--;
        setTimeout(() => this.layoutTourStep(), 0);
    }

    goToTourStep(i: number): void {
        if (i < 0 || i >= this.tourSteps.length || i === this.tourIndex) { return; }
        this.tourIndex = i;
        setTimeout(() => this.layoutTourStep(), 0);
    }

    @HostListener('window:resize')
    onTourViewportChange(): void {
        if (this.showTour) { this.layoutTourStep(); }
        // The flyouts' coordinates were measured against the old viewport.
        if (this.upscaleMenuOpen) { this.closeUpscaleMenu(); }
        if (this.generateMenuOpen) { this.closeGenerateMenu(); }
    }

    @HostListener('window:keydown', ['$event'])
    onTourKeydown(event: KeyboardEvent): void {
        // Escape closes the open popups too, whether or not the tour is running.
        if (event.key === 'Escape' && (this.upscaleMenuOpen || this.generateMenuOpen)) {
            this.closeUpscaleMenu();
            this.closeGenerateMenu();
            if (!this.showTour) { return; }
        }
        if (event.key === 'Escape' && this.authDialog) {
            this.closeAuthDialog();
            if (!this.showTour) { return; }
        }
        // TEMPORARY: remove with the coming-soon placeholder. Every other modal
        // here answers Escape, and one that did not would read as stuck.
        if (event.key === 'Escape' && this.comingSoonFeature) {
            this.closeComingSoon();
            if (!this.showTour) { return; }
        }
        if (event.key === 'Escape' && (this.showCreditPlans || this.editMenuImageId)) {
            this.closeEditMenu();
            if (!this.showTour) { return; }
        }
        if (!this.showTour) { return; }
        if (event.key === 'Escape') {
            event.preventDefault();
            this.endTour();
        } else if (event.key === 'ArrowRight' || event.key === 'Enter') {
            event.preventDefault();
            this.nextTourStep();
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.prevTourStep();
        }
    }

    /**
     * Measure the current step's target element and position both the spotlight
     * and the popover. Falls back to a centered card when there is no target
     * (welcome / finish) or the element isn't on screen.
     */
    private layoutTourStep(): void {
        const step = this.tourStep;
        const el = step?.target
            ? (document.querySelector(step.target) as HTMLElement | null)
            : null;

        if (!el) {
            this.tourSpot = { top: 0, left: 0, width: 0, height: 0, visible: false };
            this.tourArrow = 'none';
            this.placeTourPopover(null, 'center');
            return;
        }

        try { el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch { /* noop */ }

        const rect = el.getBoundingClientRect();
        const pad = 6;
        this.tourSpot = {
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            visible: true
        };
        this.placeTourPopover(rect, step.placement || 'bottom');
    }

    /** Compute the popover coordinates for a target rect + preferred side. */
    private placeTourPopover(rect: DOMRect | null, placement: TourStep['placement']): void {
        // Measure the rendered popover for accurate centering; fall back to
        // sensible defaults on the very first paint.
        setTimeout(() => {
            const pop = this.tourPopoverEl?.nativeElement;
            const pw = pop?.offsetWidth || 360;
            const ph = pop?.offsetHeight || 220;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const gap = 16;
            const margin = 12;

            let top: number;
            let left: number;
            let side = placement || 'bottom';

            if (!rect || side === 'center') {
                top = (vh - ph) / 2;
                left = (vw - pw) / 2;
                this.tourArrow = 'none';
            } else {
                // Flip to the opposite side if the preferred side would overflow.
                if (side === 'bottom' && rect.bottom + gap + ph > vh) { side = 'top'; }
                else if (side === 'top' && rect.top - gap - ph < 0) { side = 'bottom'; }
                else if (side === 'right' && rect.right + gap + pw > vw) { side = 'left'; }
                else if (side === 'left' && rect.left - gap - pw < 0) { side = 'right'; }

                switch (side) {
                    case 'top':
                        top = rect.top - ph - gap;
                        left = rect.left + rect.width / 2 - pw / 2;
                        this.tourArrow = 'bottom';
                        break;
                    case 'right':
                        top = rect.top + rect.height / 2 - ph / 2;
                        left = rect.right + gap;
                        this.tourArrow = 'left';
                        break;
                    case 'left':
                        top = rect.top + rect.height / 2 - ph / 2;
                        left = rect.left - pw - gap;
                        this.tourArrow = 'right';
                        break;
                    case 'bottom':
                    default:
                        top = rect.bottom + gap;
                        left = rect.left + rect.width / 2 - pw / 2;
                        this.tourArrow = 'top';
                        break;
                }
            }

            // Keep the whole card on screen.
            left = Math.max(margin, Math.min(left, vw - pw - margin));
            top = Math.max(margin, Math.min(top, vh - ph - margin));
            this.tourPop = { top, left };
        }, 0);
    }

    onMessageListScroll(): void {
        const el = this.messageList?.nativeElement;
        if (!el) { return; }
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        this.stickToBottom = distanceFromBottom < 80;
        this.onTranscriptScroll(el);
    }

    /**
     * Move the site header with the transcript's scroll direction.
     *
     * Scrolling DOWN shows it, scrolling UP hides it. That is the inverse of the
     * usual site convention, and deliberately so: the studio's transcript grows
     * downward from the composer, so reading back UP through the history is the
     * gesture that wants the screen, and coming back DOWN toward the newest turn
     * is the gesture that is done reading. Swap the two operands below to flip it.
     */
    private onTranscriptScroll(el: HTMLElement): void {
        const top = el.scrollTop;

        // Our own scrolling, or a transcript with nothing to scroll through:
        // neither is the reader asking for anything, so the bar stays put and the
        // baseline just follows along.
        if (Date.now() < this.headerScrollSuppressedUntil
            || el.scrollHeight - el.clientHeight <= AiChatComponent.HEADER_SCROLL_MIN_RANGE) {
            this.lastTranscriptScrollTop = top;
            return;
        }

        const delta = top - this.lastTranscriptScrollTop;

        // Left un-updated on purpose — see HEADER_SCROLL_THRESHOLD.
        if (Math.abs(delta) < AiChatComponent.HEADER_SCROLL_THRESHOLD) { return; }

        this.lastTranscriptScrollTop = top;
        this.headerHidden = delta < 0;
    }

    /**
     * Hand the header back and re-baseline, for the moments that are not a scroll
     * at all: opening a session, or signing in. Leaving the bar hidden there would
     * strand it until the reader happened to scroll down.
     */
    private resetHeaderTravel(): void {
        this.headerHidden = false;
        this.lastTranscriptScrollTop = this.messageList?.nativeElement?.scrollTop || 0;
    }


    /* ---------- Copy / edit a prompt ---------- */

    /**
     * The turn whose prompt was just copied, held so its button can show a tick.
     *
     * The message object itself rather than its index: the transcript is spliced
     * while a turn streams, and an index would end up marking whichever turn had
     * moved into that slot.
     */
    copiedMessage: UIMessage | null = null;

    private copiedMessageTimer: any = null;

    /**
     * Copy a turn's text to the clipboard — a prompt or a reply, same handler.
     *
     * The raw `content` is copied, not the rendered HTML: an assistant reply is
     * markdown, and markdown is what pastes usefully into an editor or back into
     * the composer. Copying the rendering would paste a wall of tags.
     *
     * Confirmed on the button rather than with a toast: the tick is attached to
     * the turn that was copied, which a toast floating over the composer is not.
     * Only a failure is worth interrupting for.
     */
    copyMessage(msg: UIMessage): void {
        const text = (msg && msg.content ? msg.content : '').trim();
        if (!text) { return; }

        this.writeToClipboard(text).then(ok => {
            if (!ok) {
                this.toastr.error('Could not copy the prompt');
                return;
            }

            this.copiedMessage = msg;

            if (this.copiedMessageTimer) { clearTimeout(this.copiedMessageTimer); }
            this.copiedMessageTimer = setTimeout(() => {
                this.copiedMessage = null;
                this.copiedMessageTimer = null;
            }, 1600);
        });
    }

    /**
     * Put text on the clipboard, by whichever route this page is allowed.
     *
     * navigator.clipboard exists only in a secure context, which http://<lan-ip>
     * is not — so it is absent on the dev server as soon as it is opened from
     * another machine. The hidden-textarea route is kept for that case rather
     * than letting the button quietly do nothing there.
     */
    private writeToClipboard(text: string): Promise<boolean> {
        if (navigator.clipboard && window.isSecureContext) {
            return navigator.clipboard.writeText(text)
                .then(() => true)
                .catch(() => this.copyByTextarea(text));
        }

        return Promise.resolve(this.copyByTextarea(text));
    }

    private copyByTextarea(text: string): boolean {
        try {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.setAttribute('readonly', '');
            // Off screen but not display:none — a hidden field cannot be selected,
            // and position:fixed keeps the page from scrolling to it.
            ta.style.position = 'fixed';
            ta.style.top = '-1000px';
            ta.style.opacity = '0';

            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(ta);

            return ok;
        } catch (e) {
            console.warn('clipboard fallback failed', e);
            return false;
        }
    }

    /**
     * Whether this turn's result carries its own action bar.
     *
     * That bar already prints the timestamp, so the row at the foot of the
     * bubble would print it a second time. When this is true the foot row is
     * suppressed and its copy button moves up beside the size chip.
     *
     * Gated on the assistant role because the bar itself is: a USER turn with an
     * attachment renders no bar, so suppressing its foot row would take away its
     * copy button and leave it with nothing.
     */
    showsMediaBar(msg: UIMessage): boolean {
        if (!msg || msg.role !== 'assistant') { return false; }

        return !!((msg.images && msg.images.length) || (msg.videos && msg.videos.length));
    }

    /**
     * Load a prompt back into the composer to be worked on.
     *
     * Offered on the customer's own turns only. It used to sit on both sides,
     * where on a reply it meant "carry this forward" — take what the model wrote
     * as the starting text for the next turn. That is a different action wearing
     * the same icon, and on the assistant side it read as an offer to edit the
     * reply itself, which this has never done.
     *
     * The transcript is left alone: this means "start from these words", not
     * "rewrite history". The original turn and whatever it produced stay in the
     * session, and sending adds a new one.
     */
    editMessage(msg: UIMessage): void {
        const text = msg && msg.content ? msg.content : '';
        if (!text) { return; }

        this.pendingMessage = text;

        /*
         * Deferred one tick, for two reasons: ngAfterViewChecked grows the
         * textarea from pendingMessage and the caret has to be set after it has
         * its height, and the click that got here would otherwise take the focus
         * straight back.
         *
         * Caret at the end rather than the text selected — the point is to amend
         * the prompt, and a full selection means the next keystroke wipes it.
         */
        setTimeout(() => {
            const el = this.composerInputEl?.nativeElement;
            if (!el) { return; }

            el.focus();
            try {
                el.setSelectionRange(text.length, text.length);
            } catch (e) {
                // Older engines throw on a textarea that is not yet laid out;
                // the focus is the part that matters.
            }
        }, 0);
    }

    onMessageImageLoad(event?: Event, media?: { id?: string; url: string }): void {
        this.recordImageSize(event, media);

        if (this.stickToBottom) {
            this.scrollToBottom(false);
        }
    }

    /* ---------- Pixel dimensions shown beside each result ---------- */

    /**
     * Measured dimensions per asset, keyed by id (or url, for assets that arrive
     * without one). Read off the element rather than the payload: the studio only
     * fills width/height on some responses, but a loaded <img>/<video> always
     * knows its own size.
     */
    private mediaDims: { [key: string]: string } = {};

    private mediaKey(media?: { id?: string; url?: string }): string {
        return media?.id || media?.url || '';
    }

    private recordImageSize(event?: Event, media?: { id?: string; url: string }): void {
        const key = this.mediaKey(media);
        if (!key || this.mediaDims[key]) { return; }

        const el = event?.target as HTMLImageElement | null;
        const w = el?.naturalWidth;
        const h = el?.naturalHeight;
        if (w && h) { this.mediaDims[key] = w + ' × ' + h; }
    }

    /** Same job for a video, off the metadata rather than the pixels. */
    onVideoMetadata(event: Event, media: { id?: string; url: string }): void {
        const key = this.mediaKey(media);
        if (!key || this.mediaDims[key]) { return; }

        const el = event?.target as HTMLVideoElement | null;
        const w = el?.videoWidth;
        const h = el?.videoHeight;
        if (w && h) { this.mediaDims[key] = w + ' × ' + h; }
    }

    /**
     * "1024 × 1024" for an asset whose size is known, '' otherwise — the label is
     * hidden rather than showing a placeholder while an image loads.
     * Prefers what the payload declared, so a cached image that fires no load
     * event still gets a figure.
     */
    mediaSize(media?: { id?: string; url?: string; width?: number; height?: number }): string {
        if (media?.width && media?.height) { return media.width + ' × ' + media.height; }
        return this.mediaDims[this.mediaKey(media)] || '';
    }

    private scrollToBottom(smooth = true): void {
        const el = this.messageList?.nativeElement;
        if (!el) { return; }
        /*
         * Claimed before the scroll starts, and for long enough to cover it: a
         * smooth scroll emits events for the whole glide, and every one of them
         * would read as the reader travelling downward.
         */
        this.headerScrollSuppressedUntil = Date.now() + (smooth ? 800 : 150);
        el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
        this.stickToBottom = true;
    }

    /**
     * @param done run once the list has landed. Signing in needs this: the newest
     * session cannot be opened until there is a list to pick it from.
     */
    loadSessions(done?: () => void): void {
        this.isLoadingSessions = true;
        this.chatService.listSessions(50, this.currentUser.customers_id).subscribe(
            res => {
                this.sessions = res?.sessions || [];
                this.isLoadingSessions = false;
                if (done) { done(); }
            },
            () => {
                this.isLoadingSessions = false;
                this.toastr.error('Failed to load sessions');
            }
        );
    }

    /**
     * Sessions matching the sidebar search. Filters the loaded page of sessions
     * (listSessions caps at 50) on title only — that's all the row shows.
     */
    get filteredSessions(): ChatSessionDto[] {
        // The unsent draft heads the list: it is where the next message goes, and
        // the studio has no row for it yet.
        const all = this.draftSession ? [this.draftSession, ...this.sessions] : this.sessions;

        const q = this.sessionSearch.trim().toLowerCase();
        if (!q) { return all; }
        return all.filter(s => (s.title || 'Untitled session').toLowerCase().includes(q));
    }

    clearSessionSearch(): void {
        this.sessionSearch = '';
    }

    /* ---- Empty-session starting points ---- */

    private readonly imagePromptStarters = [
        'A serene mountain lake at dawn',
        'Portrait of a cyberpunk samurai',
        'Minimal product shot of a perfume bottle',
        'Cozy cabin in a snowy forest'
    ];

    /** Drop a starter into the composer so the user can tweak it before sending. */
    usePromptStarter(text: string): void {
        this.pendingMessage = text;
        this.lastPrefill = text;
    }

    newSession(): void {
        this.activeSessionId = null;
        this.messages = [];
        this.attachedImages = [];
        this.pendingMessage = '';
        this.resetCredits();
        this.resetHeaderTravel();
    }

    /* ---------- Empty-chat greeting ---------- */

    /**
     * Which greeting the empty transcript shows:
     *   'guest'     — nobody signed in
     *   'first-run' — signed in, nothing generated yet
     *   'returning' — signed in with past sessions to pick up
     */
    get emptyChatPhase(): 'guest' | 'first-run' | 'returning' {
        if (!this.isSignedIn) { return 'guest'; }
        return this.sessions && this.sessions.length ? 'returning' : 'first-run';
    }

    /**
     * The session "continue previous project" opens. Picked by timestamp rather
     * than trusting the list order, since that is the studio's to decide.
     */
    get latestSession(): ChatSessionDto | null {
        if (!this.sessions || !this.sessions.length) { return null; }
        return this.sessions.reduce(
            (newest, s) => (this.sessionTime(s) > this.sessionTime(newest) ? s : newest),
            this.sessions[0]
        );
    }

    private sessionTime(session: ChatSessionDto): number {
        const parsed = Date.parse(session?.updated_at || session?.created_at || '');
        return isNaN(parsed) ? 0 : parsed;
    }

    continuePreviousProject(): void {
        const session = this.latestSession;
        if (!session) { return; }

        // The sessions panel starts folded for a fresh visit; opening a project
        // from here shows which one is now active.
        this.sidebarCollapsed = false;
        this.sessionsListOpen = true;
        this.selectSession(session);
    }

    startSomethingNew(): void {
        // First, because the setter clears any previous draft on the way past.
        this.newSession();

        const now = new Date().toISOString();
        this.draftSession = {
            id: AiChatComponent.DRAFT_SESSION_ID,
            title: 'Untitled',
            model: '',
            created_at: now,
            updated_at: now
        };

        // Selects the placeholder, so the row reads as the one being written to.
        this.activeSessionId = AiChatComponent.DRAFT_SESSION_ID;

        // A filter left over from before would hide the row we just added.
        this.sessionSearch = '';

        // The list is where the new row appears; folded away, nothing would.
        this.sidebarCollapsed = false;
        this.sessionsListOpen = true;

        this.focusComposer();
    }

    /**
     * Put the caret in the composer so typing can start immediately.
     *
     * Deferred a tick: the click that got here is still settling, and taking
     * focus in the same turn hands it straight back to the button.
     */
    private focusComposer(): void {
        setTimeout(() => {
            const el = this.composerInputEl?.nativeElement;
            if (el) { el.focus(); }
        }, 0);
    }

    selectSession(session: ChatSessionDto): void {
        if (this.activeSessionId === session.id) {
            return;
        }

        // The draft has no server row to fetch a history for; clicking it just
        // returns to the empty composer.
        if (session.id === AiChatComponent.DRAFT_SESSION_ID) {
            this.activeSessionId = AiChatComponent.DRAFT_SESSION_ID;
            this.focusComposer();
            return;
        }
        this.activeSessionId = session.id;
        this.loadHistory(session.id);
    }

    private loadHistory(sessionId: string): void {
        this.messages = [];
        this.attachedImages = [];
        this.isLoadingHistory = true;
        // The transcript the last scroll position referred to is going away.
        this.resetHeaderTravel();
        this.chatService.getHistory(sessionId).subscribe(
            res => {
                const msgs: UIMessage[] = (res?.messages || []).map(m => ({
                    role: m.role,
                    content: m.content,
                    images: m.images || [],
                    videos: (m.videos || []).map(v => ({ id: v.id, url: v.url })),
                    created_at: m.created_at
                }));
                this.attachVideoPrompts(msgs);
                this.messages = msgs;
                this.shouldScroll = true;
                this.stickToBottom = true;
                this.isLoadingHistory = false;
            },
            () => {
                this.isLoadingHistory = false;
                this.toastr.error('Failed to load chat history');
            }
        );
        this.loadCredits(sessionId);
    }

    /**
     * History stores a video's prompt on the user turn that asked for it, while
     * the assistant turn only carries a confirmation line. Copy it across so the
     * video can be shown with the prompt that produced it.
     */
    private attachVideoPrompts(msgs: UIMessage[]): void {
        for (let i = 0; i < msgs.length; i++) {
            const m = msgs[i];
            if (m.role !== 'assistant' || !m.videos?.length || m.prompt) { continue; }

            for (let j = i - 1; j >= 0; j--) {
                if (msgs[j].role === 'user' && msgs[j].content) {
                    m.prompt = msgs[j].content;
                    break;
                }
            }
        }
    }

    send(): void {
        const text = (this.pendingMessage || '').trim();
        if ((!text && this.attachedImages.length === 0) || this.isSending) {
            return;
        }

        // Every generation path runs through here, so this one guard covers them
        // all: nothing is submitted for a visitor with no account.
        if (!this.isSignedIn) {
            this.requireAccount(text);
            return;
        }

        // Stock is a search, not a generation: it queries the library and lists
        // what comes back, spending no credits on a model.
        if (this.isStockMode && text) {
            this.runStockSearch(text);
            return;
        }

        // Converse mode: let the studio orchestrator decide chat vs create/refine.
        if (this.converseMode && text && this.attachedImages.length === 0) {
            this.converseSend();
            return;
        }

        // In "Generate" mode with an image model selected, use the parameterized
        // studio generate endpoint (honours the Generation Settings panel). Video
        // always goes through the orchestrator instead, so it can confirm a model
        // switch and hand back a job to poll.
        if (this.modelKind === 'image' && this.activeNav === 'generate'
            && this.selectedMode && this.attachedImages.length === 0) {
            this.generateWithSettings();
            return;
        }

        const userMsg: UIMessage = {
            role: 'user',
            content: text,
            images: this.attachedImages.map(a => ({ id: a.id, url: a.url })),
            created_at: new Date().toISOString()
        };
        this.messages.push(userMsg);

        const req: ChatRequest = {};
        if (this.activeSessionId) { req.session_id = this.activeSessionId; }
        if (text) { req.message = text; }
        if (this.attachedImages.length) {
            req.attached_image_ids = this.attachedImages.map(a => a.id);
        }
        req.customer_id = JSON.parse(localStorage.getItem('currentUser') || 'null')?.customers_id;
        // Forward the selected model + its control values so the orchestrator
        // honours the active model's settings (controls vary per model, and the
        // video models carry duration / resolution / aspect_ratio here).
        if (this.selectedMode) { req.model = this.selectedMode; }
        if (this.controlValues && Object.keys(this.controlValues).length) {
            req.controls = this.controlValues;
        }

        this.pendingMessage = '';
        this.attachedImages = [];

        this.streamTurn(req, userMsg);
    }

    /**
     * The catalogue model a reported id refers to.
     *
     * Ids arrive in several spellings — 'image.nano_banana', 'google/nano_banana',
     * plain 'nano_banana' — so the value is cut back to the part after the last
     * slash and dot, the same reduction the backend's serviceToken() makes.
     */
    private modelByToken(value: any): ModelInfo | undefined {
        if (typeof value !== 'string' || !value.trim()) { return undefined; }

        let token = value.toLowerCase().trim();
        const slash = token.lastIndexOf('/');
        if (slash > -1) { token = token.slice(slash + 1); }
        const dot = token.lastIndexOf('.');
        if (dot > -1) { token = token.slice(dot + 1); }

        return this.allModels.find(m => (m.key || '').toLowerCase() === token);
    }

    /**
     * Whether this step means an image is being made now.
     *
     * A turn opens on the chat model — "Understanding your request — Claude 4.5
     * Sonnet" — and only reaches an image model if it decides to generate. So the
     * placeholder cannot be chosen when the turn is sent: a question asked with an
     * image model selected is answered in words, and would have sat behind an
     * image frame that never filled.
     *
     * The model the step reports is the signal. The label is only consulted when
     * no id came through, and even then it is matched against the catalogue's own
     * labels rather than scanned for verbs.
     */
    private stepMakesImage(ev: StreamEvent): boolean {
        for (const key of ['model_used', 'model', 'operation']) {
            const model = this.modelByToken(ev[key]);
            if (model) { return this.kindOf(model) === 'image'; }
        }

        const label = (ev.label || '').toLowerCase();
        if (!label) { return false; }

        return this.allModels.some(m =>
            this.kindOf(m) === 'image'
            && !!m.label
            && label.indexOf(m.label.toLowerCase()) > -1);
    }

    /**
     * Frame slots for the placeholder grid.
     *
     * Cached per count so the getter hands *ngFor the same array on every change
     * detection pass — a fresh literal would make Angular tear the frames down and
     * rebuild them, restarting the shimmer on every tick.
     */
    private skeletonFrameCache: { [count: number]: number[] } = {};

    imageSkeletonFrames(count: number | undefined): number[] {
        const n = Math.min(Math.max(Math.floor(count || 1), 1), 4);
        if (!this.skeletonFrameCache[n]) {
            this.skeletonFrameCache[n] = Array(n).fill(0);
        }
        return this.skeletonFrameCache[n];
    }

    /** How many frames to draw, from what the turn asked for. Clamped to the picker's range. */
    private turnImageCount(req: ChatRequest): number {
        const asked = Number(req.controls?.num_images);
        if (!isFinite(asked) || asked < 1) { return 1; }
        return Math.min(Math.floor(asked), 4);
    }

    /**
     * Run one orchestrator turn over the streaming sendMessage feed.
     *
     * Shared by the first send and by the confirm re-send, so both handle live
     * steps, the model-switch handshake, async video jobs and errors the same way.
     */
    private streamTurn(req: ChatRequest, userMsg: UIMessage | null): void {
        const pending: UIMessage = {
            role: 'assistant',
            content: '',
            images: [],
            created_at: new Date().toISOString(),
            pending: true,
            // Starts as a plain chat turn — the dots. A step naming an image model
            // promotes it to the image placeholder below.
            imagePending: false,
            imageCount: this.turnImageCount(req)
        };
        this.messages.push(pending);
        this.shouldScroll = true;
        this.stickToBottom = true;
        this.beginTurn(pending);

        let gotResult = false;

        /*
         * The orchestrator's id for this turn, kept so an answer can be tied back to
         * the question that asked it. Read off every event rather than only the
         * result, since the id is issued when the turn opens and any event may be
         * the one carrying it.
         */
        let turnId: string | null = null;

        this.activeRequest = this.chatService.sendMessageStream(req).subscribe({
            next: (ev: StreamEvent) => {
                // Mirrored onto the component so stopGenerating() can cancel this
                // turn upstream; the local above still drives everything it did.
                if (ev.turn_id) { turnId = ev.turn_id; this.activeTurnId = ev.turn_id; }

                if (ev.event === 'step') {
                    // Show the live step label under the typing loader.
                    pending.step = ev.label || '';

                    // Once an image model is running, the wait becomes the frame
                    // the picture will land in. One-way: a later chat step must
                    // not drop the frame back to dots mid-turn.
                    if (!pending.imagePending && this.stepMakesImage(ev)) {
                        pending.imagePending = true;
                    }

                    this.shouldScroll = true;
                    return;
                }

                if (ev.event === 'error') {
                    this.toastr.error(ev.detail || 'Generation failed');
                    return;
                }

                if (ev.event !== 'result') { return; }

                gotResult = true;
                if (ev.session_id) { this.activeSessionId = ev.session_id; }

                /*
                 * An upscale can be served by more than one provider and they are
                 * not interchangeable — they differ in cost and in the ceiling they
                 * can reach — so the orchestrator asks rather than choosing. Nothing
                 * is spent until an option is picked; picking replays this same turn
                 * with upscale_model set.
                 */
                if (ev.action === 'choose_upscaler') {
                    this.removePending(pending);
                    this.upscalerChoice = ev;
                    this.upscalerContext = {
                        req,
                        userMsg,
                        turnId: turnId || ev.turn_id || ev.pending?.turn_id || null,
                        // Which image is being upscaled. The replay normally carries
                        // the turn's own attachment; this is the fallback for an
                        // upscale asked for without one, where the orchestrator
                        // resolved the image itself and names it here.
                        imageId: ev.image_id || ev.pending?.image_id || null
                    };
                    return;
                }

                // The orchestrator wants a model change agreed before it spends
                // anything — ask, then re-send the same turn with confirm:true.
                if (ev.action === 'confirm' && ev.pending) {
                    this.removePending(pending);
                    this.switchConfirm = ev;
                    this.switchContext = { req, userMsg };
                    return;
                }

                // Video comes back as an async job; keep the bubble pending and poll.
                if (ev.job?.job_id) {
                    pending.pending = true;
                    pending.step = '';
                    pending.prompt = req.message || '';
                    pending.job = {
                        status: ev.job.status || 'queued',
                        model: ev.model_label || ev.model_used,
                        operation: ev.job.operation || 'text_to_video',
                        startedAt: Date.now(),
                        elapsed: 0
                    };
                    this.activeJobId = ev.job.job_id;
                    this.pollJob(ev.job.job_id, pending);
                    return;
                }

                pending.pending = false;
                pending.step = '';
                pending.content = ev.ai_message || '';
                pending.images = (ev.new_images || []).map(i => ({ id: i.id, url: i.url }));
                this.shouldScroll = true;
                this.stickToBottom = true;
            },
            error: (err) => {
                this.removePending(pending);
                this.endTurn();
                this.toastr.error(err?.error?.message || err?.error?.detail || 'Generation failed');
            },
            complete: () => {
                // If the stream ended without a result event, drop the placeholder.
                if (!gotResult) { this.removePending(pending); }
                this.endTurn();
                this.shouldScroll = true;
                this.stickToBottom = true;
                this.loadSessions();
                if (this.activeSessionId) { this.loadCredits(this.activeSessionId); }
            }
        });
    }

    /* ---- Stop the turn in flight ---- */

    /**
     * The orchestrator's id for the live turn, as soon as the stream reports it.
     *
     * Held on the component, not just in streamTurn's local, because the cancel
     * endpoint is keyed on it and stopGenerating() is called from outside that
     * closure. Null until the first event arrives — a turn stopped in the gap
     * before then has no id to cancel, and tears down locally as it always did.
     */
    private activeTurnId: string | null = null;

    /** Register a turn as the live one. Every send path goes through here. */
    private beginTurn(pending: UIMessage): void {
        this.activePending = pending;
        this.activeJobId = null;
        this.activeTurnId = null;
        this.isSending = true;
    }

    /** A turn that finished on its own — nothing left for stop to cut off. */
    private endTurn(): void {
        this.isSending = false;
        this.activeRequest = null;
        this.activePending = null;
        this.activeJobId = null;
        this.activeTurnId = null;
    }

    /**
     * True while anything is running that the reader can stop: an open request,
     * or a video job being polled. Drives the composer's stop button, which
     * stands in for send exactly as long as this holds.
     */
    get isGenerating(): boolean {
        return this.isSending || !!this.activeJobId;
    }

    /**
     * Stop the turn. The request is dropped and any poller with it, the run is
     * cancelled upstream, and the bubble stays in the transcript marked as
     * stopped rather than vanishing.
     *
     * This used to be client-side only — the studio exposed no cancel, so a render
     * already under way ran to completion and we merely stopped listening. It now
     * calls converse/stop, so the work is actually abandoned.
     *
     * The local teardown still runs FIRST and does not wait for that call. Stop
     * has to be instant to be worth pressing, the request may fail or be slow,
     * and a turn that has already finished upstream answers 404 — none of which
     * should leave the composer stuck showing a stop button.
     */
    stopGenerating(): void {
        if (!this.isGenerating) { return; }

        /*
         * Read before the teardown below clears it.
         *
         * Null when the turn was stopped in the gap before its first event, which
         * is the one case with nothing to cancel: the orchestrator issues the id
         * with the first event, so upstream has not started a run to abandon.
         */
        const turnId = this.activeTurnId;

        // Unsubscribed first: an event still in flight would otherwise land on
        // the bubble after it has been marked.
        if (this.activeRequest) {
            this.activeRequest.unsubscribe();
            this.activeRequest = null;
        }

        if (this.activeJobId) {
            this.clearPoller(this.activeJobId);
            this.activeJobId = null;
        }

        const pending = this.activePending;
        if (pending) {
            pending.pending = false;
            pending.stopped = true;
            pending.step = '';
            pending.job = undefined;
        }

        this.activePending = null;
        this.activeTurnId = null;
        this.isSending = false;
        this.editingImageId = null;
        this.shouldScroll = true;

        /*
         * Told upstream last, and its result is not acted on. The reader has had
         * their answer — the turn is visibly stopped — so a failed cancel is worth
         * a log and nothing more. Raising a toast here would report a problem the
         * reader cannot do anything about, for a turn they have already left.
         */
        if (turnId) {
            this.chatService.stopConverse(turnId).subscribe(
                res => {
                    if (!res || res.stopped !== true) {
                        console.warn('upstream did not confirm the stop', turnId, res);
                    }
                },
                err => console.warn('upstream stop failed', turnId, err)
            );
        }
    }

    /* <TEMPORARY — coming-soon placeholder> ======================================
     *
     * Stands in for Compliance and Licence until their real screens exist. The
     * two rail items were `disabled`, which said "not for you" rather than "not
     * yet"; this lets them answer a click and explain themselves.
     *
     * TO REMOVE, when either feature ships: delete this block, delete the
     * .coming-soon modal from the template and its rules from the stylesheet, and
     * point that rail item's (click) back at selectNav('compliance' | 'licence').
     * Nothing else references any of it — that is deliberate, so taking it out is
     * three deletions and no untangling.
     * ------------------------------------------------------------------------- */

    /** The feature whose placeholder is open, by display name. Null when closed. */
    comingSoonFeature: string | null = null;

    openComingSoon(feature: string): void {
        this.comingSoonFeature = feature;
    }

    closeComingSoon(): void {
        this.comingSoonFeature = null;
    }

    /* </TEMPORARY — coming-soon placeholder> */

    /* ---- Model-switch confirmation ---- */

    /** The result event that asked to confirm a switch; null when no modal is up. */
    switchConfirm: StreamEvent | null = null;

    /** The turn to replay on confirm, with the user bubble it belongs to. */
    private switchContext: { req: ChatRequest; userMsg: UIMessage | null } | null = null;

    /** Re-send the same turn with the orchestrator's `pending` and confirm:true. */
    confirmModelSwitch(): void {
        const ev = this.switchConfirm;
        const ctx = this.switchContext;
        this.closeSwitchConfirm();

        if (!ev || !ctx) { return; }
        this.streamTurn({ ...ctx.req, confirm: true, pending: ev.pending }, ctx.userMsg);
    }

    /**
     * Drop the turn entirely and hand the prompt back, so declining a switch
     * leaves no half-answered exchange in the transcript.
     */
    cancelModelSwitch(): void {
        const ctx = this.switchContext;
        this.closeSwitchConfirm();
        if (!ctx) { return; }

        if (ctx.userMsg) { this.removePending(ctx.userMsg); }
        if (!this.pendingMessage) { this.pendingMessage = ctx.req.message || ''; }
    }

    private closeSwitchConfirm(): void {
        this.switchConfirm = null;
        this.switchContext = null;
    }

    /* ---- Upscaler choice ---- */

    /** The result event that asked which upscaler to use; null when no modal is up. */
    upscalerChoice: StreamEvent | null = null;

    /**
     * The turn to replay once an upscaler is picked.
     *
     * turnId is carried separately because the orchestrator keys the answer to the
     * turn that asked, and it is not part of the request that produced it.
     */
    private upscalerContext: {
        req: ChatRequest;
        userMsg: UIMessage | null;
        turnId: string | null;
        imageId: string | null;
    } | null = null;

    /**
     * The options as the modal lists them: Auto first, then whatever the
     * orchestrator offered.
     *
     * Auto is added here rather than sent by the orchestrator because it is not a
     * provider — it is "you decide", and the only choice that may switch provider
     * when the source is too large for the one it would otherwise use.
     */
    get upscalerOptions(): UpscalerOption[] {
        const offered = (this.upscalerChoice?.options || []).filter(o => o.available !== false);

        return [
            {
                key: 'auto',
                label: 'Auto',
                subtitle: 'Chosen from the target and this source’s size, and the only option '
                    + 'that can switch provider if the source is too large for one of them.'
            },
            ...offered
        ];
    }

    /** "Upscale to 4K" — the heading, from the target the orchestrator asked about. */
    get upscalerTargetLabel(): string {
        return (this.upscalerChoice?.target || '').toUpperCase();
    }

    /** "1365 × 2048", or '' when the orchestrator didn't report a source size. */
    get upscalerSourceLabel(): string {
        const size = this.upscalerChoice?.source_size;
        return Array.isArray(size) && size.length >= 2 ? `${size[0]} × ${size[1]}` : '';
    }

    /** Replay the turn with the chosen upscaler. Nothing was spent before this. */
    pickUpscaler(option: UpscalerOption): void {
        const ctx = this.upscalerContext;
        this.closeUpscalerChoice();

        if (!ctx || !option?.key) { return; }

        // The same turn again — session_id, message, model, controls and the
        // attachment all unchanged — plus the answer.
        const req: ChatRequest = { ...ctx.req, upscale_model: option.key };
        if (ctx.turnId) { req.turn_id = ctx.turnId; }
        if (!req.attached_image_ids?.length && ctx.imageId) {
            req.attached_image_ids = [ctx.imageId];
        }

        this.streamTurn(req, ctx.userMsg);
    }

    /**
     * Drop the turn and hand the prompt back, so declining leaves no half-answered
     * exchange in the transcript — same as cancelling a model switch.
     */
    cancelUpscalerChoice(): void {
        const ctx = this.upscalerContext;
        this.closeUpscalerChoice();
        if (!ctx) { return; }

        if (ctx.userMsg) { this.removePending(ctx.userMsg); }
        if (!this.pendingMessage) { this.pendingMessage = ctx.req.message || ''; }
    }

    private closeUpscalerChoice(): void {
        this.upscalerChoice = null;
        this.upscalerContext = null;
    }

    /* ============================================================
     *  Studio API — generation settings, edit tools, video, credits
     * ============================================================ */

    loadGenerationModels(): void {
        this.chatService.getGenerationModels().subscribe(
            (res: GenerationModelsResponse) => {
                const models = res?.models || [];
                this.allModels = models.length ? models : this.fallbackModels();

                // Studio default only applies to the image side; each kind keeps
                // its own remembered pick.
                const imageDefault = res?.default && this.allModels.some(m => m.key === res.default && this.kindOf(m) === 'image')
                    ? res.default
                    : this.firstKeyOfKind('image');
                this.modelByKind = { image: imageDefault, video: this.firstKeyOfKind('video') };

                const keep = this.selectedMode && this.allModels.some(m => m.key === this.selectedMode)
                    ? this.selectedMode
                    : '';
                this.selectMode(keep || this.modelByKind[this.modelKind] || imageDefault);

                // Permissions may already be in — re-check the pick against them.
                this.applyModelPermissions();
            },
            () => {
                // Discovery uses a studio X-API-Key we may not have — fall back to
                // the known model catalogue so the panel stays usable.
                this.allModels = this.fallbackModels();
                this.modelByKind = { image: 'chatimage', video: this.firstKeyOfKind('video') };
                this.selectMode(this.selectedMode || 'chatimage');
                this.applyModelPermissions();
            }
        );
    }

    /** Older payloads may omit `kind`; treat those as image models. */
    private kindOf(model: ModelInfo | undefined): 'image' | 'video' {
        return model?.kind === 'video' ? 'video' : 'image';
    }

    private firstKeyOfKind(kind: 'image' | 'video'): string {
        return this.allModels.find(m => this.kindOf(m) === kind && this.isModelAllowed(m))?.key || '';
    }

    /**
     * Whether the customer's plan sells this model — matched to a credit-pack
     * line on the text after the dot, so `image.chatimage` is the line for the
     * model keyed `chatimage`.
     *
     * With no lines at all (the plan predates credit packs, or the lookup
     * failed) nothing is hidden: the backend treats a missing line as uncharged
     * rather than refused, so filtering everything out would empty the picker
     * for customers who are allowed to generate.
     */
    private isModelAllowed(model: ModelInfo | undefined): boolean {
        if (!model) { return false; }
        if (!this.permissionTools.length) { return true; }
        return !!this.permissionToolFor(model.key);
    }

    /** A model's own control defaults, keyed by control (duration, resolution…). */
    private videoControlDefaults(model: ModelInfo | undefined): { [key: string]: any } {
        const values: { [key: string]: any } = {};
        for (const control of model?.controls || []) {
            if (control.default !== undefined && control.default !== null && control.default !== '') {
                values[control.key] = control.default;
            }
        }
        return values;
    }

    get hasVideoModels(): boolean {
        return this.allModels.some(m => this.kindOf(m) === 'video' && this.isModelAllowed(m));
    }

    /**
     * Drop a selection the plan doesn't sell. The catalogue and the permissions
     * load in parallel, so whichever lands first may have picked a model the
     * other rules out — this runs after both and settles it.
     */
    private applyModelPermissions(): void {
        if (!this.allModels.length || !this.permissionTools.length) { return; }

        const allowedKey = (key: string) =>
            !!key && this.isModelAllowed(this.allModels.find(m => m.key === key));

        if (!allowedKey(this.modelByKind.image)) { this.modelByKind.image = this.firstKeyOfKind('image'); }
        if (!allowedKey(this.modelByKind.video)) { this.modelByKind.video = this.firstKeyOfKind('video'); }

        if (allowedKey(this.selectedMode)) { return; }

        const sameKind = this.modelByKind[this.modelKind];
        if (sameKind) {
            this.selectMode(sameKind);
            return;
        }

        // Nothing of this kind is sold — fall back to the other kind, through
        // setModelKind so the composer's Text→Video chip follows along.
        const other = this.modelKind === 'video' ? 'image' : 'video';
        if (this.firstKeyOfKind(other)) { this.setModelKind(other); }
    }

    /**
     * Flip the panel between image and video generation. Video models submit
     * through the async text→video job, so the composer's Text→Video mode
     * follows this toggle rather than being a separate thing to remember.
     */
    setModelKind(kind: 'image' | 'video'): void {
        if (this.modelKind === kind) { return; }

        const next = this.modelByKind[kind] || this.firstKeyOfKind(kind);
        if (!next) {
            this.toastr.info(`No ${kind} models are available right now.`);
            return;
        }

        this.modelKind = kind;
        this.videoFromTextMode = kind === 'video';
        if (this.videoFromTextMode) { this.converseMode = false; }
        this.selectMode(next);
    }

    /*
     * Apply a model selection and (re)seed the control values from that
     * model's control defaults, so we only ever send values it accepts.
     */
    selectMode(mode: string): void {
        this.selectedMode = mode;

        // Keep the toggle, the per-kind memory and the video job model in step
        // with whatever model is now active.
        const kind = this.kindOf(this.allModels.find(m => m.key === mode));
        this.modelKind = kind;
        if (mode) { this.modelByKind[kind] = mode; }
        if (kind === 'video' && mode) { this.videoTextModel = mode; }

        const values: { [key: string]: any } = {};
        for (const c of this.activeControls) {
            values[c.key] = c.default !== undefined ? c.default : (c.type === 'number' ? (c.min ?? 1) : null);
        }
        this.controlValues = values;
    }

    onModelChange(): void {
        this.selectMode(this.selectedMode);
    }

    get activeModel(): ModelInfo | undefined {
        return this.allModels.find(m => m.key === this.selectedMode);
    }

    get activeControls(): ModelControl[] {
        return this.activeModel?.controls || [];
    }

    get selectedModelLabel(): string {
        const m = this.activeModel;
        return m ? m.model : this.selectedMode;
    }

    // Group a select control's options by their `group` field (for <optgroup>).
    optionGroups(control: ModelControl): ControlGroup[] {
        const groups: ControlGroup[] = [];
        for (const opt of control.options || []) {
            const label = opt.group || '';
            let g = groups.find(x => x.label === label);
            if (!g) { g = { label, options: [] }; groups.push(g); }
            g.options.push({ value: opt.value, label: opt.label });
        }
        return groups;
    }

    private fallbackModels(): ModelInfo[] {
        const aspect: ModelControl = {
            key: 'aspect_ratio', type: 'select', label: 'Aspect ratio', default: 'square',
            options: [
                { value: 'square', label: 'Square · 1:1', group: 'Standard' },
                { value: 'landscape', label: 'Landscape · 16:9', group: 'Standard' },
                { value: 'portrait', label: 'Portrait · 9:16', group: 'Standard' },
                { value: 'classic_4_3', label: 'Classic · 4:3', group: 'Standard' },
                { value: 'instagram_story', label: 'Instagram Story · 9:16', group: 'Social media' },
                { value: 'youtube_thumbnail', label: 'YouTube Thumbnail · 16:9', group: 'Social media' }
            ]
        };
        const style: ModelControl = {
            key: 'style_preset', type: 'select', label: 'Style preset', default: 'none',
            options: ['none', 'photographic', 'cinematic', 'digital_art', 'anime', '3d_render', 'watercolor', 'minimal']
                .map(v => ({ value: v, label: v.replace('_', ' ') }))
        };
        const num: ModelControl = { key: 'num_images', type: 'number', label: 'Number of images', default: 1, min: 1, max: 4 };
        const neg: ModelControl = { key: 'negative_prompt', type: 'textarea', label: 'Negative prompt', default: '', placeholder: 'things to avoid (optional)', help: 'Advisory — applied as an “avoid” hint in the prompt.' };
        return [
            { key: 'chatimage', model: 'gpt-image-1', kind: 'image', label: 'Chat Image 1', subtitle: 'OpenAI GPT Image 1 — strong text & prompt fidelity', controls: [aspect, num, style, neg] },
            { key: 'fast', model: 'flux-schnell', kind: 'image', label: 'Fast', subtitle: 'FLUX Schnell — seconds, great for drafts', controls: [aspect, num, style, neg] },
            { key: 'quality', model: 'flux-1.1-pro-ultra', kind: 'image', label: 'Quality', subtitle: 'FLUX 1.1 Pro Ultra — best fidelity', controls: [aspect, style, neg] },
            { key: 'imagen', model: 'imagen-4', kind: 'image', label: 'Imagen 4', subtitle: 'Google Imagen 4 — photoreal', controls: [aspect, style, neg] }
        ];
    }

    loadCredits(sessionId: string): void {
        if (!sessionId) { return; }
        this.chatService.getCredits(sessionId).subscribe(
            res => { this.applyCredits(res); },
            () => { this.resetCredits(); }
        );
    }

    /*
     * Normalise the studio credits payload. The balance fields may arrive
     * nested under `credits` or flat on the root object; `tools` (usage by
     * tool / LLM) may likewise sit on either level.
     */
    private applyCredits(res: CreditsResponse | null): void {
        if (!res) { this.resetCredits(); return; }
        const c: any = res.credits ?? res;
        this.credits = c && (c.total != null || c.remaining != null)
            ? {
                plan: c.plan,
                total: c.total,
                deducted: c.deducted,
                remaining: c.remaining,
                reserved: c.reserved,
                remaining_inr: c.remaining_inr,
                credit_value_inr: c.credit_value_inr
            }
            : null;
        this.creditsTools = res.credits?.tools ?? res.tools ?? [];
        this.creditsCustomerName = res.customer_name ?? null;
        this.creditsCustomerId = res.customer_id ?? null;
    }

    private resetCredits(): void {
        this.credits = null;
        this.creditsTools = [];
        this.creditsCustomerName = null;
        this.creditsCustomerId = null;
    }

    /**
     * Plan-side balance, for the badge when the session-keyed one has nothing.
     *
     * Null until the call answers and null again if it fails, so a failed request
     * shows no badge rather than a zero — "0 credits" is a real state and must not
     * be counterfeited by a network error.
     */
    planCredits: { remaining: number; total: number } | null = null;

    /**
     * Whether the badge is falling back to the plan figure.
     *
     * `credits` is the studio wallet for THIS chat session and does not exist
     * until one is opened, which is why the badge vanished on a fresh studio. The
     * session figure still wins wherever it exists: it is the one the tools spend
     * against and it moves as they run.
     */
    get showPlanCredits(): boolean {
        return !this.credits && !!this.planCredits;
    }

    private loadPlanCredits(): void {
        if (!this.isSignedIn) { this.planCredits = null; return; }

        this.chatService.getPlanCredits().subscribe(
            (res: any) => {
                const remaining = res && res.data ? Number(res.data.remaining) : NaN;
                this.planCredits = isFinite(remaining)
                    ? { remaining, total: Number(res.data.total) || 0 }
                    : null;
            },
            () => { this.planCredits = null; }
        );
    }

    /* ---- Discovery: edit tools + video models ---- */

    loadEditToolsCatalogue(): void {
        this.chatService.getEditTools().subscribe(
            res => { this.editToolsCatalogue = (res as any)?.tools || []; },
            () => { this.editToolsCatalogue = []; }
        );
    }

    loadVideoModelsCatalogue(): void {
        this.chatService.getVideoModels().subscribe(
            res => { this.videoModelsCatalogue = res?.models || []; },
            () => { this.videoModelsCatalogue = []; }
        );
    }

    /* ---- Plan entitlements (collections + the tools the plan sells) ---- */

    /**
     * Tool lines the customer's plan sells, keyed by service_code — the rate and
     * monthly cap behind each model. Empty when the plan has no credit pack.
     */
    permissionTools: PermissionTool[] = [];

    /** Collection ids the seat may download from, as returned by the API. */
    permissionCollections = '';

    loadUserPermission(): void {
        const customerId = this.currentUser?.customers_id;
        if (!customerId) { return; }

        this.chatService.getUserPermission(customerId).subscribe(
            (res: UserPermissionResponse) => {
                this.permissionTools = res?.tools || [];
                this.permissionCollections = res?.data?.[0]?.collection || '';
                this.applyModelPermissions();
            },
            () => {
                this.permissionTools = [];
                this.permissionCollections = '';
            }
        );
    }

    /** The plan's line for a model key (`gpt_image_2` → `image.gpt_image_2`). */
    permissionToolFor(modelKey: string): PermissionTool | undefined {
        if (!modelKey) { return undefined; }
        const key = modelKey.toLowerCase();
        return this.permissionTools.find(t => {
            const code = (t.service_code || '').toLowerCase();
            return code === key || code.slice(code.lastIndexOf('.') + 1) === key;
        });
    }

    /** What the selected model costs per run, or null when the plan omits it. */
    get selectedModelCredits(): number | null {
        const tool = this.permissionToolFor(this.selectedMode);
        return tool ? tool.sell_credits : null;
    }

    /* ---- Customer directory ---- */

    // loadCustomers(): void {
    //     this.chatService.getCustomers().subscribe(
    //         res => { this.customers = res?.customers || []; },
    //         () => { this.customers = []; }
    //     );
    // }

    /* ---- Recent async jobs panel ---- */

    openJobsModal(): void {
        this.showJobsPanel = true;
        this.loadJobs();
    }

    closeJobsModal(): void {
        this.showJobsPanel = false;
    }

    loadJobs(): void {
        this.isLoadingJobs = true;
        this.chatService.listJobs(20).subscribe(
            res => {
                this.jobs = res?.jobs || [];
                this.isLoadingJobs = false;
            },
            () => {
                this.jobs = [];
                this.isLoadingJobs = false;
                this.toastr.error('Failed to load jobs');
            }
        );
    }

    /* ---- Text → video (async job) ---- */

    toggleVideoFromText(): void {
        // Mirrors the Generation Settings image/video toggle, so the composer chip
        // and the panel can't disagree about what send() will produce.
        const next = this.videoFromTextMode ? 'image' : 'video';
        if (this.hasVideoModels || next === 'image') {
            this.setModelKind(next);
            return;
        }

        // No video catalogue (discovery fell back) — keep the chip usable on its own.
        this.videoFromTextMode = !this.videoFromTextMode;
        if (this.videoFromTextMode) { this.converseMode = false; }
    }

    toggleConverse(): void {
        this.converseMode = !this.converseMode;
        if (this.converseMode) { this.videoFromTextMode = false; }
    }

    generateVideoFromText(): void {
        const text = (this.pendingMessage || '').trim();
        if (!text || this.isSending) { return; }

        this.messages.push({
            role: 'user',
            content: text,
            images: [],
            created_at: new Date().toISOString()
        });

        const pending: UIMessage = {
            role: 'assistant',
            content: '',
            images: [],
            created_at: new Date().toISOString(),
            pending: true,
            prompt: text,
            job: {
                status: 'queued',
                model: this.activeModel?.model || this.videoTextModel,
                operation: 'text_to_video',
                startedAt: Date.now(),
                elapsed: 0
            }
        };
        this.messages.push(pending);
        this.shouldScroll = true;
        this.stickToBottom = true;

        this.pendingMessage = '';
        this.beginTurn(pending);

        // Duration / resolution / aspect ratio / camera_fixed / seed come from the
        // selected video model's own controls in Generation Settings; the literals
        // are the fallback for when no video model is loaded there.
        const req: any = {
            prompt: text,
            model: this.videoTextModel,
            duration: 5,
            resolution: '1080p',
            aspect_ratio: '16:9',
            session_id: this.activeSessionId || null,
            customer_id: this.currentUser?.customers_id
        };
        if (this.modelKind === 'video') {
            for (const c of this.activeControls) {
                const v = this.controlValues[c.key];
                if (v !== null && v !== undefined && v !== '') { req[c.key] = v; }
            }
        }

        this.activeRequest = this.chatService.generateVideoText(req).subscribe(
            job => {
                this.isSending = false;
                this.activeRequest = null;
                if (job?.job_id) {
                    // The turn is still live — it just moves from a request to a
                    // poller, which is what stop has to reach for from here.
                    this.activeJobId = job.job_id;
                    this.pollJob(job.job_id, pending);
                    if (job.session_id) { this.activeSessionId = job.session_id; }
                } else {
                    this.removePending(pending);
                    this.endTurn();
                    this.toastr.error('Could not start video job');
                }
            },
            err => {
                this.removePending(pending);
                this.endTurn();
                this.toastr.error(err?.error?.message || err?.error?.detail || 'Video generation failed');
            }
        );
    }

    /* ---- Orchestrator turn (converse) ----
     * Alternative to send() that lets the studio orchestrator decide whether
     * to chat, create or refine. Supports the confirm handshake for
     * video / model-switch turns (resend with confirm + the returned pending).
     */
    converseSend(confirm = false, pending: any = null): void {
        const text = (this.pendingMessage || '').trim();
        if (!text || this.isSending) { return; }

        this.messages.push({
            role: 'user',
            content: text,
            images: [],
            created_at: new Date().toISOString()
        });

        const pendingMsg: UIMessage = {
            role: 'assistant',
            content: '',
            images: [],
            created_at: new Date().toISOString(),
            pending: true
        };
        this.messages.push(pendingMsg);
        this.shouldScroll = true;
        this.stickToBottom = true;

        this.pendingMessage = '';
        this.beginTurn(pendingMsg);

        this.activeRequest = this.chatService.converse({
            message: text,
            session_id: this.activeSessionId || null,
            model: this.selectedMode || undefined,
            controls: this.controlValues,
            confirm,
            pending,
            customer_id: this.currentUser?.customers_id
        }).subscribe(
            (res: ConverseResponse) => {
                this.isSending = false;
                this.activeRequest = null;
                if (res?.session_id) { this.activeSessionId = res.session_id; }

                // Video/model-switch handshake: re-send with confirm:true.
                if (res?.action === 'confirm' && res?.pending) {
                    this.replacePending(pendingMsg, res?.ai_message || 'Confirm to continue…', []);
                    return;
                }

                if (res?.job?.job_id) {
                    // Restart as a polled video job.
                    pendingMsg.pending = true;
                    pendingMsg.job = { status: 'queued', operation: 'text_to_video', startedAt: Date.now(), elapsed: 0 };
                    this.activeJobId = res.job.job_id;
                    this.pollJob(res.job.job_id, pendingMsg);
                } else {
                    this.replacePending(
                        pendingMsg,
                        res?.ai_message || '',
                        (res?.new_images || []).map(i => ({ id: i.id, url: i.url }))
                    );
                }
                this.loadSessions();
                if (this.activeSessionId) { this.loadCredits(this.activeSessionId); }
            },
            err => {
                this.removePending(pendingMsg);
                this.endTurn();
                this.toastr.error(err?.error?.message || err?.error?.detail || 'Request failed');
            }
        );
    }

    /* ---- Studio download (streams the asset + records the download) ---- */

    downloadAssetFile(img: { id?: string; url: string }, assetType: 'image' | 'video' = 'image'): void {
        if (!img?.url) {
            this.toastr.info('This asset has no downloadable URL.');
            return;
        }
        // Gate behind the same licence modal as openDownloadModal(); the actual
        // transfer runs from runStudioDownload() once the terms are accepted.
        this.downloadModalImage = { id: img.id || '', url: img.url };
        this.downloadAccepted = false;
        this.pendingDownload = { kind: 'studio', assetType };
    }

    /* Streams the asset straight from its URL — no backend/API response involved. */
    private runStudioDownload(): void {
        const asset = this.downloadModalImage;
        if (!asset) { return; }
        const filename = this.deriveFilename(asset.url, asset.id);
        this.isDownloading = true;

        fetch(asset.url, { mode: 'cors' })
            .then(res => {
                if (!res.ok) { throw new Error('Network response was not ok'); }
                return res.blob();
            })
            .then(blob => {
                const blobUrl = URL.createObjectURL(blob);
                this.triggerBrowserDownload(blobUrl, filename);
                setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                this.finishDownload();
            })
            .catch(() => {
                // CORS-blocked fetch: fall back to a direct link to the asset URL.
                this.triggerBrowserDownload(asset.url, filename, true);
                this.finishDownload();
            });
    }

    private triggerBrowserDownload(href: string, filename: string, newTab = false): void {
        const a = document.createElement('a');
        a.href = href;
        a.download = filename;
        if (newTab) {
            a.target = '_blank';
            a.rel = 'noopener';
        }
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    private finishDownload(): void {
        this.isDownloading = false;
        this.downloadModalImage = null;
        this.downloadAccepted = false;
    }

    /* ---- Credits · usage by tool modal ---- */

    openCreditsModal(): void {
        this.showCreditsModal = true;
        // Refresh so the modal reflects the latest balance / tool usage.
        if (this.activeSessionId) { this.loadCredits(this.activeSessionId); }
    }

    closeCreditsModal(): void {
        this.showCreditsModal = false;
    }

    /** The one attached reference image, or null when nothing is attached. */
    get attachedImage(): { id: string; url: string } | null {
        return this.attachedImages[0] || null;
    }

    /** Auto-picked edit image the user dismissed, by id. A newer image re-arms it. */
    private dismissedEditImageId: string | null = null;

    /*
     * The image the composer's EDIT IMAGE toolbar acts on: the attached
     * reference if there is one, otherwise the most recent generated image.
     */
    get activeEditImage(): { id: string; url: string } | null {
        if (this.attachedImage) {
            return this.attachedImage;
        }
        for (let i = this.messages.length - 1; i >= 0; i--) {
            const m = this.messages[i];
            if (m.role === 'assistant' && m.images?.length) {
                const candidate = m.images[m.images.length - 1];
                // Dismissed: stay empty rather than quietly falling back to an
                // older image the user didn't pick either.
                return candidate.id === this.dismissedEditImageId ? null : candidate;
            }
        }
        return null;
    }

    /**
     * Clear the strip's image, whichever kind it is: detaches an attached
     * reference, or drops the auto-picked one. Generating or attaching another
     * image brings the strip back.
     */
    clearEditImage(): void {
        const img = this.activeEditImage;
        if (!img) { return; }

        if (this.attachedImage?.id === img.id) {
            this.removeAttachment(img.id);
            return;
        }
        this.dismissedEditImageId = img.id;
    }

    /**
     * Caption for the composer's edit strip: names the attached reference when
     * there is one, otherwise the image the tools would fall back to.
     */
    get editToolbarLabel(): string {
        const n = this.attachedImages.length;
        if (!n) { return 'EDIT IMAGE'; }
        const used = this.referenceBytesUsed;
        const size = used ? ' · ' + this.formatBytes(used) + ' of ' + this.formatBytes(this.MAX_REFERENCE_BYTES) : '';
        return (n > 1 ? 'REFERENCES · ' + n + '/' + this.MAX_REFERENCE_IMAGES : 'REFERENCE') + size;
    }

    editFromComposer(tool: EditTool): void {
        const img = this.activeEditImage;
        if (!img) {
            this.toastr.info('Attach or generate an image first, then pick an edit tool.');
            return;
        }
        this.applyEditTool(img, tool);
    }

    animateFromComposer(): void {
        const img = this.activeEditImage;
        if (!img) {
            this.toastr.info('Attach or generate an image first.');
            return;
        }
        this.animateImage(img);
    }

    talkFromComposer(): void {
        const img = this.activeEditImage;
        if (!img) {
            this.toastr.info('Attach or generate a portrait first.');
            return;
        }
        this.startTalkingHead(img);
    }

    /**
     * The Generate tab's send.
     *
     * Runs over the streaming sendMessage orchestrator rather than the one-shot
     * generateImage endpoint, so this path gets what the chat path already had:
     * live step labels while the tools run, the model-switch confirm handshake,
     * and async video jobs. streamTurn() owns all of that, so the work here is
     * only shaping the request.
     *
     * The controls the model exposes are nested under `controls` (where the
     * orchestrator reads them and fills its own defaults for anything omitted)
     * instead of being flattened alongside the prompt, which is what
     * generateImage took.
     */
    generateWithSettings(): void {
        const text = (this.pendingMessage || '').trim();
        if (!text || this.isSending) { return; }

        const userMsg: UIMessage = {
            role: 'user',
            content: text,
            images: [],
            created_at: new Date().toISOString()
        };
        this.messages.push(userMsg);

        const req: ChatRequest = { message: text };
        if (this.activeSessionId) { req.session_id = this.activeSessionId; }
        if (this.selectedMode) { req.model = this.selectedMode; }
        req.customer_id = this.currentUser?.customers_id;

        // Only the selected model's own controls, dropping empty/null values
        // (e.g. an unset seed or a blank negative prompt) so they don't override
        // the orchestrator's defaults with nothing.
        const controls: { [key: string]: any } = {};
        for (const c of this.activeControls) {
            const v = this.controlValues[c.key];
            if (v !== null && v !== undefined && v !== '') {
                controls[c.key] = v;
            }
        }
        if (Object.keys(controls).length) { req.controls = controls; }

        this.pendingMessage = '';

        this.streamTurn(req, userMsg);
    }

    private replacePending(pending: UIMessage, content: string, images: { id: string; url: string }[], videos?: { url: string }[]): void {
        const idx = this.messages.indexOf(pending);
        if (idx > -1) { this.messages.splice(idx, 1); }
        this.messages.push({
            role: 'assistant',
            content,
            images,
            videos,
            // Carried over so a freshly rendered video shows its prompt, exactly
            // as it will after a history reload.
            prompt: pending.prompt,
            created_at: new Date().toISOString()
        });
        this.shouldScroll = true;
        this.stickToBottom = true;
    }

    private removePending(pending: UIMessage): void {
        const idx = this.messages.indexOf(pending);
        if (idx > -1) { this.messages.splice(idx, 1); }
    }

    /* ---- Edit tools ---- */

    applyEditTool(img: { id: string; url: string }, tool: EditTool, extra?: { [k: string]: any }): void {
        if (this.editingImageId) { return; }

        // Crop opens a region-selection modal first; it re-enters here with `extra`.
        if (tool === 'crop' && !extra) {
            this.openCropModal(img);
            return;
        }

        // Erase opens a brush-paint modal first; it re-enters here with `extra` (mask).
        if (tool === 'erase' && !extra) {
            this.openEraseModal(img);
            return;
        }

        // Replace BG opens a prompt modal first; it re-enters here with `extra` (prompt).
        if (tool === 'replace_bg' && !extra) {
            this.openReplaceBgModal(img);
            return;
        }

        // Tools that still read their prompt from the composer.
        const promptTools: EditTool[] = ['fill'];
        let prompt: string | undefined;
        if (promptTools.includes(tool)) {
            prompt = (this.pendingMessage || '').trim();
            if (!prompt) {
                this.toastr.info('Type what you want in the box, then click the tool.');
                return;
            }
        }

        this.editingImageId = img.id;
        const pending: UIMessage = {
            role: 'assistant',
            content: '',
            images: [],
            created_at: new Date().toISOString(),
            pending: true
        };
        this.messages.push(pending);
        this.shouldScroll = true;

        /*
         * Registered as the live turn, exactly as send() does. Without this the
         * composer never swapped to Stop for a tool run, and there was no held
         * subscription for stopGenerating() to drop even if it had.
         */
        this.beginTurn(pending);

        this.activeRequest = this.chatService.editImage({
            tool,
            session_id: this.activeSessionId || null,
            image_id: tool === 'remove_bg' ? undefined : img.id,
            image_ids: tool === 'remove_bg' ? [img.id] : undefined,
            prompt,
            user_id: this.studioUserId,
            customer_id: this.currentUser?.customers_id,
            ...(extra || {})
        }).subscribe(
            res => {
                this.replacePending(pending, res?.ai_message || '', (res?.new_images || []).map(i => ({ id: i.id, url: i.url })));
                if (res?.session_id) { this.activeSessionId = res.session_id; }
                this.editingImageId = null;
                this.endTurn();
                if (promptTools.includes(tool)) { this.pendingMessage = ''; }
                this.loadSessions();
                if (this.activeSessionId) { this.loadCredits(this.activeSessionId); }
            },
            err => {
                this.removePending(pending);
                this.editingImageId = null;
                this.endTurn();
                this.toastr.error(err?.error?.message || err?.error?.detail || 'Edit failed');
            }
        );
    }

    /* ---- Face restore (dedicated endpoint) ---- */

    faceRestore(img: { id: string; url: string }, scale = 2): void {
        if (this.editingImageId) { return; }
        this.editingImageId = img.id;

        const pending: UIMessage = {
            role: 'assistant',
            content: '',
            images: [],
            created_at: new Date().toISOString(),
            pending: true
        };
        this.messages.push(pending);
        this.shouldScroll = true;

        const req: FaceRestoreRequest = {
            session_id: this.activeSessionId || null,
            user_id: this.studioUserId,
            image_id: img.id,
            scale,
            customer_id: this.currentUser?.customers_id
        };

        /*
         * Registered as the live turn, exactly as send() does. Without this the
         * composer never swapped to Stop for a tool run, and there was no held
         * subscription for stopGenerating() to drop even if it had.
         */
        this.beginTurn(pending);

        this.activeRequest = this.chatService.faceRestore(req).subscribe(
            res => {
                this.replacePending(pending, res?.ai_message || '', (res?.new_images || []).map(i => ({ id: i.id, url: i.url })));
                if (res?.session_id) { this.activeSessionId = res.session_id; }
                this.editingImageId = null;
                this.endTurn();
                this.loadSessions();
                if (this.activeSessionId) { this.loadCredits(this.activeSessionId); }
            },
            err => {
                this.removePending(pending);
                this.editingImageId = null;
                this.endTurn();
                this.toastr.error(err?.error?.message || err?.error?.detail || 'Face restore failed');
            }
        );
    }

    faceRestoreFromComposer(): void {
        const img = this.activeEditImage;
        if (!img) {
            this.toastr.info('Attach or generate an image first.');
            return;
        }
        this.faceRestore(img);
    }

    /* ---- Restore old/blurry photo (dedicated endpoint) ---- */

    restoreImage(img: { id: string; url: string }): void {
        if (this.editingImageId) { return; }
        this.editingImageId = img.id;

        const pending: UIMessage = {
            role: 'assistant',
            content: '',
            images: [],
            created_at: new Date().toISOString(),
            pending: true
        };
        this.messages.push(pending);
        this.shouldScroll = true;

        const req: RestoreImageRequest = {
            session_id: this.activeSessionId || null,
            user_id: this.studioUserId,
            image_id: img.id,
            customer_id: this.currentUser?.customers_id
        };

        /*
         * Registered as the live turn, exactly as send() does. Without this the
         * composer never swapped to Stop for a tool run, and there was no held
         * subscription for stopGenerating() to drop even if it had.
         */
        this.beginTurn(pending);

        this.activeRequest = this.chatService.restoreImage(req).subscribe(
            res => {
                this.replacePending(pending, res?.ai_message || '', (res?.new_images || []).map(i => ({ id: i.id, url: i.url })));
                if (res?.session_id) { this.activeSessionId = res.session_id; }
                this.editingImageId = null;
                this.endTurn();
                this.loadSessions();
                if (this.activeSessionId) { this.loadCredits(this.activeSessionId); }
            },
            err => {
                this.removePending(pending);
                this.editingImageId = null;
                this.endTurn();
                this.toastr.error(err?.error?.message || err?.error?.detail || 'Photo restore failed');
            }
        );
    }

    restoreFromComposer(): void {
        const img = this.activeEditImage;
        if (!img) {
            this.toastr.info('Attach or generate an image first.');
            return;
        }
        this.restoreImage(img);
    }

    /* ---- Crop (region-select modal) ---- */

    openCropModal(img: { id: string; url: string }): void {
        this.cropModalImage = { id: img.id, url: img.url };
        this.cropRect = null;
        this.cropStageRect = null;
    }

    closeCropModal(): void {
        if (this.editingImageId === this.cropModalImage?.id) { return; }
        this.cropModalImage = null;
        this.cropRect = null;
        this.cropDragging = false;
    }

    onCropMouseDown(event: MouseEvent): void {
        const stage = event.currentTarget as HTMLElement;
        const r = stage.getBoundingClientRect();
        this.cropStageRect = { left: r.left, top: r.top, width: r.width, height: r.height };
        this.cropDragging = true;
        this.cropStartX = this.clamp(event.clientX - r.left, 0, r.width);
        this.cropStartY = this.clamp(event.clientY - r.top, 0, r.height);
        this.cropRect = { x: this.cropStartX, y: this.cropStartY, w: 0, h: 0 };
        event.preventDefault();
    }

    onCropMouseMove(event: MouseEvent): void {
        if (!this.cropDragging || !this.cropStageRect) { return; }
        const cx = this.clamp(event.clientX - this.cropStageRect.left, 0, this.cropStageRect.width);
        const cy = this.clamp(event.clientY - this.cropStageRect.top, 0, this.cropStageRect.height);
        this.cropRect = {
            x: Math.min(this.cropStartX, cx),
            y: Math.min(this.cropStartY, cy),
            w: Math.abs(cx - this.cropStartX),
            h: Math.abs(cy - this.cropStartY)
        };
    }

    onCropMouseUp(): void {
        this.cropDragging = false;
    }

    confirmCrop(): void {
        if (!this.cropModalImage || !this.cropRect || !this.cropStageRect) {
            this.toastr.info('Drag on the image to select a crop area.');
            return;
        }
        const { width, height } = this.cropStageRect;
        if (this.cropRect.w < 5 || this.cropRect.h < 5 || !width || !height) {
            this.toastr.info('Drag on the image to select a crop area.');
            return;
        }
        const norm = {
            x: +(this.cropRect.x / width).toFixed(6),
            y: +(this.cropRect.y / height).toFixed(6),
            w: +(this.cropRect.w / width).toFixed(6),
            h: +(this.cropRect.h / height).toFixed(6)
        };
        const img = this.cropModalImage;
        this.cropModalImage = null;
        this.cropRect = null;
        this.applyEditTool(img, 'crop', norm);
    }

    private clamp(v: number, min: number, max: number): number {
        return Math.min(Math.max(v, min), max);
    }

    /* ---- Erase (brush-paint mask modal) ---- */

    openEraseModal(img: { id: string; url: string }): void {
        this.eraseModalImage = { id: img.id, url: img.url };
        this.hasEraseStrokes = false;
        this.eraseDrawing = false;
        this.displayCtx = null;
        this.maskCtx = null;
        this.maskCanvas = null;
    }

    closeEraseModal(): void {
        if (this.editingImageId === this.eraseModalImage?.id) { return; }
        this.eraseModalImage = null;
        this.hasEraseStrokes = false;
        this.eraseDrawing = false;
    }

    // Called once the source image has rendered so we know its display + natural size.
    onEraseImageLoad(): void {
        const img = this.eraseImg?.nativeElement;
        const canvas = this.eraseCanvas?.nativeElement;
        if (!img || !canvas) { return; }

        // Visible overlay canvas matches the displayed image size.
        canvas.width = img.clientWidth;
        canvas.height = img.clientHeight;
        this.displayCtx = canvas.getContext('2d');
        this.displayCtx?.clearRect(0, 0, canvas.width, canvas.height);

        // Off-screen mask canvas at the image's natural resolution (white = remove).
        this.maskCanvas = document.createElement('canvas');
        this.maskCanvas.width = img.naturalWidth || img.clientWidth;
        this.maskCanvas.height = img.naturalHeight || img.clientHeight;
        this.maskCtx = this.maskCanvas.getContext('2d');
        if (this.maskCtx) {
            this.maskCtx.fillStyle = '#000';
            this.maskCtx.fillRect(0, 0, this.maskCanvas.width, this.maskCanvas.height);
        }
        this.hasEraseStrokes = false;
    }

    onEraseMouseDown(event: MouseEvent): void {
        this.eraseDrawing = true;
        this.paintErase(event);
    }

    onEraseMouseMove(event: MouseEvent): void {
        if (!this.eraseDrawing) { return; }
        this.paintErase(event);
    }

    onEraseMouseUp(): void {
        this.eraseDrawing = false;
    }

    private paintErase(event: MouseEvent): void {
        const canvas = this.eraseCanvas?.nativeElement;
        if (!canvas || !this.displayCtx || !this.maskCtx || !this.maskCanvas) { return; }
        const rect = canvas.getBoundingClientRect();
        const dx = event.clientX - rect.left;
        const dy = event.clientY - rect.top;
        const r = this.eraseBrush / 2;

        // Visible highlight (semi-transparent red) so the user sees the mask.
        this.displayCtx.fillStyle = 'rgba(239, 68, 68, 0.5)';
        this.displayCtx.beginPath();
        this.displayCtx.arc(dx, dy, r, 0, Math.PI * 2);
        this.displayCtx.fill();

        // Mask: pure white on black, scaled to natural resolution.
        const sx = this.maskCanvas.width / canvas.width;
        const sy = this.maskCanvas.height / canvas.height;
        this.maskCtx.fillStyle = '#fff';
        this.maskCtx.beginPath();
        this.maskCtx.arc(dx * sx, dy * sy, r * sx, 0, Math.PI * 2);
        this.maskCtx.fill();

        this.hasEraseStrokes = true;
    }

    clearErase(): void {
        const canvas = this.eraseCanvas?.nativeElement;
        if (canvas && this.displayCtx) {
            this.displayCtx.clearRect(0, 0, canvas.width, canvas.height);
        }
        if (this.maskCanvas && this.maskCtx) {
            this.maskCtx.fillStyle = '#000';
            this.maskCtx.fillRect(0, 0, this.maskCanvas.width, this.maskCanvas.height);
        }
        this.hasEraseStrokes = false;
    }

    confirmErase(): void {
        if (!this.eraseModalImage || !this.hasEraseStrokes || !this.maskCanvas) {
            this.toastr.info('Paint over the object you want to remove first.');
            return;
        }
        const mask = this.maskCanvas.toDataURL('image/png');
        const img = this.eraseModalImage;
        this.eraseModalImage = null;
        this.hasEraseStrokes = false;
        this.submitErase(img, mask);
    }

    private submitErase(img: { id: string; url: string }, mask: string): void {
        if (this.editingImageId) { return; }
        this.editingImageId = img.id;

        const pending: UIMessage = {
            role: 'assistant',
            content: '',
            images: [],
            created_at: new Date().toISOString(),
            pending: true
        };
        this.messages.push(pending);
        this.shouldScroll = true;

        const req: EraseImageRequest = {
            session_id: this.activeSessionId || null,
            user_id: this.studioUserId,
            image_id: img.id,
            mask,
            customer_id: this.currentUser?.customers_id
        };

        /*
         * Registered as the live turn, exactly as send() does. Without this the
         * composer never swapped to Stop for a tool run, and there was no held
         * subscription for stopGenerating() to drop even if it had.
         */
        this.beginTurn(pending);

        this.activeRequest = this.chatService.eraseImage(req).subscribe(
            res => {
                this.replacePending(pending, res?.ai_message || '', (res?.new_images || []).map(i => ({ id: i.id, url: i.url })));
                if (res?.session_id) { this.activeSessionId = res.session_id; }
                this.editingImageId = null;
                this.endTurn();
                this.loadSessions();
                if (this.activeSessionId) { this.loadCredits(this.activeSessionId); }
            },
            err => {
                this.removePending(pending);
                this.editingImageId = null;
                this.endTurn();
                this.toastr.error(err?.error?.message || err?.error?.detail || 'Erase failed');
            }
        );
    }

    /* ---- Generative fill (brush-paint mask + prompt modal) ---- */

    openFillModal(img: { id: string; url: string }): void {
        this.fillModalImage = { id: img.id, url: img.url };
        this.fillPrompt = '';
        this.fillDims = '';
        this.hasFillStrokes = false;
        this.fillDrawing = false;
        this.fillDisplayCtx = null;
        this.fillMaskCtx = null;
        this.fillMaskCanvas = null;
    }

    fillFromComposer(): void {
        const img = this.activeEditImage;
        if (!img) {
            this.toastr.info('Attach or generate an image first.');
            return;
        }
        this.openFillModal(img);
    }

    closeFillModal(): void {
        if (this.editingImageId === this.fillModalImage?.id) { return; }
        this.fillModalImage = null;
        this.fillPrompt = '';
        this.hasFillStrokes = false;
        this.fillDrawing = false;
    }

    // Called once the source image has rendered so we know its display + natural size.
    onFillImageLoad(): void {
        const img = this.fillImg?.nativeElement;
        const canvas = this.fillCanvas?.nativeElement;
        if (!img || !canvas) { return; }

        // Visible overlay canvas matches the displayed image size.
        canvas.width = img.clientWidth;
        canvas.height = img.clientHeight;
        this.fillDisplayCtx = canvas.getContext('2d');
        this.fillDisplayCtx?.clearRect(0, 0, canvas.width, canvas.height);

        // Off-screen mask canvas at the image's natural resolution (white = change).
        this.fillMaskCanvas = document.createElement('canvas');
        this.fillMaskCanvas.width = img.naturalWidth || img.clientWidth;
        this.fillMaskCanvas.height = img.naturalHeight || img.clientHeight;
        this.fillMaskCtx = this.fillMaskCanvas.getContext('2d');
        if (this.fillMaskCtx) {
            this.fillMaskCtx.fillStyle = '#000';
            this.fillMaskCtx.fillRect(0, 0, this.fillMaskCanvas.width, this.fillMaskCanvas.height);
        }
        this.fillDims = `${this.fillMaskCanvas.width}×${this.fillMaskCanvas.height}`;
        this.hasFillStrokes = false;
    }

    onFillMouseDown(event: MouseEvent): void {
        this.fillDrawing = true;
        this.paintFill(event);
    }

    onFillMouseMove(event: MouseEvent): void {
        if (!this.fillDrawing) { return; }
        this.paintFill(event);
    }

    onFillMouseUp(): void {
        this.fillDrawing = false;
    }

    private paintFill(event: MouseEvent): void {
        const canvas = this.fillCanvas?.nativeElement;
        if (!canvas || !this.fillDisplayCtx || !this.fillMaskCtx || !this.fillMaskCanvas) { return; }
        const rect = canvas.getBoundingClientRect();
        const dx = event.clientX - rect.left;
        const dy = event.clientY - rect.top;
        const r = this.fillBrush / 2;

        // Visible highlight (semi-transparent) so the user sees the mask.
        this.fillDisplayCtx.fillStyle = 'rgba(129, 140, 248, 0.5)';
        this.fillDisplayCtx.beginPath();
        this.fillDisplayCtx.arc(dx, dy, r, 0, Math.PI * 2);
        this.fillDisplayCtx.fill();

        // Mask: pure white on black, scaled to natural resolution.
        const sx = this.fillMaskCanvas.width / canvas.width;
        const sy = this.fillMaskCanvas.height / canvas.height;
        this.fillMaskCtx.fillStyle = '#fff';
        this.fillMaskCtx.beginPath();
        this.fillMaskCtx.arc(dx * sx, dy * sy, r * sx, 0, Math.PI * 2);
        this.fillMaskCtx.fill();

        this.hasFillStrokes = true;
    }

    clearFill(): void {
        const canvas = this.fillCanvas?.nativeElement;
        if (canvas && this.fillDisplayCtx) {
            this.fillDisplayCtx.clearRect(0, 0, canvas.width, canvas.height);
        }
        if (this.fillMaskCanvas && this.fillMaskCtx) {
            this.fillMaskCtx.fillStyle = '#000';
            this.fillMaskCtx.fillRect(0, 0, this.fillMaskCanvas.width, this.fillMaskCanvas.height);
        }
        this.hasFillStrokes = false;
    }

    confirmFill(): void {
        if (!this.fillModalImage || !this.hasFillStrokes || !this.fillMaskCanvas) {
            this.toastr.info('Paint over the area you want to fill or remove first.');
            return;
        }
        const img = this.fillModalImage;
        const mask = this.fillMaskCanvas.toDataURL('image/png');
        const prompt = (this.fillPrompt || '').trim();
        this.fillModalImage = null;
        this.fillPrompt = '';
        this.hasFillStrokes = false;
        this.submitFill(img, mask, prompt);
    }

    private submitFill(img: { id: string; url: string }, mask: string, prompt: string): void {
        if (this.editingImageId) { return; }
        this.editingImageId = img.id;

        const pending: UIMessage = {
            role: 'assistant',
            content: '',
            images: [],
            created_at: new Date().toISOString(),
            pending: true
        };
        this.messages.push(pending);
        this.shouldScroll = true;

        const req: FillImageRequest = {
            session_id: this.activeSessionId || null,
            user_id: this.studioUserId,
            image_id: img.id,
            mask,
            prompt,
            customer_id: this.currentUser?.customers_id
        };

        /*
         * Registered as the live turn, exactly as send() does. Without this the
         * composer never swapped to Stop for a tool run, and there was no held
         * subscription for stopGenerating() to drop even if it had.
         */
        this.beginTurn(pending);

        this.activeRequest = this.chatService.fillImage(req).subscribe(
            res => {
                this.replacePending(pending, res?.ai_message || '', (res?.new_images || []).map(i => ({ id: i.id, url: i.url })));
                if (res?.session_id) { this.activeSessionId = res.session_id; }
                this.editingImageId = null;
                this.endTurn();
                this.loadSessions();
                if (this.activeSessionId) { this.loadCredits(this.activeSessionId); }
            },
            err => {
                this.removePending(pending);
                this.editingImageId = null;
                this.endTurn();
                this.toastr.error(err?.error?.message || err?.error?.detail || 'Generative fill failed');
            }
        );
    }

    /* ---- Replace background (prompt modal) ---- */

    openReplaceBgModal(img: { id: string; url: string }): void {
        this.replaceBgImage = { id: img.id, url: img.url };
        this.replaceBgPrompt = '';
    }

    closeReplaceBgModal(): void {
        if (this.editingImageId === this.replaceBgImage?.id) { return; }
        this.replaceBgImage = null;
        this.replaceBgPrompt = '';
    }

    confirmReplaceBg(): void {
        const prompt = (this.replaceBgPrompt || '').trim();
        if (!this.replaceBgImage || !prompt) {
            this.toastr.info('Describe the new background scene first.');
            return;
        }
        const img = this.replaceBgImage;
        this.replaceBgImage = null;
        this.replaceBgPrompt = '';
        this.applyEditTool(img, 'replace_bg', { prompt });
    }

    /* ---- Video (Animate) ---- */

    /** The image waiting for a video model to be chosen; null when no modal is up. */
    animateChoice: { id: string; url: string } | null = null;

    /**
     * The models the Animate modal lists: every video model, with the ones the
     * plan does not sell shown locked rather than hidden.
     *
     * Same rule as the generate picker — the list doubles as the line-up on offer,
     * and a customer cannot ask for a model they have never seen.
     */
    get animateModels(): ModelInfo[] {
        return this.generateVideoModels;
    }

    /**
     * Ask which video model to animate on.
     *
     * This used to run straight onto firstAllowedVideoModel, which respected the
     * plan but gave no say: with several video models sold, the cheapest and the
     * most expensive are one list position apart and the customer was never told
     * which one they got.
     */
    animateImage(img: { id: string; url: string }): void {
        if (this.editingImageId) { return; }

        if (!this.animateModels.length) {
            this.toastr.info('No video models are available right now.');
            return;
        }

        this.animateChoice = img;
    }

    /** Run the animation on the picked model, unless the plan does not sell it. */
    pickAnimateModel(model: ModelInfo): void {
        if (!model?.key) { return; }

        // Listed to show the range, but not selectable: say why and leave the
        // modal open, the way the generate picker does.
        if (this.isModelLocked(model)) {
            this.toastr.info(this.lockedModelHint(model));
            return;
        }

        const img = this.animateChoice;
        this.animateChoice = null;
        if (!img) { return; }

        this.startAnimate(img, model);
    }

    cancelAnimateChoice(): void {
        this.animateChoice = null;
    }

    private startAnimate(img: { id: string; url: string }, model: ModelInfo): void {
        if (this.editingImageId) { return; }

        this.editingImageId = img.id;

        // Duration / resolution / aspect ratio come from that model's own control
        // defaults — they differ per model (wan-2.2 tops out at 720p, and the
        // aspect ratios are named presets, not "16:9").
        const defaults = this.videoControlDefaults(model);

        const pending: UIMessage = {
            role: 'assistant',
            content: '',
            images: [],
            created_at: new Date().toISOString(),
            pending: true,
            job: {
                status: 'queued',
                model: model.model || model.key,
                operation: 'image_to_video',
                startedAt: Date.now(),
                elapsed: 0
            }
        };
        this.messages.push(pending);
        this.shouldScroll = true;

        // Same registration as the five above; the job branch below then hands
        // the live turn from the request over to the poller.
        this.beginTurn(pending);

        this.activeRequest = this.chatService.generateVideoImage({
            image_id: img.id,
            prompt: (this.pendingMessage || '').trim() || 'gentle natural motion',
            model: model.key,
            duration: defaults.duration ?? 5,
            resolution: defaults.resolution ?? '720p',
            aspect_ratio: defaults.aspect_ratio ?? 'landscape',
            session_id: this.activeSessionId || null,
            customer_id: this.currentUser?.customers_id
        }).subscribe(
            job => {
                this.isSending = false;
                this.activeRequest = null;
                if (job?.job_id) {
                    // The turn is still live — it moves from a request to a poller,
                    // which is what stop reaches for from here. Same handover as
                    // generateVideoText().
                    this.activeJobId = job.job_id;
                    this.pollJob(job.job_id, pending);
                } else {
                    this.removePending(pending);
                    this.editingImageId = null;
                    this.endTurn();
                    this.toastr.error('Could not start video job');
                }
            },
            err => {
                this.removePending(pending);
                this.editingImageId = null;
                this.endTurn();
                this.toastr.error(err?.error?.message || err?.error?.detail || 'Video generation failed');
            }
        );
    }

    private pollJob(jobId: string, pending: UIMessage): void {
        this.clearPoller(jobId);
        this.jobPollers[jobId] = setInterval(() => {
            this.chatService.getVideoJob(jobId).subscribe(
                job => {
                    // The reader stopped this turn while the poll was in flight;
                    // clearing the interval cannot recall a request already sent.
                    if (pending.stopped) { return; }

                    // Live-update the status card on every poll.
                    if (pending.job) {
                        pending.job.status = job?.status || pending.job.status;
                        if (job?.model) { pending.job.model = job.model; }
                        if (job?.operation) { pending.job.operation = job.operation; }
                        pending.job.elapsed = Math.round((Date.now() - (pending.job.startedAt || Date.now())) / 1000);
                    }

                    if (job?.status === 'succeeded') {
                        this.clearPoller(jobId);
                        this.editingImageId = null;
                        this.releaseJobTurn(jobId);
                        this.replacePending(pending, 'Here is your video.', [], job.output?.url ? [{ url: job.output.url }] : []);
                        this.loadSessions();
                        if (this.activeSessionId) { this.loadCredits(this.activeSessionId); }
                    } else if (job?.status === 'failed') {
                        this.clearPoller(jobId);
                        this.editingImageId = null;
                        this.releaseJobTurn(jobId);
                        this.replacePending(pending, 'Video generation failed: ' + (job?.error || 'unknown error'), []);
                        this.toastr.error(job?.error || 'Video render failed');
                    }
                },
                () => { /* transient poll error — keep polling */ }
            );
        }, 4000);
    }

    jobStatusLabel(status: string): string {
        switch (status) {
            case 'queued': return 'Queued';
            case 'processing': return 'Processing';
            case 'succeeded': return 'Completed';
            case 'failed': return 'Failed';
            default: return status || 'Working';
        }
    }

    jobOperationLabel(op?: string): string {
        switch (op) {
            case 'image_to_video': return 'Animating image into video';
            case 'text_to_video': return 'Generating video';
            case 'talking_head': return 'Creating talking-head video';
            default: return 'Generating video';
        }
    }

    /**
     * A polled job has reached a terminal state, so the turn it belonged to is
     * over — drop it, and with it the stop button.
     *
     * This was missing entirely: pollJob cleared its interval but left
     * activeJobId set, so isGenerating stayed true after a video finished and the
     * composer sat on Stop with nothing left to stop. It went unnoticed because
     * only text-to-video reached here; every tool routed through the poller now,
     * so it would have been the common case.
     *
     * Guarded on the id because a late poll from an abandoned job must not clear
     * a turn the reader has since started.
     */
    private releaseJobTurn(jobId: string): void {
        if (this.activeJobId === jobId) {
            this.endTurn();
        }
    }

    private clearPoller(jobId: string): void {
        if (this.jobPollers[jobId]) {
            clearInterval(this.jobPollers[jobId]);
            delete this.jobPollers[jobId];
        }
    }

    /* ---- Talking head (Talk) ---- */

    // Opens the lip-sync modal for the chosen portrait; the actual job is
    // kicked off from confirmTalk() once the user picks an audio file.
    startTalkingHead(img: { id: string; url: string }): void {
        this.talkingHeadImage = { id: img.id, url: img.url };
        this.talkAudioFile = null;
        this.talkAudioName = '';
    }

    closeTalkModal(): void {
        if (this.editingImageId === this.talkingHeadImage?.id) { return; }
        this.talkingHeadImage = null;
        this.talkAudioFile = null;
        this.talkAudioName = '';
    }

    onTalkAudioSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input?.files?.[0];
        if (!file) { return; }
        this.talkAudioFile = file;
        this.talkAudioName = file.name;
        // Allow re-selecting the same file later.
        if (input) { input.value = ''; }
    }

    confirmTalk(): void {
        const img = this.talkingHeadImage;
        const file = this.talkAudioFile;
        if (!img || !file) {
            this.toastr.info('Choose an audio file first.');
            return;
        }
        this.talkingHeadImage = null;
        this.talkAudioFile = null;
        this.talkAudioName = '';
        this.submitTalkingHead(img, file);
    }

    private submitTalkingHead(img: { id: string; url: string }, file: File): void {
        const pending: UIMessage = {
            role: 'assistant',
            content: '',
            images: [],
            created_at: new Date().toISOString(),
            pending: true,
            job: { status: 'queued', operation: 'talking_head', startedAt: Date.now(), elapsed: 0 }
        };
        this.messages.push(pending);
        this.shouldScroll = true;
        this.editingImageId = img.id;

        // Talk is two calls — upload then job — and the reader can stop during
        // either, so activeRequest holds whichever is in flight.
        this.beginTurn(pending);

        this.activeRequest = this.chatService.uploadAudio(file, this.activeSessionId).subscribe(
            audio => {
                // Upload is done; the job request is now what stop has to drop.
                this.activeRequest = this.chatService.talkingHead({
                    session_id: this.activeSessionId || null,
                    image_id: img.id,
                    audio_url: audio.url,
                    customer_id: this.currentUser?.customers_id
                }).subscribe(
                    job => {
                        this.isSending = false;
                        this.activeRequest = null;
                        if (job?.job_id) {
                            this.activeJobId = job.job_id;
                            this.pollJob(job.job_id, pending);
                        } else {
                            this.removePending(pending);
                            this.editingImageId = null;
                            this.endTurn();
                            this.toastr.error('Could not start talking-head job');
                        }
                    },
                    err => {
                        this.removePending(pending);
                        this.editingImageId = null;
                        this.endTurn();
                        this.toastr.error(err?.error?.message || err?.error?.detail || 'Talking-head failed');
                    }
                );
            },
            () => {
                this.removePending(pending);
                this.editingImageId = null;
                this.endTurn();
                this.toastr.error('Audio upload failed');
            }
        );
    }

    triggerUpload(): void {
        // Full: say so rather than open a picker whose files would be turned away.
        if (this.referenceSlotsLeft <= 0) {
            this.toastr.info('Up to ' + this.MAX_REFERENCE_IMAGES + ' reference images — remove one to add another.');
            return;
        }
        this.fileInput?.nativeElement.click();
    }

    onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input?.files || []);
        if (!files.length) { return; }

        // The picker is already filtered to accept="image/*", but a determined
        // "All files" selection gets through it on some platforms.
        const images = files.filter(f => this.isImageFile(f));
        if (!images.length) {
            this.toastr.error('Only image files can be attached.');
            input.value = '';
            return;
        }
        if (images.length < files.length) {
            this.toastr.info('Skipped the files that are not images.');
        }

        this.uploadImageFiles(images, input);
    }

    /* ---------- Drop an image anywhere on the chat area ---------- */

    /** True while a dragged file is over the chat area — drives the drop overlay. */
    isDragOver = false;

    /**
     * dragenter/dragleave fire again for every child element the pointer crosses,
     * so the overlay is keyed to a depth count rather than the last event seen.
     */
    private dragDepth = 0;

    onDragEnter(event: DragEvent): void {
        if (!this.isFileDrag(event)) { return; }
        this.dragDepth++;
        this.isDragOver = true;
    }

    onDragOver(event: DragEvent): void {
        if (!this.isFileDrag(event)) { return; }
        // Both required: without them the browser handles the drop itself and
        // navigates away from the studio to the dropped file.
        event.preventDefault();
        if (event.dataTransfer) { event.dataTransfer.dropEffect = 'copy'; }
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent): void {
        if (!this.isFileDrag(event)) { return; }
        this.dragDepth = Math.max(0, this.dragDepth - 1);
        if (!this.dragDepth) { this.isDragOver = false; }
    }

    /**
     * Dropping a file on the chat area attaches it exactly as the paperclip
     * does. Images only: anything else is refused with a word about why, rather
     * than being sent to an endpoint that would reject it.
     */
    onDrop(event: DragEvent): void {
        if (!this.isFileDrag(event)) { return; }
        event.preventDefault();
        this.dragDepth = 0;
        this.isDragOver = false;

        const files = Array.from(event.dataTransfer?.files || []);
        if (!files.length) { return; }

        const images = files.filter(f => this.isImageFile(f));
        if (!images.length) {
            this.toastr.error(
                files.length > 1
                    ? 'Images only — none of those files is an image.'
                    : 'Images only — that file isn’t an image.'
            );
            return;
        }
        if (images.length < files.length) {
            this.toastr.info('Skipped the files that are not images.');
        }

        // Up to MAX_REFERENCE_IMAGES, as the paperclip allows; uploadImageFiles()
        // says so when there were more than the room left.
        this.uploadImageFiles(images);
    }

    /** Whether this drag is carrying files, as opposed to selected text or a URL. */
    private isFileDrag(event: DragEvent): boolean {
        const types = event.dataTransfer?.types;
        if (!types) { return false; }
        return Array.prototype.indexOf.call(types, 'Files') !== -1;
    }

    /**
     * `type` is the reliable signal, but some sources (a few archive tools, older
     * Windows shells) hand over a blank one — hence the extension fallback.
     */
    private isImageFile(file: File): boolean {
        if (file.type) { return file.type.toLowerCase().startsWith('image/'); }
        return /\.(png|jpe?g|gif|webp|bmp|avif|heic|heif|tiff?|svg)$/i.test(file.name || '');
    }

    /** Room left for more reference images on the composer. */
    get referenceSlotsLeft(): number {
        return Math.max(0, this.MAX_REFERENCE_IMAGES - this.attachedImages.length);
    }

    /** Bytes the attached uploads already use of MAX_REFERENCE_BYTES. */
    get referenceBytesUsed(): number {
        return this.attachedImages.reduce((n, a) => n + (a.size || 0), 0);
    }

    /** "4.2 MB" — for the limit messages. */
    private formatBytes(bytes: number): string {
        return bytes >= 1024 * 1024
            ? (bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '') + ' MB'
            : Math.max(1, Math.round(bytes / 1024)) + ' KB';
    }

    /**
     * The one upload path, shared by the attach button and a drop: up to the room
     * left — MAX_REFERENCE_IMAGES files and MAX_REFERENCE_BYTES (5 MB) in all —
     * each added to the composer's references.
     * `input` is the hidden file input to reset afterwards, when the upload came
     * from it.
     *
     * One at a time, in order: the first upload of a fresh session opens it
     * upstream, and the rest must go up into that same session rather than each
     * opening their own.
     */
    private uploadImageFiles(files: File[], input?: HTMLInputElement): void {
        if (this.isUploading) {
            this.toastr.info('An upload is already running — one moment.');
            if (input) { input.value = ''; }
            return;
        }

        const room = this.referenceSlotsLeft;
        if (room <= 0) {
            this.toastr.info('Up to ' + this.MAX_REFERENCE_IMAGES + ' reference images — remove one to add another.');
            if (input) { input.value = ''; }
            return;
        }
        if (files.length > room) {
            this.toastr.info('Up to ' + this.MAX_REFERENCE_IMAGES + ' reference images — attached the first '
                + room + '.');
        }

        /*
         * The 5 MB budget, across what is already attached and what is picked.
         * Taken in order: a file that would go over is skipped, and a smaller one
         * after it may still fit.
         */
        let budget = this.MAX_REFERENCE_BYTES - this.referenceBytesUsed;
        const queue: File[] = [];
        const tooBig: string[] = [];
        files.slice(0, room).forEach(f => {
            if (f.size <= budget) {
                queue.push(f);
                budget -= f.size;
            } else {
                tooBig.push(f.name || 'a file');
            }
        });
        if (tooBig.length) {
            this.toastr.warning(
                'Uploads are limited to ' + this.formatBytes(this.MAX_REFERENCE_BYTES) + ' in total per message'
                + (this.referenceBytesUsed ? ' (' + this.formatBytes(this.referenceBytesUsed) + ' already attached)' : '')
                + ' — skipped ' + tooBig.join(', ') + '.'
            );
        }
        if (!queue.length) {
            if (input) { input.value = ''; }
            return;
        }

        this.isUploading = true;
        this.uploadingCount = queue.length;

        const next = () => {
            const file = queue.shift();
            if (!file) {
                this.isUploading = false;
                this.uploadingCount = 0;
                if (input) { input.value = ''; }
                return;
            }
            this.uploadOneImage(file, () => {
                this.uploadingCount = queue.length;
                next();
            });
        };
        next();
    }

    /** Upload one image and add it to the composer's references; `done` runs either way. */
    private uploadOneImage(file: File, done: () => void): void {
        this.chatService.uploadImage(file, this.activeSessionId, this.currentUser).subscribe(
            (res: any) => {
                if (!res?.id || !res?.url) {
                    this.toastr.error('Upload failed' + (file.name ? ': ' + file.name : ''));
                    done();
                    return;
                }

                // Uploading without an active session makes the upstream open one
                // and return its id. Adopt it here — otherwise the next send()
                // starts a *second* session and the uploaded reference is orphaned.
                if (res.session_id && res.session_id !== this.activeSessionId) {
                    const isNew = !this.sessions.some((item: any) => item.id === res.session_id);
                    this.activeSessionId = res.session_id;
                    this.loadCredits(res.session_id);
                    if (isNew) { this.loadSessions(); }
                }

                // The freshly opened session has no history worth fetching, and
                // loadHistory() would wipe the attachment strip — so just show the
                // chip, exactly like an upload inside an ongoing session does.
                this.addReferenceImage({ id: res.id, url: res.url, size: file.size });
                this.shouldScroll = true;
                done();
            },
            () => {
                this.toastr.error('Upload failed' + (file.name ? ': ' + file.name : ''));
                done();
            }
        );
    }

    /**
     * Add an uploaded image beside those already attached — up to
     * MAX_REFERENCE_IMAGES, and never the same image twice.
     */
    private addReferenceImage(img: { id: string; url: string; size?: number }): void {
        if (!img?.id || this.attachedImages.some(a => a.id === img.id)) { return; }
        if (this.attachedImages.length >= this.MAX_REFERENCE_IMAGES) { return; }
        this.attachedImages = [...this.attachedImages, { id: img.id, url: img.url, size: img.size || 0 }];
    }

    removeAttachment(id: string): void {
        this.attachedImages = this.attachedImages.filter(a => a.id !== id);
    }

    /* ============================================================
     *  Stock: search the library and list the results in the chat
     * ============================================================ */

    /** How many rows one page of results asks for. */
    private static readonly STOCK_PER_PAGE = 24;

    get isStockMode(): boolean {
        return this.activeNav === 'stock' || this.currentMode === 'stock';
    }

    /**
     * The words to search for. Stock mode prefills the composer with its own
     * lead-in ("Get Stock Images: "), which is a label for the person typing and
     * not part of what they are looking for.
     */
    private stockQuery(text: string): string {
        return (text || '')
            .replace(/^\s*(get\s+)?stock\s+images?\s*[:\-]?\s*/i, '')
            .replace(/^\s*(find|search|show\s+me)\s+/i, '')
            .trim();
    }

    /**
     * The collections this seat may search. Same source the product search uses;
     * the plan's permission list is the fallback when the stored user predates
     * plan_collections.
     */
    /**
     * The signed-in customer's id, for the search service's customerId parameter.
     *
     * Read from storage at call time rather than from the `currentUser` snapshot
     * the constructor took: the snapshot is captured once when the studio opens,
     * so a session that signed in afterwards would search as an anonymous visitor
     * and get unentitled pricing back. Falls back to the snapshot, then to ''.
     */
    private get stockCustomerId(): string {
        let stored: any = null;
        try {
            stored = JSON.parse(localStorage.getItem('currentUser') || 'null');
        } catch {
            stored = null;
        }

        return stored?.customers_id || this.currentUser?.customers_id || '';
    }

    private get stockCollections(): string {
        const plan = this.currentUser?.plan_collections;
        if (Array.isArray(plan)) { return plan.join(','); }
        if (plan) { return String(plan); }
        return this.permissionCollections || '';
    }

    private runStockSearch(text: string): void {
        const query = this.stockQuery(text);
        if (!query) {
            this.toastr.info('Type what you are looking for, then send.');
            return;
        }

        const now = new Date().toISOString();

        this.messages.push({
            role: 'user',
            content: text,
            images: [],
            created_at: now
        });

        const card: UIMessage = {
            role: 'assistant',
            content: '',
            images: [],
            created_at: now,
            stock: {
                query,
                total: 0,
                pages: 0,
                page: 1,
                seconds: 0,
                results: [],
                loading: true
            }
        };
        this.messages.push(card);

        this.pendingMessage = '';
        this.lastPrefill = '';
        this.shouldScroll = true;
        this.stickToBottom = true;

        // The card below carries the full response now; the preview strip has
        // served its purpose.
        this.clearStockSuggestions();

        this.fetchStockPage(card.stock as StockSearch, 1);
    }

    /** Next page, appended to the same card rather than opening a second one. */
    loadMoreStock(stock: StockSearch): void {
        if (stock.loading || stock.page >= stock.pages) { return; }
        this.fetchStockPage(stock, stock.page + 1);
    }

    private fetchStockPage(stock: StockSearch, page: number): void {
        stock.loading = true;
        stock.error = '';

        this.apiService.searchStockImages(
            stock.query,
            AiChatComponent.STOCK_PER_PAGE,
            page,
            this.stockCollections,
            this.stockCustomerId,
            // The result card carries its own loading state, and the full-page
            // preloader would cover the transcript the results are landing in.
            true
        ).subscribe(
            (res: any) => {
                const rows: StockResult[] = res?.results || [];

                // Page 1 replaces, later pages append — the card is one running
                // list, not a pager the reader has to step through.
                stock.results = page === 1 ? rows : stock.results.concat(rows);
                stock.total = Number(res?.total) || stock.results.length;
                stock.pages = Number(res?.pages) || 0;
                stock.seconds = Number(res?.time) || 0;
                stock.page = page;
                stock.loading = false;

                // Only the first page scrolls: on "load more" the reader is
                // already partway down the list and should stay there.
                if (page === 1) { this.shouldScroll = true; }
            },
            () => {
                stock.loading = false;
                stock.error = 'The library search failed. Please try again.';
            }
        );
    }

    /** Whatever caption the row actually carries, falling back to the filename. */
    stockCaption(row: StockResult): string {
        return (row?.images_caption || row?.caption || row?.filename || '').trim();
    }

    stockThumb(row: StockResult): string {
        return row?.thumbnail || row?.preview || '';
    }

    /* ---- Stock suggestions while typing ---- */

    /** How many thumbnails the typeahead strip shows. */
    /**
     * How many thumbnails the typeahead asks for.
     *
     * Enough to fill the strip across a wide composer rather than leaving half the
     * row empty; on a narrower one the overflow scrolls. Still a preview — sending
     * asks for STOCK_PER_PAGE and lists everything.
     */
    private static readonly STOCK_SUGGEST_COUNT = 15;

    /** Placeholder slots, kept in step with the count actually requested. */
    readonly stockSuggestSkeletons = Array(AiChatComponent.STOCK_SUGGEST_COUNT).fill(0);

    /** Keystrokes, debounced into searches. */
    private stockSuggestInput$ = new Subject<string>();
    private stockSuggestSub: Subscription | null = null;

    /** What the strip is showing, and for which query. */
    stockSuggestions: StockResult[] = [];
    stockSuggestLoading = false;
    stockSuggestQuery = '';

    /**
     * True from the first query until the search is sent, the mode changes, or the
     * composer is emptied.
     *
     * Held as its own flag rather than inferred from `stockSuggestions.length`,
     * which made the strip disappear the moment a query came back with nothing —
     * exactly when the reader most needs to be told so. An empty result is a
     * result, and the strip stays to report it.
     */
    stockSuggestActive = false;

    /** Shown only in stock mode, and only once a query has been asked. */
    get showStockSuggestions(): boolean {
        return this.isStockMode && this.stockSuggestActive;
    }

    /** No rows for what has been typed, and nothing still in flight. */
    get stockSuggestEmpty(): boolean {
        return !this.stockSuggestLoading && this.stockSuggestions.length === 0;
    }

    /**
     * Wire the typeahead: 300ms after the typing settles, ask the library for a
     * first handful so the reader can see whether the words are landing before
     * committing to a search.
     *
     * switchMap rather than mergeMap so a superseded query's response cannot
     * arrive late and overwrite a newer one — the usual typeahead race.
     *
     * Errors are swallowed per-query inside the inner call: letting one reach the
     * outer stream would kill the subscription and silently stop the typeahead
     * for the rest of the session.
     */
    private initStockSuggestions(): void {
        this.stockSuggestSub = this.stockSuggestInput$.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            switchMap(query => {
                this.stockSuggestQuery = query;

                // Nothing typed is nothing to preview — the strip appears with the
                // first word and goes away if the composer is cleared back out.
                if (!query) {
                    this.stockSuggestLoading = false;
                    this.stockSuggestActive = false;
                    return of([] as StockResult[]);
                }

                // Guests get no suggestion strip rather than a search request per
                // keystroke: submitting the prompt shows the sign-in card anyway.
                if (!this.isSignedIn) {
                    this.stockSuggestLoading = false;
                    this.stockSuggestActive = false;
                    return of([] as StockResult[]);
                }

                this.stockSuggestActive = true;
                this.stockSuggestLoading = true;

                return this.apiService.searchStockImages(
                    query,
                    AiChatComponent.STOCK_SUGGEST_COUNT,
                    1,
                    this.stockCollections,
                    this.stockCustomerId,
                    true                             // skipLoader — a keystroke is not a page load
                ).pipe(
                    map((res: any) => (res?.results || []) as StockResult[]),
                    catchError(() => of([] as StockResult[]))
                );
            })
        ).subscribe(rows => {
            this.stockSuggestLoading = false;
            this.stockSuggestions = rows.slice(0, AiChatComponent.STOCK_SUGGEST_COUNT);
        });
    }

    /**
     * Feed the typeahead from the composer. The lead-in the mode prefills
     * ("Get Stock Images: ") is stripped first, so the strip doesn't search for
     * the label.
     */
    private onStockTyping(text: string): void {
        if (!this.isStockMode) {
            this.clearStockSuggestions();
            return;
        }

        this.stockSuggestInput$.next(this.stockQuery(text));
    }

    private clearStockSuggestions(): void {
        this.stockSuggestions = [];
        this.stockSuggestLoading = false;
        this.stockSuggestQuery = '';
        this.stockSuggestActive = false;
    }

    /**
     * A suggestion is a look, not a choice: open it in the same lightbox the
     * result cards use, leaving what has been typed alone.
     */
    openStockSuggestion(row: StockResult): void {
        this.openStockPreview(row, this.stockSuggestions);
    }

    /* ---- Stock lightbox ---- */

    /**
     * The open result, as the list it came from plus a position in it. Holding the
     * list (not just the row) is what lets the arrows step through the search
     * without reopening: the viewer is a window onto that result set.
     */
    stockViewer: { rows: StockResult[]; index: number } | null = null;

    openStockPreview(row: StockResult, rows: StockResult[]): void {
        const index = rows.indexOf(row);
        if (index < 0) { return; }
        this.stockViewer = { rows, index };
    }

    closeStockViewer(): void {
        this.stockViewer = null;
    }

    /* ---- Stock download confirmation ---------------------------------------
     *
     * Its own small flow, deliberately separate from the licence-acceptance modal
     * the generated-image download uses. That one gates on reading terms; this one
     * states a price. Sharing the machinery would have meant changing a path that
     * already works for a different question.
     * --------------------------------------------------------------------- */

    /** The row awaiting confirmation, or null when the dialog is closed. */
    stockDownloadRow: StockResult | null = null;

    /**
     * Which action is running, or '' when idle.
     *
     * One flag rather than two booleans: both buttons spend the same credit and
     * only one can run at a time, and this way each shows its own spinner without
     * the two states being able to contradict each other.
     */
    stockAction: '' | 'download' | 'edit' = '';

    /** True while either action is out, so neither can be fired twice. */
    get stockDownloading(): boolean {
        return this.stockAction !== '';
    }

    /**
     * Which rate a stock image is billed at, by filename prefix.
     *
     * Mirrors Helper::stockServiceCode() on the backend — partner-sourced images
     * carry a DPPS_ prefix and are priced on stock.api_image, everything else on
     * stock.contributor_image. Matched case-insensitively for the same reason it
     * is there: the rates differ by several credits and the cheaper one is the
     * wrong way to be wrong.
     *
     * This decides only what the dialog SAYS. The charge is the backend's, taken
     * from the same column — so if the two ever disagree, the backend is right and
     * this is the bug.
     */
    stockServiceCode(row: StockResult): string {
        const filename = (row?.filename || '').toString();

        return filename.toLowerCase().indexOf('dpps_') === 0
            ? 'stock.api_image'
            : 'stock.contributor_image';
    }

    /**
     * What this download will cost, or null when the plan carries no line for it —
     * in which case the dialog says nothing about credits rather than guessing a
     * number the backend will not honour.
     */
    stockDownloadCredits(row: StockResult): number | null {
        const tool = this.permissionToolFor(this.stockServiceCode(row));

        return tool ? tool.sell_credits : null;
    }

    /**
     * Ask before spending. stopPropagation because the button sits inside the
     * card's own click target, which opens the viewer.
     */
    openStockDownload(row: StockResult, event: Event): void {
        event.stopPropagation();
        event.preventDefault();

        if (!this.isSignedIn) {
            this.openAuthDialog('signin');
            return;
        }

        /*
         * Held from the moment the dialog opens, because this is the file the
         * editor hand-off uploads — not the download_link the charge returns.
         * That link is a short-lived signed GCS URL, and reading it back from the
         * browser is exactly what was failing; the thumbnail is served plainly and
         * fetches without trouble.
         */
        this.stockEditThumbUrl = this.stockThumb(row);
        this.stockDownloadRow = row;
    }

    /** The thumbnail captured when the dialog opened — see openStockDownload(). */
    private stockEditThumbUrl = '';

    /**
     * Edit straight from the card, without the confirm dialog.
     *
     * The dialog exists to ask before spending, and edit no longer spends
     * anything, so there is nothing to confirm.
     *
     * It still has to set up what confirmStockDownload() reads: the row it acts
     * on, and the thumbnail the hand-off uploads. stopPropagation because this
     * sits inside the card's own click target, which opens the viewer.
     */
    editStockImage(row: StockResult, event: Event): void {
        event.stopPropagation();
        event.preventDefault();

        if (!this.isSignedIn) {
            this.openAuthDialog('signin');
            return;
        }

        if (this.stockDownloading) { return; }

        /*
         * The viewer is a full-screen overlay, and the image is handed to the
         * COMPOSER underneath it. Left open, the customer would be looking at a
         * lightbox while the thing they asked for happened out of sight.
         *
         * Harmless from the card, where there is no viewer open to close.
         */
        this.closeStockViewer();

        this.stockEditThumbUrl = this.stockThumb(row);
        this.stockDownloadRow = row;

        this.confirmStockDownload('edit');
    }

    closeStockDownload(): void {
        if (this.stockDownloading) { return; }
        this.stockDownloadRow = null;
    }

    /**
     * Confirmed. The credits come off server-side in Helper::download(), priced
     * from the customer's own credit pack line — nothing here deducts anything.
     */
    confirmStockDownload(mode: 'download' | 'edit'): void {
        const row = this.stockDownloadRow;
        if (!row || this.stockDownloading) { return; }

        this.stockAction = mode;

        /*
         * EDIT DOES NOT CALL THE DOWNLOAD API.
         *
         * It used to: the charge was what proved the customer could have the
         * image, and the editor was handed the thumbnail afterwards. The
         * thumbnail is the only thing the hand-off ever uploaded — the
         * download_link is a signed GCS URL the browser cannot read back — so
         * skipping the call costs the editor nothing, and it is already in
         * stockEditThumbUrl.
         *
         * What it DOES cost: editing a stock image now charges no credits and is
         * checked against no plan. Downloading still does both.
         */
        if (mode === 'edit') {
            this.sendStockToEditor(row, this.stockEditThumbUrl || this.stockThumb(row));
            return;
        }

        const imageData: any = {
            id: row.id,
            filename: row.filename || row.id,
            thumbnail: this.stockThumb(row),
            license: row.license || 'RF (Royalty Free)',
            collection: row.collection,
            size: 'Extra Large',
            price: 0,
            type: 'Image',
            caption: this.stockCaption(row),
            category: 'creative',
            searchText: '',
            usage: 'all_media',
        };

        this.apiService.downloadImage(imageData, true).subscribe(
            (data: any) => {
                const first = data && data[0];
                const link = first && first.download_link;

                if (!link) {
                    /*
                     * The backend explains refusals it understands — out of credits,
                     * or a plan that does not cover this collection or size. Shown
                     * as sent; the generic line is only for what it cannot explain.
                     */
                    this.stockAction = '';
                    this.toastr.error((first && first.message) || 'Could not start the download. Please try again.');
                    return;
                }

                // The balance just moved, so the badge would otherwise be stale.
                if (this.activeSessionId) { this.loadCredits(this.activeSessionId); }

                // The edit branch that stood here is gone with the early return
                // above: by this point `mode` can only be 'download'.
                this.stockAction = '';
                this.stockDownloadRow = null;
                window.open(link, '_blank');
            },
            () => {
                this.stockAction = '';
                this.toastr.error('Could not start the download. Please try again.');
            }
        );
    }

    /**
     * Put a stock image into the composer as the reference to edit.
     *
     * TWO STEPS, and the order is the point.
     *
     * The chip is set straight away, so the reference appears the moment the
     * download returns rather than after a second round trip.
     *
     * The upload behind it is what makes the reference USABLE. send() passes
     * `attached_image_ids` upstream and nothing else — the urls in the message are
     * local render state — so the studio has to have issued the id or the edit
     * runs against no reference at all. When it lands, the placeholder is swapped
     * for the studio's own id and url.
     *
     * `imageUrl` is the THUMBNAIL, not the download_link. The link is a signed GCS
     * URL that the browser cannot read back, and it expires in twenty minutes; the
     * thumbnail is plainly served and is what uploads cleanly. The full-resolution
     * file is still what the customer was charged for and still what Download
     * gives them — it is only the editor's working copy that comes from here.
     */
    private sendStockToEditor(row: StockResult, imageUrl: string): void {
        const placeholderId = String(row.id);

        /*
         * Visible immediately: chip, preview, and edit mode. The dialog closes
         * here too, and with it the busy flag — the upload below carries on in the
         * background, and holding the flag for it would lock the customer out of
         * downloading a second image for no reason they could see.
         */
        this.stockAction = '';
        this.stockDownloadRow = null;
        this.onEditImage({ id: placeholderId, url: imageUrl });
        this.shouldScroll = true;

        const name = (row.filename || placeholderId || 'stock') + '.jpg';

        /*
         * Through the backend, not fetch(imageUrl) directly. The resizer answers
         * 200 with the image but sends no Access-Control-Allow-Origin, so reading
         * the body in the browser is blocked outright — that was the failed
         * request in the network panel, not a bad URL. proxyImage() re-serves it
         * from our own origin, which is the only way to get the bytes at all.
         */
        this.chatService.fetchImageBlob(imageUrl).subscribe(
            (blob: Blob) => { this.uploadStockBlob(blob, name, placeholderId); },
            // proxyImage refused or failed (e.g. a host missing from its allow-list):
            // build the bytes in the browser instead, then upload them like a file
            // picked from the customer's computer.
            () => {
                this.stockBlobInBrowser(imageUrl).then(
                    (blob) => { this.uploadStockBlob(blob, name, placeholderId); },
                    () => { this.warnStockEditDegraded(); }
                );
            }
        );
    }

    /**
     * Upload a stock image's bytes as a File — the same uploadImage call a local
     * attach makes — and swap the placeholder for the reference id it returns.
     */
    private uploadStockBlob(blob: Blob, name: string, placeholderId: string): void {
        const file = new File([blob], name, { type: blob.type || 'image/jpeg' });

        this.chatService.uploadImage(file, this.activeSessionId, this.currentUser).subscribe(
            (res: any) => {
                if (!res?.id || !res?.url) { this.warnStockEditDegraded(); return; }

                // Uploading without an active session opens one upstream;
                // adopt it, or the next send() starts a second and orphans
                // this reference — the rule uploadOneImage() follows too.
                if (res.session_id && res.session_id !== this.activeSessionId) {
                    const isNew = !this.sessions.some((item: any) => item.id === res.session_id);
                    this.activeSessionId = res.session_id;
                    this.loadCredits(res.session_id);
                    if (isNew) { this.loadSessions(); }
                }

                /*
                 * Only if the placeholder is still what is attached. The
                 * customer may have attached something else while this was
                 * in flight, and replacing their choice with an image they
                 * moved on from would be worse than leaving it.
                 */
                if (this.attachedImage?.id === placeholderId) {
                    this.attachImage({ id: res.id, url: res.url });
                }
            },
            () => { this.warnStockEditDegraded(); }
        );
    }

    /**
     * The image's bytes, read by the browser itself — the fallback when proxyImage
     * cannot serve them.
     *
     * Works only for hosts that send Access-Control-Allow-Origin (Depositphotos sends
     * `*`). Native fetch, not HttpClient: the interceptor would add Authorization,
     * which forces a preflight that Depositphotos answers with 415. If fetch fails,
     * the image is drawn onto a canvas and re-exported as a JPEG.
     */
    private stockBlobInBrowser(url: string): Promise<Blob> {
        const viaFetch = fetch(url, { mode: 'cors', credentials: 'omit' }).then(r => {
            if (!r.ok) { throw new Error('HTTP ' + r.status); }
            return r.blob();
        }).then(blob => {
            if (!blob.size || (blob.type && !blob.type.startsWith('image/'))) {
                throw new Error('not an image');
            }
            return blob;
        });

        return viaFetch.catch(() => new Promise<Blob>((resolve, reject) => {
            const img = new Image();
            // Without this the canvas is tainted and toBlob() throws SecurityError.
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.naturalWidth;
                    canvas.height = img.naturalHeight;
                    canvas.getContext('2d')!.drawImage(img, 0, 0);
                    canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/jpeg', 0.95);
                } catch (e) {
                    reject(e);
                }
            };
            img.onerror = () => reject(new Error('image failed to load'));
            img.src = url;
        }));
    }

    /**
     * The image is attached and visible, but the studio never issued an id for it.
     *
     * Said plainly rather than swallowed: the chip looks exactly as it does on a
     * working reference, so without this the next edit would appear to ignore the
     * image for no visible reason. The credit is already spent and the download
     * itself succeeded, so the attachment is left in place rather than torn down.
     */
    private warnStockEditDegraded(): void {
        // this.toastr.warning('Downloaded, but the editor could not take it as a reference. Attach it from your device to edit.');
    }

    get stockViewerRow(): StockResult | null {
        const v = this.stockViewer;
        return v ? v.rows[v.index] || null : null;
    }

    /**
     * Step through the results. Clamped rather than wrapped: reaching the end of
     * what was loaded should read as the end, not silently start over — and the
     * search has more pages behind it that this viewer has not asked for.
     */
    stepStockViewer(delta: number): void {
        const v = this.stockViewer;
        if (!v) { return; }
        const next = v.index + delta;
        if (next < 0 || next >= v.rows.length) { return; }
        v.index = next;
    }

    get canStepStockBack(): boolean {
        return !!this.stockViewer && this.stockViewer.index > 0;
    }

    get canStepStockForward(): boolean {
        return !!this.stockViewer && this.stockViewer.index < this.stockViewer.rows.length - 1;
    }

    /** Arrow keys walk the viewer; Escape closes it. */
    @HostListener('document:keydown', ['$event'])
    onStockViewerKeydown(event: KeyboardEvent): void {
        if (!this.stockViewer) { return; }

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.stepStockViewer(1);
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.stepStockViewer(-1);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            this.closeStockViewer();
        }
    }

    /**
     * The library's own page for a result: /product/detail/:type/:collection/:id,
     * the same shape the cart and search pages link with.
     */
    stockDetailLink(row: StockResult): (string | number)[] | null {
        if (!row?.id || !row?.collection) { return null; }
        return ['/product/detail', 'images', row.collection, row.id];
    }

    /** Counts here run to eight figures, so they are never shown bare. */
    formatCount(value: number): string {
        return (Number(value) || 0).toLocaleString('en-IN');
    }

    /** Search time, to two decimals — the API sends far more precision than that. */
    stockSeconds(stock: StockSearch): string {
        return (Number(stock?.seconds) || 0).toFixed(2);
    }

    // Index-prefixed: two pages of results can legitimately repeat an id, and a
    // duplicate trackBy key is a hard error in ngForOf.
    trackByStockRow(index: number, row: StockResult): string {
        return index + ':' + (row?.id || '');
    }

    /**
     * Voice input: toggles the browser's Web Speech API recognizer. While
     * listening, the composer row is replaced by the recording bar and the
     * transcript accumulates in `voiceTranscript`; ending the take writes it
     * into the prompt box, appended to whatever was already typed there.
     */
    toggleRecording(): void {
        if (this.isRecording) {
            this.finishRecording();
            return;
        }

        // Ask the browser where mic permission stands before starting, so a
        // blocked mic says so instead of silently doing nothing, and the
        // "Listening…" toast only appears once we actually have access.
        this.checkMicPermission().then(state => {
            this.zone.run(() => {
                if (state === 'denied') {
                    this.toastr.error('Microphone access is blocked. Allow mic permission in your browser and try again.');
                    return;
                }
                // granted → announce now. prompt/unknown → the browser asks first,
                // so the announcement waits for audio capture to actually start.
                this.startRecording(state === 'granted');
            });
        });
    }

    /**
     * Current microphone permission as the browser reports it. The Permissions
     * API doesn't cover 'microphone' everywhere (e.g. Safari, older Firefox), so
     * an unsupported or failing query resolves to 'prompt' — we start and let the
     * browser do the asking.
     */
    private checkMicPermission(): Promise<'granted' | 'denied' | 'prompt'> {
        const perms: any = (navigator as any)?.permissions;
        if (!perms?.query) { return Promise.resolve('prompt'); }

        try {
            return perms.query({ name: 'microphone' as any })
                .then((status: any) => status?.state || 'prompt')
                .catch(() => 'prompt' as const);
        } catch {
            return Promise.resolve('prompt');
        }
    }

    /**
     * @param announce show the "Listening…" toast immediately. False when
     * permission hasn't been granted yet — the toast then waits for
     * `onaudiostart`, which only fires once the user allows the mic.
     */
    private startRecording(announce = true): void {
        const SpeechRecognitionCtor =
            (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

        if (!SpeechRecognitionCtor) {
            this.toastr.error('Voice input isn’t supported in this browser. Please use Chrome or Edge.');
            return;
        }

        const recognition = new SpeechRecognitionCtor();
        recognition.lang = navigator.language || 'en-US';
        recognition.interimResults = true;   // show words as they’re spoken
        recognition.continuous = true;        // keep listening until stopped

        // Preserve anything already typed; new speech is appended after it.
        this.recognitionBaseText = this.pendingMessage ? this.pendingMessage.trim() + ' ' : '';
        this.voiceFinalText = '';
        this.voiceTranscript = '';

        recognition.onresult = (event: any) => {
            let finalChunk = '';
            let interimChunk = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalChunk += transcript;
                } else {
                    interimChunk += transcript;
                }
            }
            // No zone.run and no binding to update: nothing on screen shows the
            // words while the bar is up, so the transcript is just banked here
            // and read once, when the take ends.
            if (finalChunk) {
                this.voiceFinalText = (this.voiceFinalText + finalChunk).replace(/\s+/g, ' ') + ' ';
            }
            this.voiceTranscript = (this.voiceFinalText + interimChunk).trim();
        };

        recognition.onerror = (event: any) => {
            this.zone.run(() => {
                const err = event?.error;
                if (err === 'not-allowed' || err === 'service-not-allowed') {
                    this.toastr.error('Microphone access was blocked. Allow mic permission in your browser and try again.');
                } else if (err && err !== 'aborted' && err !== 'no-speech') {
                    this.toastr.error('Voice input error: ' + err);
                }
            });
        };

        recognition.onend = () => {
            // Fires on manual stop, prolonged silence, or after an error. A stop
            // we asked for has already cleared isRecording; anything else ended
            // the take on its own, so treat it as a finish and keep the words.
            this.zone.run(() => {
                this.recognition = null;
                if (this.isRecording) { this.finishRecording(); }
            });
        };

        // Only reached once the browser has granted the mic, so it's the right
        // moment to announce listening when permission was still 'prompt' — and
        // the only safe moment to open the meter's own capture, which would
        // otherwise race the recognizer for the permission prompt and ask the
        // user twice.
        recognition.onaudiostart = () => {
            this.zone.run(() => {
                this.startVoiceMeter();
                if (announce) { return; }
                // this.toastr.info('Listening… speak now. Press stop to drop it in the prompt.');
            });
        };

        try {
            recognition.start();
            this.recognition = recognition;
            this.isRecording = true;
            if (announce) {
                // this.toastr.info('Listening… speak now. Press stop to drop it in the prompt.');
            }
        } catch {
            this.isRecording = false;
            this.recognition = null;
            this.toastr.error('Could not start voice input.');
        }
    }

    /**
     * End the take and keep what was said — the square button on the recording
     * bar, the mic chip toggled a second time, and a recognizer that stopped on
     * its own all land here. The transcript goes in after whatever was already
     * typed, which is what `recognitionBaseText` holds.
     */
    finishRecording(): void {
        const spoken = this.voiceTranscript.trim();
        this.stopRecording();

        if (spoken) {
            this.pendingMessage = (this.recognitionBaseText + spoken).replace(/\s+/g, ' ').trim();
            // The text is the user's now, not a mode prefill, so applyMode() must
            // not treat it as replaceable boilerplate.
            this.lastPrefill = '';
        }
        this.clearTranscript();
    }

    /**
     * Enter ends the take, the way it sends a typed prompt.
     *
     * A window listener rather than a handler on the composer: the textarea is
     * swapped out for the recording bar while a take is live, so there is no
     * focused input to catch the key. Guarded on isRecording, so Enter behaves
     * normally everywhere else.
     *
     * Plain Enter only, matching onComposerKeydown — a modified Enter is left to
     * whatever else the reader is doing.
     */
    @HostListener('window:keydown', ['$event'])
    onRecordingKeydown(event: KeyboardEvent): void {
        if (!this.isRecording) { return; }
        if (event.key !== 'Enter' || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) { return; }

        // Stops the key reaching the stop button if it happens to hold focus,
        // which would finish the take twice.
        event.preventDefault();
        this.finishRecording();
    }

    /** End the take and throw it away — the X on the recording bar. */
    cancelRecording(): void {
        this.stopRecording();
        this.clearTranscript();
    }

    private clearTranscript(): void {
        this.voiceTranscript = '';
        this.voiceFinalText = '';
        this.recognitionBaseText = '';
    }

    private stopRecording(): void {
        // Cleared first: onend fires from stop() and uses this to tell a stop we
        // asked for from one the recognizer decided on.
        this.isRecording = false;

        if (this.recognition) {
            try { this.recognition.stop(); } catch { /* noop */ }
        }
        this.stopVoiceMeter();
    }

    /* ---- Recording waveform ---- */

    /**
     * Open a second capture purely to measure level, so the bar can trace the
     * voice the way ChatGPT's does. Best-effort: a browser that refuses the
     * stream (or has no AudioContext) leaves `voiceMeterLive` false and the bars
     * fall back to their CSS animation.
     */
    private startVoiceMeter(): void {
        const AudioCtor = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtor || !navigator?.mediaDevices?.getUserMedia) { return; }

        navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
            // The take may have ended while the browser was still asking.
            if (!this.isRecording) {
                stream.getTracks().forEach(t => t.stop());
                return;
            }

            const ctx: AudioContext = new AudioCtor();
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 1024;
            analyser.smoothingTimeConstant = 0.65;
            ctx.createMediaStreamSource(stream).connect(analyser);

            this.micStream = stream;
            this.audioCtx = ctx;
            this.analyser = analyser;
            this.waveSamples = [];
            this.lastSampleAt = 0;
            this.voiceMeterLive = true;

            this.zone.runOutsideAngular(() => this.runWaveLoop(analyser));
        }).catch(() => { /* keep the CSS fallback */ });
    }

    /**
     * Sample the analyser on every frame but only commit a bar every ~95ms —
     * that cadence is what makes the trace scroll at a readable speed. Runs
     * outside Angular and writes the bars itself, so no change detection.
     */
    private runWaveLoop(analyser: AnalyserNode): void {
        const buf = new Uint8Array(analyser.fftSize);

        const step = () => {
            if (this.analyser !== analyser) { return; }

            analyser.getByteTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++) {
                const v = (buf[i] - 128) / 128;
                sum += v * v;
            }
            // RMS is small for ordinary speech, hence the gain before clamping.
            const level = Math.min(1, Math.sqrt(sum / buf.length) * 3.4);

            const now = performance.now();
            if (now - this.lastSampleAt >= 95) {
                this.lastSampleAt = now;
                this.waveSamples.push(level);
                if (this.waveSamples.length > this.voiceBars.length) {
                    this.waveSamples.shift();
                }
                this.paintWave();
            }

            this.waveFrame = requestAnimationFrame(step);
        };

        this.waveFrame = requestAnimationFrame(step);
    }

    /**
     * Right-aligned: the newest sample is the rightmost bar and older ones drift
     * left, so silence trails off as a row of flat dots. Height carries the
     * level, and brightness follows it so the live end of the trace reads
     * stronger than its tail.
     */
    private paintWave(): void {
        const bars = this.voiceWave?.nativeElement?.children;
        if (!bars) { return; }

        const samples = this.waveSamples;
        const offset = samples.length - bars.length;

        for (let i = 0; i < bars.length; i++) {
            const level = samples[offset + i] ?? 0;
            const bar = bars[i] as HTMLElement;
            bar.style.transform = `scaleY(${(0.055 + level * 0.945).toFixed(3)})`;
            bar.style.opacity = `${(0.4 + level * 0.6).toFixed(2)}`;
        }
    }

    private stopVoiceMeter(): void {
        if (this.waveFrame) {
            cancelAnimationFrame(this.waveFrame);
            this.waveFrame = 0;
        }
        // Cleared before the teardown below: the rAF loop bails on this.
        this.analyser = null;
        this.voiceMeterLive = false;

        if (this.micStream) {
            this.micStream.getTracks().forEach(t => t.stop());
            this.micStream = null;
        }
        if (this.audioCtx) {
            const ctx = this.audioCtx;
            this.audioCtx = null;
            ctx.close().catch(() => { /* already closed */ });
        }
        this.waveSamples = [];
    }

    get currentModeConfig(): ChatModeConfig | null {
        return this.currentMode ? this.chatModeConfig[this.currentMode] : null;
    }

    get composerPlaceholder(): string {
        if (this.videoFromTextMode) {
            return 'Describe a short video to generate (text → video)…';
        }
        if (this.converseMode) {
            return 'Ask or instruct — the AI decides whether to chat, create or refine…';
        }
        return this.currentModeConfig?.placeholder || DEFAULT_PLACEHOLDER;
    }

    /**
     * The composer opens as just the prompt box with attach and mic beside it.
     * Send, the model line and the settings row only appear once there is
     * something to send — an empty box has nothing for them to act on, and the
     * first thing a new visitor sees should be one field, not a control panel.
     *
     * An attachment counts as content: an image on its own is a valid turn, so
     * send has to be reachable without typing.
     */
    get composerExpanded(): boolean {
        return !!this.pendingMessage.trim() || this.attachedImages.length > 0;
    }

    get showBanner(): boolean {
        return !!this.currentModeConfig?.header;
    }

    selectNav(nav: NavKey): void {
        this.activeNav = nav;
        // Switching away from stock leaves a strip that no longer belongs to the
        // mode on screen; onStockTyping only runs on the next keystroke.
        this.clearStockSuggestions();
        const mode = this.navToMode[nav];
        if (mode) {
            this.applyMode(mode);
        } else {
            this.clearMode();
        }
    }

    /* ---------- Generate rail submenu ---------- */

    /**
     * Every model the plan sells, whatever the settings panel's image/video toggle
     * is currently on — the rail lists what you can generate with, which is a
     * property of the plan, not of that toggle. Listed as two labelled groups so
     * it stays obvious which ones produce video.
     */
    /**
     * The whole catalogue, not just what the plan sells.
     *
     * Models the plan does not include are listed and disabled rather than
     * hidden: the picker doubles as the line-up on offer, and a customer cannot
     * ask for a model they have never seen. `isModelLocked()` marks them, and
     * pickGenerateModel() refuses them.
     */
    get generateModels(): ModelInfo[] {
        return this.allModels;
    }

    get generateImageModels(): ModelInfo[] {
        return this.generateModels.filter(m => this.kindOf(m) === 'image');
    }

    get generateVideoModels(): ModelInfo[] {
        return this.generateModels.filter(m => this.kindOf(m) === 'video');
    }

    /** True when the plan does not sell this model, so the row is shown but inert. */
    isModelLocked(model: ModelInfo): boolean {
        return !this.isModelAllowed(model);
    }

    /**
     * What the row's tooltip says about a locked model. Written as an invitation
     * rather than a refusal — the point of listing it is that it can be bought.
     */
    lockedModelHint(model: ModelInfo): string {
        return (model?.label || model?.model || 'This model') + ' is not on your plan \u2014 talk to us to add it';
    }

    /* ---------- Upscale rail submenu ---------- */

    /** True while any of the four pairings is the active section. */
    get isUpscaleActive(): boolean {
        return this.upscaleOptions.some(o => o.nav === this.activeNav);
    }

    /**
     * The parent is a disclosure only — unlike Generate, it selects nothing of
     * its own. There is no neutral "upscale" turn to start: every run needs a
     * size and an engine, so the choice belongs to the children.
     */
    toggleUpscaleMenu(event: MouseEvent): void {
        if (this.upscaleMenuOpen) {
            this.closeUpscaleMenu();
            return;
        }

        this.upscaleMenuPos = this.flyoutPosition(event.currentTarget as HTMLElement | null);
        this.upscaleMenuOpen = true;
        // Two flyouts open beside each other would overlap.
        this.closeGenerateMenu();
    }

    closeUpscaleMenu(): void {
        this.upscaleMenuOpen = false;
        this.upscaleMenuPos = null;
    }

    /** Picking a pairing closes the flyout, the way a select closes on choice. */
    pickUpscaleOption(nav: UpscaleNavKey): void {
        this.selectNav(nav);
        this.closeUpscaleMenu();
    }

    /**
     * Click anywhere else closes it. Matched on the panel and the button that owns
     * it rather than by walking the DOM: the button's own handler does the
     * toggling, so a click on it must fall through here untouched.
     */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.upscaleMenuOpen && !this.generateMenuOpen) { return; }

        const target = event.target as HTMLElement | null;

        if (this.upscaleMenuOpen
            && !target?.closest('#vnav-upscale-options, [aria-controls="vnav-upscale-options"]')) {
            this.closeUpscaleMenu();
        }

        if (this.generateMenuOpen
            && !target?.closest('#vnav-generate-models, [aria-controls="vnav-generate-models"]')) {
            this.closeGenerateMenu();
        }
    }

    /* ---------- Generate rail submenu ---------- */

    /**
     * Generate is the one rail item that expands. Clicking it selects the section
     * as before and toggles the list; re-clicking an already-active Generate just
     * folds the list away.
     */
    selectGenerateNav(event?: MouseEvent): void {
        const opening = this.activeNav === 'generate' ? !this.generateMenuOpen : true;

        if (opening) {
            this.generateMenuPos = this.flyoutPosition(event?.currentTarget as HTMLElement | null);
            this.closeUpscaleMenu();
        } else {
            this.generateMenuPos = null;
        }

        this.generateMenuOpen = opening;
        this.selectNav('generate');
    }

    closeGenerateMenu(): void {
        this.generateMenuOpen = false;
        this.generateMenuPos = null;
    }

    /**
     * Which mark to draw beside a model in the rail's picker.
     *
     * Matched on the model family rather than the exact id, so a new version
     * — flux-2, gpt-image-3, wan-3 — inherits its family's mark instead of
     * dropping to the generic one. Both the id and the label are searched
     * because the catalogue is inconsistent about which one carries the family
     * name.
     *
     * These are drawn glyphs, not vendor logos: the marks say "these two rows
     * are the same family" without shipping trademarked artwork.
     */
    modelIcon(model: ModelInfo): string {
        const id = ((model?.model || '') + ' ' + (model?.key || '') + ' ' + (model?.label || '')).toLowerCase();

        if (id.indexOf('banana') > -1) { return 'banana'; }
        if (id.indexOf('gpt') > -1 || id.indexOf('openai') > -1) { return 'gpt'; }
        if (id.indexOf('imagen') > -1 || id.indexOf('gemini') > -1 || id.indexOf('google') > -1) { return 'google'; }
        if (id.indexOf('flux') > -1) { return 'flux'; }
        if (id.indexOf('seedream') > -1) { return 'seedream'; }
        if (id.indexOf('seedance') > -1) { return 'seedance'; }
        if (id.indexOf('wan') > -1) { return 'wan'; }
        if (id.indexOf('horse') > -1) { return 'horse'; }
        if (id.indexOf('kling') > -1 || id.indexOf('luma') > -1 || id.indexOf('runway') > -1
            || id.indexOf('veo') > -1 || id.indexOf('sora') > -1) { return 'motion'; }

        // Anything unrecognised still gets a mark, so no row sits alone without one.
        return this.kindOf(model) === 'video' ? 'motion' : 'default';
    }

    /**
     * Vendor logo files, keyed by the family modelIcon() resolves.
     *
     * Several families share one file because they share one vendor: Seedream and
     * Seedance are both ByteDance, and Wan is Alibaba's, which is why it takes the
     * Qwen mark.
     *
     * Families with no entry — flux, horse, motion and the generic default — have
     * no logo supplied, and fall back to the drawn glyph in the template rather
     * than to a broken image.
     */
    private static readonly MODEL_ICON_FILES: { [key: string]: string } = {
        gpt:      'openai.svg',
        google:   'deepmind.svg',
        banana:   'nano-banana-google.svg',
        seedream: 'bytedance.svg',
        seedance: 'bytedance.svg',
        wan:      'qwen.svg',
    };

    /**
     * The logo for a model's family, or null where none was supplied.
     *
     * Built on modelIcon() rather than re-matching the id, so the families stay
     * defined in exactly one place and a logo can never end up on a different
     * model from the glyph it replaced.
     */
    modelIconSrc(model: ModelInfo): string | null {
        const file = AiChatComponent.MODEL_ICON_FILES[this.modelIcon(model)];

        return file ? 'assets/images/ai/' + file : null;
    }

    /**
     * The upscaler engine's logo, for the rows in the Upscale flyout.
     *
     * Separate from the model icons because the two lists carry different things:
     * these are engines (`bria`, `esrgan`) named on the option itself, not model
     * families resolved from an id. Both are supplied, so there is no glyph
     * fallback here — an unknown engine gets Bria's rather than a broken image.
     */
    upscalerIconSrc(icon: string): string {
        const file = icon === 'esrgan' ? 'real-esrgan.png' : 'briaai-color.svg';

        return 'assets/images/ai/' + file;
    }

    /**
     * Pick a model from the rail. A model of the other kind goes through
     * setModelKind first, so the composer's text→video mode and the converse flag
     * follow the switch instead of disagreeing with the model that is now active;
     * same-kind picks go straight to selectMode.
     */
    pickGenerateModel(model: ModelInfo): void {
        if (!model?.key) { return; }

        // Listed for the sake of showing the range, but not selectable: say why,
        // and leave the menu open so the rest of the list is still there.
        if (this.isModelLocked(model)) {
            this.toastr.info(this.lockedModelHint(model));
            return;
        }
        this.activeNav = 'generate';

        const kind = this.kindOf(model);
        if (kind !== this.modelKind) {
            this.setModelKind(kind);
        }
        this.selectMode(model.key);
        // A choice made is a menu finished, the way a select closes on pick.
        this.closeGenerateMenu();

        // Picking a model is the moment its settings are relevant, so surface them
        // over the composer rather than making the user go find the panel.
        this.showComposerControls = true;
        this.openControlKey = '';
    }

    /* ---------- Sessions rail item and sidebar ---------- */

    /**
     * The rail's Sessions item. Mirrors selectGenerateNav: re-clicking an already
     * active Sessions folds the list away, otherwise it opens. It also brings the
     * sidebar back when collapsed, since the rail is the only way back in once
     * the header arrow that folded it is off screen.
     */
    selectSessionsNav(): void {
        if (this.sidebarCollapsed) {
            this.sidebarCollapsed = false;
            this.sessionsListOpen = true;
        } else {
            this.sessionsListOpen = this.activeNav === 'sessions' ? !this.sessionsListOpen : true;
        }
        this.selectNav('sessions');
    }

    /** The header arrow — folds the sessions column away entirely. */
    toggleSidebar(): void {
        this.sidebarCollapsed = !this.sidebarCollapsed;
        // Reaching for the sessions column is asking for the chrome back, so
        // writing mode lets go rather than fighting the click.
        this.composerTouched = false;
    }

    /**
     * The way back in, from the button that appears once the column is folded.
     * Opens the list as well as the column: the two are separate flags, and
     * restoring only the column could leave an empty panel behind.
     */
    openSidebar(): void {
        this.sidebarCollapsed = false;
        this.sessionsListOpen = true;
        // Writing mode also hides the column, so asking for it back has to lift
        // that too — otherwise the button would appear to do nothing while a
        // draft is in the composer.
        this.composerTouched = false;
    }

    /**
     * Whether the sessions column is off screen, by either route: folded away by
     * hand, or slid out by writing mode. What the restore button keys off, so one
     * button covers both — the column being gone is the same problem to the
     * reader whichever thing hid it.
     *
     * Signed out there is nothing to go back to, and the column starts folded.
     */
    get sessionsHidden(): boolean {
        return (this.sidebarCollapsed || this.isImmersive) && this.isSignedIn;
    }

    /* ---------- Composer control chips ---------- */

    toggleComposerControls(): void {
        this.showComposerControls = !this.showComposerControls;
        this.openControlKey = '';
    }

    /** A toggle flips in place; everything else opens a value popover. */
    toggleControlChip(control: ModelControl): void {
        if (control.type === 'toggle') {
            this.controlValues[control.key] = !this.controlValues[control.key];
            return;
        }
        this.openControlKey = this.openControlKey === control.key ? '' : control.key;
    }

    closeControlChip(): void {
        this.openControlKey = '';
    }

    setControlValue(control: ModelControl, value: any): void {
        this.controlValues[control.key] = value;
        this.openControlKey = '';
    }

    /**
     * Chips are narrow, so they carry the first word of the control's label —
     * "Aspect ratio" reads as "Aspect", "Negative prompt" as "Negative". The full
     * label stays in the chip's tooltip.
     */
    controlChipLabel(control: ModelControl): string {
        return (control.label || control.key).split(' ')[0];
    }

    /** The chip's right-hand value: what this control is currently set to. */
    controlValueLabel(control: ModelControl): string {
        const raw = this.controlValues[control.key];

        if (control.type === 'toggle') { return raw ? 'On' : 'Off'; }
        if (raw === null || raw === undefined || raw === '') { return 'None'; }

        if (control.type === 'select') {
            const opt = (control.options || []).find(o => o.value === raw);
            return this.shortOptionLabel(opt ? opt.label : String(raw));
        }
        return String(raw);
    }

    /**
     * Option labels arrive as "Square · 1:1" — the part after the dot is the bit
     * people scan a chip for, so that is what the chip shows. Labels with no dot
     * pass through, capitalised (the style presets arrive lower-case).
     */
    private shortOptionLabel(label: string): string {
        const parts = (label || '').split('·');
        const tail = (parts.length > 1 ? parts[parts.length - 1] : label || '').trim();
        return tail.charAt(0).toUpperCase() + tail.slice(1);
    }

    /**
     * Which glyph a chip shows. Matched on the control's key rather than a fixed
     * list of controls, so a model that ships a control we've never seen still
     * gets a sensible icon instead of a blank.
     */
    controlIcon(control: ModelControl): string {
        const key = (control.key || '').toLowerCase();
        if (control.type === 'toggle') { return 'toggle'; }
        if (key.includes('negative')) { return 'negative'; }
        if (key.includes('style')) { return 'style'; }
        if (key.includes('aspect') || key.includes('ratio') || key.includes('size')) { return 'aspect'; }
        if (key.includes('quality') || key.includes('resolution')) { return 'quality'; }
        if (key.includes('seed')) { return 'seed'; }
        if (control.type === 'number') { return 'number'; }
        return 'default';
    }

    trackByControlKey(_: number, item: ModelControl): string {
        return item?.key;
    }

    isGenerateModelActive(model: ModelInfo): boolean {
        return !!model?.key && this.selectedMode === model.key;
    }

    /** Per-run credit cost, for the row's tooltip. Null when the plan omits it. */
    modelCredits(model: ModelInfo): number | null {
        const tool = this.permissionToolFor(model?.key || '');
        return tool ? tool.sell_credits : null;
    }

    /**
     * Two-tone dot beside each model: the cheap tier reads as the brand accent,
     * anything above the catalogue's median as the premium amber. Derived from
     * the plan's own sell_credits rather than a hard-coded list of model names,
     * so a new model lands in the right tier without a code change. Falls back to
     * 'standard' when the plan carries no prices at all.
     */
    modelTier(model: ModelInfo): 'standard' | 'premium' {
        const cost = this.modelCredits(model);
        if (cost === null) { return 'standard'; }

        // Compared within its own kind: video costs far more than image across
        // the board, so one shared median would mark every video model premium.
        const kind = this.kindOf(model);
        const priced = this.generateModels
            .filter(m => this.kindOf(m) === kind)
            .map(m => this.modelCredits(m))
            .filter((c): c is number => c !== null)
            .sort((a, b) => a - b);
        if (priced.length < 2) { return 'standard'; }

        const median = priced[Math.floor(priced.length / 2)];
        return cost > median ? 'premium' : 'standard';
    }

    toggleChipMode(key: ChatModeKey): void {
        if (this.currentMode === key) {
            this.clearMode();
        } else {
            this.applyMode(key);
        }
    }

    applyMode(key: ChatModeKey): void {
        const cfg = this.chatModeConfig[key];
        if (!cfg) { return; }
        this.currentMode = key;

        const current = this.pendingMessage || '';
        if (!current.trim() || current === this.lastPrefill) {
            this.pendingMessage = cfg.inputText;
        }
        this.lastPrefill = cfg.inputText;
    }

    clearMode(): void {
        if (this.pendingMessage === this.lastPrefill) {
            this.pendingMessage = '';
        }
        this.currentMode = null;
        this.lastPrefill = '';
    }

    /**
     * Make this the composer's only reference image, replacing any attached.
     * For the single-image tools — "use as reference", refine, upscale, edit —
     * which act on exactly one image. Uploads and drops add instead, up to
     * MAX_REFERENCE_IMAGES (addReferenceImage()).
     */
    private attachImage(img: { id: string; url: string }): void {
        if (!img?.id) { return; }
        this.attachedImages = [{ id: img.id, url: img.url }];
    }

    onEditImage(img: { id: string; url: string }): void {
        this.attachImage(img);
        this.applyMode('edit');
    }

    /* ---- Per-image Edit menu ---- */

    /** Image whose Edit dropdown is open, by id (only one at a time). */
    editMenuImageId: string | null = null;

    isEditMenuOpen(img: { id: string }): boolean {
        return this.editMenuImageId === img.id;
    }

    toggleEditMenu(img: { id: string }): void {
        this.editMenuImageId = this.isEditMenuOpen(img) ? null : img.id;
    }

    closeEditMenu(): void {
        this.editMenuImageId = null;
    }

    /**
     * Run one tool from an image's Edit menu. Single entry point so the menu
     * closes in one place, whatever the tool does next — inline edit, modal,
     * async video job, or a composer prefill.
     */
    runImageEdit(img: { id: string; url: string }, action: ImageEditAction): void {
        this.closeEditMenu();

        switch (action) {
            case 'remove_bg':
            case 'replace_bg':
            case 'crop':
            case 'colorize':
            case 'denoise':
                // Crop / replace_bg open their own modal first, then re-enter.
                this.applyEditTool(img, action);
                break;
            case 'face':
                this.faceRestore(img);
                break;
            case 'fill':
                this.openFillModal(img);
                break;
            case 'restore':
                this.restoreImage(img);
                break;
            case 'animate':
                this.animateImage(img);
                break;
            case 'talk':
                this.startTalkingHead(img);
                break;
            case 'upscale':
                this.onUpscaleImage(img);
                break;
            case 'reference':
                this.onEditImage(img);
                break;
        }
    }

    onRefineImage(img: { id: string; url: string }): void {
        this.attachImage(img);
        this.pendingMessage = 'Refine this image';
        this.lastPrefill = 'Refine this image';
        this.currentMode = null;
    }

    onUpscaleImage(img: { id: string; url: string }): void {
        this.attachImage(img);
        this.pendingMessage = 'Upscale to 4K';
        this.lastPrefill = 'Upscale to 4K';
        this.currentMode = null;
    }

    openImagePreview(img: { id: string; url: string }): void {
        this.previewImage = { id: img.id, url: img.url };
    }

    closeImagePreview(): void {
        this.previewImage = null;
    }

    openDownloadModal(img: { id: string; url: string }): void {
        this.downloadModalImage = { id: img.id, url: img.url };
        this.downloadAccepted = false;
        this.pendingDownload = { kind: 'highres', assetType: 'image' };
    }

    /* Header label for the licence modal — it now also gates video downloads. */
    get downloadModalTitle(): string {
        return this.pendingDownload.assetType === 'video' ? 'Download Video' : 'Download Image';
    }

    closeDownloadModal(): void {
        if (this.isDownloading) { return; }
        this.downloadModalImage = null;
        this.downloadAccepted = false;
    }

    downloadImage(): void {
        if (!this.downloadModalImage || !this.downloadAccepted || this.isDownloading) { return; }

        // Both entry points share this button; run whichever path opened the modal.
        if (this.pendingDownload.kind === 'studio') {
            this.runStudioDownload();
            return;
        }

        const img = this.downloadModalImage;
        const filename = this.deriveFilename(img.url, img.id);
        this.isDownloading = true;

        const customerFrom = this.currentUser?.isExclusive === true ? 2 : 1;

        const cartProduct = {
            id: img.id,
            filename: img.id,
            thumbnail: img.url,
            license: 'Individual',
            collection: 232,
            size: 'Extra Large',
            price: 0,
            type: 'AI',
            caption: 'Nano Banana Image',
            category: 'creative',
            usage: ''
        };

        const params = {
            ...cartProduct,
            customer_from: customerFrom,
            customer_id: this.currentUser?.customers_id || '',
        };

        this.apiService.downloadNanoHighResImage(params, true).subscribe(data => {
            if (data && data.subscription == 0) {
                this.toastr.error('You’ve reached your usage limit. Please renew your plan to continue using this feature');
                this.isDownloading = false;
                return;
            }

            const downloadUrl = data.download_link;

            fetch(downloadUrl, { mode: 'cors' })
                .then(res => {
                    if (!res.ok) { throw new Error('Network response was not ok'); }
                    return res.blob();
                })
                .then(blob => {
                    const blobUrl = URL.createObjectURL(blob);
                    this.triggerBrowserDownload(blobUrl, filename);
                    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                    this.finishDownload();
                })
                .catch(() => {
                    this.isDownloading = false;
                    this.toastr.error('We’re processing a lot of requests right now. Please try again shortly');
                });
        },
            () => {
                this.isDownloading = false;
                this.toastr.error('We’re processing a lot of requests right now. Please try again shortly');
            });
    }

    private deriveFilename(url: string, id?: string): string {
        try {
            const path = new URL(url, window.location.origin).pathname;
            const last = path.split('/').pop() || '';
            if (last && /\.[a-zA-Z0-9]{2,5}$/.test(last)) { return last; }
        } catch {}
        return `mahatta-ai-${id}.png`;
    }

    onComposerKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.send();
        }
    }

    /** Month names, for formatTimestamp — see the note there on why not toLocale*. */
    private static readonly MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    /**
     * "Sep 3, 3:31 PM" — the transcript's timestamp.
     *
     * Built by hand rather than through toLocaleString() or the date pipe, which
     * both answer in the visitor's locale: the same turn then reads "03/09/2026,
     * 15:31" on a machine set to en-GB and "2026/9/3 15:31" on one set to ja-JP.
     * This format was asked for specifically, so it is produced the same way for
     * everyone.
     *
     * The DATE is local time, not UTC — created_at is an ISO string with a zone,
     * and getHours() and friends resolve it to wherever the reader is sitting,
     * which is what makes "3:31 PM" mean anything to them.
     *
     * No year: a transcript is read in the present, and the month and day are
     * enough to place a turn. An unparseable value is handed back untouched
     * rather than shown as "Invalid Date".
     */
    formatTimestamp(ts: string): string {
        if (!ts) { return ''; }

        const d = new Date(ts);
        if (isNaN(d.getTime())) { return ts; }

        const month = AiChatComponent.MONTHS[d.getMonth()];
        const hours24 = d.getHours();
        // Midnight and noon are the two the modulo gets wrong: 0 % 12 is 0, and
        // the clock calls that 12.
        const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
        const minutes = ('0' + d.getMinutes()).slice(-2);
        const meridiem = hours24 < 12 ? 'AM' : 'PM';

        return month + ' ' + d.getDate() + ', ' + hours12 + ':' + minutes + ' ' + meridiem;
    }

    trackById(_: number, item: any): string {
        return item?.id;
    }

    trackByPlanKey(_: number, item: CreditPlan): string {
        return item?.key;
    }

    /** Models are keyed by `key`, not `id` — trackById would collapse them all. */
    trackByModelKey(_: number, item: ModelInfo): string {
        return item?.key;
    }

    trackByIndex(index: number): number {
        return index;
    }
}

